import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import * as THREE from 'three';
import axios from 'axios';
import { FileItem, storageService } from '../services/storageService';
import { generateAIMedia, checkAIMediaStatus } from '../services/aiService';
import { apiKeyService } from '../services/apiKeyService';
import AudioEditor from './AudioEditor';
import VideoEffectsEditor, { VfxZone, VfxEffect, VfxConfig, GlobalSegConfig } from './VideoEffectsEditor';
import AssSubtitleEditor, { AssTextOverlay } from './AssSubtitleEditor';

interface VideoEditorProps {
  file: FileItem;
  onSave: (blob: Blob, filename: string) => void;
  onCancel: () => void;
  availableFiles?: FileItem[];
  currentPath?: string;
}

// ─── Types ────────────────────────────────────────────────────────────────────

type ClipFilter = 'none' | 'grayscale' | 'sepia' | 'blur' | 'invert';
type TransitionType = 'fade' | 'dissolve' | 'slide_left' | 'slide_right' | 'wipe_left' | 'circleopen';
type SpeedVal = 0.25 | 0.5 | 1 | 1.5 | 2;

interface ColorGrading {
  hue: number;        // -180 à +180 (teinte)
  temp: number;       // -100 à +100 (température — chaud/froid)
  lift: number;       // -100 à +100 (ombres)
  gamma: number;      // -100 à +100 (tons moyens)
  gain: number;       // -100 à +100 (hautes lumières)
}

const DEFAULT_COLOR_GRADING: ColorGrading = { hue: 0, temp: 0, lift: 0, gamma: 0, gain: 0 };

interface Clip {
  id: string;
  srcStart: number;   // secondes dans la source
  srcEnd: number;
  trackStart: number; // position sur la timeline
  filter: ClipFilter;
  speed: SpeedVal;
  brightness: number; // 0–200
  contrast: number;
  saturation: number;
  reverse: boolean;
  muteAudio?: boolean;                                        // supprime la bande son du clip
  transition?: { type: TransitionType; duration: number };    // entrant (crossfade depuis clip précédent)
  transitionIn?: { type: TransitionType; duration: number };  // fondu depuis noir en début de clip
  transitionOut?: { type: TransitionType; duration: number }; // fondu vers noir en fin de clip
  sourceKey?: string; // undefined = fichier principal
  pitchShift?: number; // -12 to 12 semitones
  colorGrading?: ColorGrading;
}

interface TimelineMarker {
  id: string;
  time: number;
  label: string;
  color: string;
}

// ─── Interactive elements ──────────────────────────────────────────────────────

type InteractiveElementType = 'quiz' | 'trueFalse' | 'info' | 'statement' | 'link' | 'imagePop' | 'hotspot' | 'navigation' | 'panorama360';

interface QuizChoice { id: string; text: string; correct: boolean; }

interface Hotspot360 {
  id: string;
  yaw: number;    // degrés
  pitch: number;  // degrés
  label: string;
  action: 'text' | 'link' | 'jump';
  value: string;
}

interface InteractiveElement {
  id: string;
  type: InteractiveElementType;
  time: number;          // timestamp de déclenchement (s)
  pauseVideo: boolean;   // pause auto quand déclenché
  // quiz / trueFalse
  question?: string;
  choices?: QuizChoice[];
  feedbackCorrect?: string;
  feedbackWrong?: string;
  jumpToOnWrong?: number; // saut si incorrect
  // info / statement
  text?: string;
  autoClose?: number;    // 0 = manuel
  // link
  linkUrl?: string;
  linkLabel?: string;
  // imagePop
  imageUrl?: string;
  imageName?: string;
  // hotspot
  hotspotX?: number;     // % 0-100 centre H
  hotspotY?: number;     // % 0-100 centre V
  hotspotW?: number;     // % largeur
  hotspotH?: number;     // % hauteur
  hotspotDuration?: number;
  hotspotShape?: 'rect' | 'circle';
  hotspotAction?: 'link' | 'text' | 'jump';
  hotspotValue?: string;
  // navigation
  label?: string;
  jumpToTime?: number;
  // panorama360
  panoramaUrl?: string;
  panoramaName?: string;
  startYaw?: number;    // degrés, default 0
  startPitch?: number;  // degrés, default 0
  hotspots360?: Hotspot360[];
}

interface RenderQueueItem {
  id: string;
  filename: string;
  format: string;
  status: 'pending' | 'rendering' | 'done' | 'error';
  progress: number;
  error?: string;
  blobUrl?: string;
  // snapshot of edl + sources needed to re-render
  edlSnapshot: any;
  sourcesSnapshot: Array<{ fieldname: string; blob: Blob; name: string }>;
  mainBlob: Blob;
  mainName: string;
}

interface TextOverlay {
  id: string;
  content: string;
  trackStart: number;
  duration: number;
  x: number;  // %
  y: number;  // %
  fontSize: number;
  color: string;
  bold: boolean;
  italic?: boolean;
  underline?: boolean;
  shadow: boolean;
  font: string; // clé dans FONT_MAP
  bgColor?: string;   // couleur de fond (#rrggbb) ou undefined = transparent
  bgOpacity?: number; // 0–100
  animIn?: 'none' | 'fade' | 'slideUp' | 'zoomIn';
  animOut?: 'none' | 'fade' | 'slideDown' | 'zoomOut';
  textAlign?: 'left' | 'center' | 'right';
  anchorX?: number; // 0 = gauche, 0.5 = centre, 1 = droite
  anchorY?: number; // 0 = haut, 0.5 = milieu, 1 = bas
}

interface AudioLayer {
  id: string;
  sourceKey: string;    // clé dans extraSources
  trackStart: number;
  duration: number;
  volume: number;       // 0-200
  track: 'main' | 'audio' | 'music';  // piste cible (main=Audio, audio=Voix, music=Music)
  fadeIn?: number;  // seconds
  fadeOut?: number; // seconds
}

interface VideoOverlay {
  id: string;
  fileItem: FileItem;
  isVideo: boolean;
  trackStart: number;
  duration: number;
  x: number;           // % 0–100, centre horizontal
  y: number;           // % 0–100, centre vertical
  width: number;       // % 5–100 de la largeur de la vidéo principale
  opacity: number;     // 0–100
  borderWidth: number; // px (0 = pas de bordure)
  borderColor: string; // couleur CSS ex. '#ffffff'
}

interface ExtraSource {
  key: string;
  fileItem: FileItem;
  type: 'video' | 'audio' | 'text' | 'image';
  // pour les images uniquement : position sur la timeline
  trackStart?: number;
  duration?: number;
  transition?: { type: TransitionType; duration: number }; // transition entrante pour image
}

interface DragState {
  type: 'clip' | 'trim-l' | 'trim-r' | 'text' | 'text-trim-r' | 'playhead' | 'text-trim-l' | 'audio-layer' | 'audio-layer-trim-r' | 'audio-layer-trim-l' | 'image-block' | 'image-trim-r' | 'overlay-block' | 'overlay-trim-r';
  id: string;
  startX: number;
  origVal: number;
  origVal2?: number;
}

// ─── Font catalog ─────────────────────────────────────────────────────────────

interface FontDef {
  label: string;
  web: string;   // valeur CSS font-family pour preview
  category: string;
}

const FONTS: Record<string, FontDef> = {
  'roboto':         { label: 'Roboto',            web: 'Roboto, sans-serif',         category: 'Sans-Serif' },
  'montserrat':     { label: 'Montserrat',         web: 'Montserrat, sans-serif',     category: 'Sans-Serif' },
  'oswald':         { label: 'Oswald',             web: 'Oswald, sans-serif',         category: 'Sans-Serif' },
  'open-sans':      { label: 'Open Sans',          web: 'Open Sans, sans-serif',      category: 'Sans-Serif' },
  'liberation':     { label: 'Liberation Sans',    web: 'Liberation Sans, sans-serif',category: 'Sans-Serif' },
  'playfair':       { label: 'Playfair Display',   web: 'Playfair Display, serif',    category: 'Serif' },
  'dejavu-serif':   { label: 'DejaVu Serif',       web: 'DejaVu Serif, serif',        category: 'Serif' },
  'free-serif':     { label: 'Free Serif',         web: 'FreeSerif, serif',           category: 'Serif' },
  'bebas':          { label: 'Bebas Neue',         web: 'Bebas Neue, sans-serif',     category: 'Display' },
  'anton':          { label: 'Anton',              web: 'Anton, sans-serif',          category: 'Display' },
  'bangers':        { label: 'Bangers',            web: 'Bangers, cursive',           category: 'Display' },
  'rubik-mono':     { label: 'Rubik Mono One',     web: 'Rubik Mono One, sans-serif', category: 'Display' },
  'cinzel':         { label: 'Cinzel Decorative',  web: 'Cinzel Decorative, serif',   category: 'Display' },
  'pacifico':       { label: 'Pacifico',           web: 'Pacifico, cursive',          category: 'Script' },
  'lobster':        { label: 'Lobster',            web: 'Lobster, cursive',           category: 'Script' },
  'dancing':        { label: 'Dancing Script',     web: 'Dancing Script, cursive',    category: 'Script' },
  'dejavu-mono':    { label: 'DejaVu Mono',        web: 'DejaVu Sans Mono, monospace',category: 'Mono' },
};

const FONT_CATEGORIES = ['Sans-Serif', 'Serif', 'Display', 'Script', 'Mono'];

const GOOGLE_FONTS_URL =
  'https://fonts.googleapis.com/css2?family=Roboto&family=Montserrat&family=Oswald&family=Open+Sans&family=Playfair+Display&family=Bebas+Neue&family=Anton&family=Bangers&family=Rubik+Mono+One&family=Cinzel+Decorative&family=Pacifico&family=Lobster&family=Dancing+Script&display=swap';

if (typeof document !== 'undefined' && !document.getElementById('gf-videoeditor')) {
  const link = document.createElement('link');
  link.id = 'gf-videoeditor';
  link.rel = 'stylesheet';
  link.href = GOOGLE_FONTS_URL;
  document.head.appendChild(link);
}

if (typeof document !== 'undefined' && !document.getElementById('ve-interactive-styles')) {
  const s = document.createElement('style');
  s.id = 've-interactive-styles';
  s.textContent = `@keyframes hs-pulse { 0%,100% { box-shadow: 0 0 0 0 rgba(34,211,238,0.5); } 50% { box-shadow: 0 0 0 6px rgba(34,211,238,0); } } @keyframes spin { to { transform: rotate(360deg); } }`;
  document.head.appendChild(s);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

let _eid = 0;
function uid(): string { return `c${++_eid}_${Math.random().toString(36).slice(2, 6)}`; }

function fmt(s: number): string {
  if (!isFinite(s) || s < 0) s = 0;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  const ms = Math.floor((s % 1) * 10);
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
    : `${m}:${String(sec).padStart(2, '0')}.${ms}`;
}

function clipDuration(c: Clip): number {
  return (c.srcEnd - c.srcStart) / c.speed;
}

function clipTrackEnd(c: Clip): number {
  return c.trackStart + clipDuration(c);
}

function totalDuration(clips: Clip[]): number {
  return clips.reduce((acc, c) => Math.max(acc, clipTrackEnd(c)), 0);
}

const FILTER_LABELS: Record<ClipFilter, string> = {
  none: 'Aucun', grayscale: 'Noir & Blanc', sepia: 'Sépia', blur: 'Flou', invert: 'Inversion',
};

const TRANSITION_LABELS: Record<TransitionType, string> = {
  fade: 'Fondu', dissolve: 'Dissolution', slide_left: 'Glissé gauche',
  slide_right: 'Glissé droite', wipe_left: 'Balayage', circleopen: 'Ouverture cercle',
};

const SPEED_LABELS: Record<string, string> = {
  '0.25': '¼×', '0.5': '½×', '1': '1×', '1.5': '1.5×', '2': '2×',
};

const CLIP_COLORS = ['#2563eb', '#16a34a', '#d97706', '#9333ea', '#dc2626', '#0891b2'];
function clipColor(idx: number): string { return CLIP_COLORS[idx % CLIP_COLORS.length]; }

// File type helpers
const isVideoFile = (n: string) => /\.(mp4|webm|mov|avi|mkv)$/i.test(n);
const isAudioFile = (n: string) => /\.(mp3|wav|ogg|aac|m4a|flac)$/i.test(n);
const isTextFile  = (n: string) => /\.(txt|srt|vtt)$/i.test(n);
const isImageFile = (n: string) => /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(n);

/** Probe the actual duration of a media blob URL (video or audio). */
function getMediaDuration(url: string, tag: 'video' | 'audio' = 'video'): Promise<number> {
  return new Promise(resolve => {
    const el = document.createElement(tag);
    el.preload = 'metadata';
    el.addEventListener('loadedmetadata', () => resolve(isFinite(el.duration) ? el.duration : 30), { once: true });
    el.addEventListener('error', () => resolve(30), { once: true });
    el.src = url;
  });
}

/** Encode an AudioBuffer to a 16-bit PCM WAV Blob */
function audioBufferToWav(ab: AudioBuffer): Blob {
  const nCh = ab.numberOfChannels, sr = ab.sampleRate, len = ab.length;
  const buf = new ArrayBuffer(44 + len * nCh * 2);
  const v = new DataView(buf);
  const ws = (off: number, s: string) => { for (let i = 0; i < s.length; i++) v.setUint8(off + i, s.charCodeAt(i)); };
  ws(0, 'RIFF'); v.setUint32(4, buf.byteLength - 8, true); ws(8, 'WAVE'); ws(12, 'fmt ');
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, nCh, true);
  v.setUint32(24, sr, true); v.setUint32(28, sr * nCh * 2, true);
  v.setUint16(32, nCh * 2, true); v.setUint16(34, 16, true);
  ws(36, 'data'); v.setUint32(40, len * nCh * 2, true);
  let offset = 44;
  for (let i = 0; i < len; i++) {
    for (let ch = 0; ch < nCh; ch++) {
      const s = Math.max(-1, Math.min(1, ab.getChannelData(ch)[i]));
      v.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
      offset += 2;
    }
  }
  return new Blob([buf], { type: 'audio/wav' });
}

/** Extract the audio track from a video URL and return a WAV Blob */
async function extractAudioFromVideo(url: string): Promise<Blob> {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token') || sessionStorage.getItem('token') || '';
  const resp = await fetch(url, token ? { headers: { Authorization: `Bearer ${token}` } } : {});
  if (!resp.ok) throw new Error(`Impossible de charger la vidéo : ${resp.status}`);
  const arrayBuffer = await resp.arrayBuffer();
  const ctx = new AudioContext();
  const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
  ctx.close();
  return audioBufferToWav(audioBuffer);
}

function parseSrt(srt: string, fontSize = 60, color = '#ffffff', bold = false, font = 'roboto'): TextOverlay[] {
  const toSec = (h: string, m: string, s: string, ms: string) =>
    +h * 3600 + +m * 60 + +s + +ms / 1000;
  // Normalize line endings so \r\n\r\n is treated as a block separator
  const normalized = srt.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  return normalized.trim().split(/\n\n+/).flatMap(block => {
    const lines = block.trim().split('\n').filter(l => l.trim() !== '' || false);
    if (lines.length < 2) return [];
    // Find the timecode line — could be lines[0] (no sequence number) or lines[1]
    const TIMECODE_RE = /(\d+):(\d+):(\d+)[,.](\d+)\s*-->\s*(\d+):(\d+):(\d+)[,.](\d+)/;
    let timeIdx = lines.findIndex(l => TIMECODE_RE.test(l));
    if (timeIdx < 0) return [];
    const match = lines[timeIdx].match(TIMECODE_RE)!;
    const start = toSec(match[1], match[2], match[3], match[4]);
    const end   = toSec(match[5], match[6], match[7], match[8]);
    if (end <= start) return [];
    const content = lines.slice(timeIdx + 1).join('\n').trim();
    if (!content) return [];
    return [{
      id: `sub_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      content,
      trackStart: start, duration: end - start,
      x: 50, y: 85, anchorX: 0.5, anchorY: 1,
      fontSize, color,
      bold, shadow: true, font,
    } as TextOverlay];
  });
}

function getTextAnimStyle(t: TextOverlay, ct: number): React.CSSProperties {
  const ANIM_DUR = Math.min(0.5, t.duration * 0.25);
  const elapsed = ct - t.trackStart;
  const remaining = t.trackStart + t.duration - ct;
  let opacity = 1;
  let transform = '';
  if (t.animIn && t.animIn !== 'none' && elapsed < ANIM_DUR && ANIM_DUR > 0) {
    const p = Math.max(0, Math.min(1, elapsed / ANIM_DUR));
    if (t.animIn === 'fade') opacity = p;
    else if (t.animIn === 'slideUp') { opacity = p; transform = `translateY(${(1 - p) * 20}px)`; }
    else if (t.animIn === 'zoomIn') { opacity = p; transform = `scale(${0.5 + 0.5 * p})`; }
  }
  if (t.animOut && t.animOut !== 'none' && remaining < ANIM_DUR && ANIM_DUR > 0) {
    const p = Math.max(0, Math.min(1, remaining / ANIM_DUR));
    if (t.animOut === 'fade') opacity = Math.min(opacity, p);
    else if (t.animOut === 'slideDown') { opacity = Math.min(opacity, p); transform = `${transform} translateY(${(1 - p) * 20}px)`; }
    else if (t.animOut === 'zoomOut') { opacity = Math.min(opacity, p); transform = `${transform} scale(${0.5 + 0.5 * p})`; }
  }
  return { opacity, transform: transform.trim() || undefined };
}

// ─── Panorama360Viewer ────────────────────────────────────────────────────────

const Panorama360Viewer: React.FC<{
  element: InteractiveElement;
  onDismiss: () => void;
  onJump: (t: number) => void;
}> = ({ element, onDismiss, onJump }) => {
  const containerRef  = useRef<HTMLDivElement>(null);
  const rendererRef   = useRef<THREE.WebGLRenderer | null>(null);
  const cameraRef     = useRef<THREE.PerspectiveCamera | null>(null);
  const camAngles     = useRef({ yaw: (element.startYaw ?? 0) * Math.PI / 180, pitch: (element.startPitch ?? 0) * Math.PI / 180 });
  const dragState     = useRef<{ x: number; y: number } | null>(null);
  const rafRef        = useRef<number>(0);
  const [hsPositions, setHsPositions] = useState<Array<{ id: string; sx: number; sy: number; visible: boolean }>>([]);
  const [hsPopup, setHsPopup]         = useState<string | null>(null);
  const [loaded, setLoaded]           = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const W = container.clientWidth  || window.innerWidth;
    const H = container.clientHeight || window.innerHeight;

    const scene    = new THREE.Scene();
    const camera   = new THREE.PerspectiveCamera(75, W / H, 0.01, 1000);
    camera.position.set(0, 0, 0.01);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.domElement.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block;';
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Sphère intérieure (normes inversées)
    const geo  = new THREE.SphereGeometry(100, 60, 40);
    geo.scale(-1, 1, 1);
    const tex = new THREE.TextureLoader().load(element.panoramaUrl ?? '', () => setLoaded(true));
    const mat  = new THREE.MeshBasicMaterial({ map: tex });
    const mesh = new THREE.Mesh(geo, mat);
    scene.add(mesh);

    const updateCam = () => {
      const { yaw, pitch } = camAngles.current;
      camera.rotation.set(
        Math.max(-Math.PI / 2 + 0.05, Math.min(Math.PI / 2 - 0.05, pitch)),
        yaw, 0, 'YXZ',
      );
    };

    const hotspots = element.hotspots360 ?? [];

    const loop = () => {
      rafRef.current = requestAnimationFrame(loop);
      updateCam();
      renderer.render(scene, camera);

      if (hotspots.length > 0) {
        const rect = container.getBoundingClientRect();
        setHsPositions(hotspots.map(hs => {
          const yr = hs.yaw   * Math.PI / 180;
          const pr = hs.pitch * Math.PI / 180;
          const dir = new THREE.Vector3(
            -Math.sin(yr) * Math.cos(pr),
             Math.sin(pr),
            -Math.cos(yr) * Math.cos(pr),
          );
          const ndc = dir.project(camera);
          return {
            id:      hs.id,
            sx:      ((ndc.x + 1) / 2) * rect.width,
            sy:      ((-ndc.y + 1) / 2) * rect.height,
            visible: ndc.z < 1.0 && Math.abs(ndc.x) < 1 && Math.abs(ndc.y) < 1,
          };
        }));
      }
    };
    loop();

    const onResize = () => {
      if (!container) return;
      const W2 = container.clientWidth, H2 = container.clientHeight;
      if (!W2 || !H2) return;
      camera.aspect = W2 / H2;
      camera.updateProjectionMatrix();
      renderer.setSize(W2, H2);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(rafRef.current);
      renderer.dispose();
      geo.dispose();
      mat.dispose();
      tex.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
      window.removeEventListener('resize', onResize);
    };
  }, [element.panoramaUrl, element.startYaw, element.startPitch]);

  const onPtrDown = (e: React.PointerEvent) => {
    dragState.current = { x: e.clientX, y: e.clientY };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setHsPopup(null);
  };
  const onPtrMove = (e: React.PointerEvent) => {
    if (!dragState.current) return;
    const dx = e.clientX - dragState.current.x;
    const dy = e.clientY - dragState.current.y;
    dragState.current = { x: e.clientX, y: e.clientY };
    camAngles.current.yaw   -= dx * 0.004;
    camAngles.current.pitch -= dy * 0.004;
  };
  const onPtrUp = () => { dragState.current = null; };

  const hotspots = element.hotspots360 ?? [];
  const isDragging = !!dragState.current;

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000001, background: '#000', overflow: 'hidden' }}>

      {/* Canvas Three.js */}
      <div ref={containerRef}
        style={{ position: 'absolute', inset: 0, cursor: isDragging ? 'grabbing' : 'grab' }}
        onPointerDown={onPtrDown} onPointerMove={onPtrMove} onPointerUp={onPtrUp} onPointerLeave={onPtrUp}
      />

      {/* Spinner chargement */}
      {!loaded && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, pointerEvents: 'none' }}>
          <div style={{ width: 36, height: 36, border: '3px solid #334155', borderTop: '3px solid #22d3ee', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <span style={{ fontSize: 12, color: '#64748b' }}>Chargement du panorama…</span>
        </div>
      )}

      {/* Hotspots projetés */}
      {loaded && hsPositions.filter(p => p.visible).map(p => {
        const hs = hotspots.find(h => h.id === p.id);
        if (!hs) return null;
        const isActive = hsPopup === hs.id;
        const ICON = hs.action === 'link' ? '🔗' : hs.action === 'jump' ? '⤵' : 'ℹ';
        return (
          <div key={hs.id} style={{ position: 'absolute', left: p.sx, top: p.sy, transform: 'translate(-50%, -50%)', zIndex: 10, pointerEvents: 'auto' }}>
            <button
              onPointerDown={e => e.stopPropagation()}
              onClick={() => {
                if (hs.action === 'link')  { window.open(hs.value, '_blank', 'noopener'); return; }
                if (hs.action === 'jump')  { const t = parseFloat(hs.value); if (!isNaN(t)) { onJump(t); onDismiss(); } return; }
                setHsPopup(isActive ? null : hs.id);
              }}
              style={{ background: isActive ? '#0891b2' : 'rgba(8,145,178,0.85)', border: '2px solid rgba(34,211,238,0.9)',
                borderRadius: '50%', width: 38, height: 38, cursor: 'pointer', color: '#fff', fontSize: 17,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 0 14px rgba(34,211,238,0.55)', animation: 'hs-pulse 2s ease-in-out infinite' }}>
              {ICON}
            </button>
            {hs.label && (
              <div style={{ position: 'absolute', top: '110%', left: '50%', transform: 'translateX(-50%)', marginTop: 2,
                background: 'rgba(15,23,42,0.88)', color: '#e2e8f0', fontSize: 10, padding: '2px 7px', borderRadius: 4,
                whiteSpace: 'nowrap', pointerEvents: 'none', border: '1px solid #334155' }}>
                {hs.label}
              </div>
            )}
            {isActive && hs.action === 'text' && (
              <div onPointerDown={e => e.stopPropagation()}
                style={{ position: 'absolute', bottom: '115%', left: '50%', transform: 'translateX(-50%)',
                  background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: '10px 14px',
                  minWidth: 180, maxWidth: 260, boxShadow: '0 8px 24px rgba(0,0,0,0.6)', pointerEvents: 'auto' }}>
                <div style={{ fontSize: 11, color: '#e2e8f0', lineHeight: 1.6 }}>{hs.value}</div>
                <button onClick={() => setHsPopup(null)}
                  style={{ marginTop: 6, background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 10 }}>
                  Fermer ✕
                </button>
              </div>
            )}
          </div>
        );
      })}

      {/* Barre du haut */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, padding: '12px 16px',
        background: 'linear-gradient(to bottom,rgba(0,0,0,0.65),transparent)', pointerEvents: 'none',
        display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 20 }}>🌐</span>
        <span style={{ fontSize: 14, color: '#e2e8f0', fontWeight: 700 }}>Panorama 360°</span>
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginLeft: 4 }}>Faites glisser pour explorer</span>
        {hotspots.length > 0 && (
          <span style={{ fontSize: 11, color: '#22d3ee', marginLeft: 'auto' }}>
            ⊙ {hotspots.length} point{hotspots.length > 1 ? 's' : ''} d'intérêt
          </span>
        )}
      </div>

      {/* Bouton continuer */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '16px',
        background: 'linear-gradient(to top,rgba(0,0,0,0.65),transparent)', display: 'flex', justifyContent: 'center' }}>
        <button onClick={onDismiss}
          style={{ background: '#0891b2', color: '#fff', border: 'none', borderRadius: 8,
            padding: '10px 32px', cursor: 'pointer', fontSize: 14, fontWeight: 700,
            boxShadow: '0 4px 18px rgba(0,0,0,0.5)' }}>
          Continuer →
        </button>
      </div>
    </div>,
    document.body
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

const VideoEditor: React.FC<VideoEditorProps> = ({ file, onSave, onCancel, availableFiles, currentPath = '' }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<number>(0);

  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [clips, setClips] = useState<Clip[]>([]);
  const [texts, setTexts] = useState<TextOverlay[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [audioVolume, setAudioVolume] = useState(100);
  const [audioMainVolume, setAudioMainVolume]   = useState(100); // volume maître piste AUDIO
  const [audioTrackVolume, setAudioTrackVolume] = useState(100); // volume maître piste VOIX
  const [musicTrackVolume, setMusicTrackVolume] = useState(100); // volume maître piste MUSIC
  const [trackPicker, setTrackPicker] = useState<{ cb: (t: 'main' | 'audio' | 'music') => void } | null>(null);
  const [voiceTrack, setVoiceTrack] = useState<'main' | 'audio' | 'music'>('audio');
  const [audioEditLayerId, setAudioEditLayerId] = useState<string | null>(null);
  const [audioEditFile, setAudioEditFile]       = useState<FileItem | null>(null);
  const [audioExtractMsg, setAudioExtractMsg]   = useState<string | null>(null); // "Extraction audio…"
  const [showVideoEffects, setShowVideoEffects] = useState(false);
  const [mainVfxConfig,   setMainVfxConfig]   = useState<VfxConfig | null>(null);
  const [processing, setProcessing] = useState(false);
  const [processMsg, setProcessMsg] = useState('');
  const [error, setError] = useState('');

  // New states
  const [displayName, setDisplayName] = useState(file.name);
  const [saveModal, setSaveModal] = useState(false);
  const [saveFileName, setSaveFileName] = useState('');
  const [saveFormat, setSaveFormat] = useState<{
    resolution: 'original' | '4k' | '1080p' | '720p' | '480p' | '1080x1080' | '1080x1920' | 'custom';
    customWidth: number;
    customHeight: number;
    quality: 'high' | 'normal' | 'low';
    fps: 0 | 24 | 30 | 60;
    format: 'mp4' | 'webm';
    waveformOverlay: boolean;
    waveformColor: string;
    waveformHeight: number;
  }>({ resolution: !file.url ? '1080p' : 'original', customWidth: 1920, customHeight: 1080, quality: 'normal', fps: 0, format: 'mp4', waveformOverlay: false, waveformColor: '#ffffff', waveformHeight: 60 });
  const [audioLayers, setAudioLayers] = useState<AudioLayer[]>([]);
  const [extraSources, setExtraSources] = useState<Record<string, ExtraSource>>({});
  const [videoOverlays, setVideoOverlays] = useState<VideoOverlay[]>([]);
  const [showAddFile, setShowAddFile] = useState<'video' | 'audio' | 'text' | 'image' | 'overlay' | null>(null);
  const [pickerPath, setPickerPath]   = useState('');
  const [pickerFiles, setPickerFiles] = useState<FileItem[]>([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerSearch, setPickerSearch]   = useState('');
  const [pickerCallback, setPickerCallback] = useState<((file: FileItem) => void) | null>(null);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  // Voice recording
  const [showVoiceRecordModal, setShowVoiceRecordModal] = useState(false);
  const [isVoiceRecording, setIsVoiceRecording] = useState(false);
  const voiceRecordedChunksRef = useRef<Blob[]>([]);
  const voiceRecorderRef = useRef<MediaRecorder | null>(null);
  const voiceStreamRef = useRef<MediaStream | null>(null);

  // ── AI Studio panel ──────────────────────────────────────────────────────────
  const [aiPanelTask, setAiPanelTask] = useState<string | null>(null);
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [aiProgress, setAiProgress] = useState<number | null>(null);
  const [aiVideoModels, setAiVideoModels] = useState<Array<{id: string; label: string; provider: string}>>([]);
  const [selectedAiModel, setSelectedAiModel] = useState('');
  const [aiSelectedImage, setAiSelectedImage] = useState<FileItem | null>(null);
  // TTS
  const [ttsText, setTtsText] = useState('');
  const [ttsVoice, setTtsVoice] = useState('nova');
  const [ttsModelQ, setTtsModelQ] = useState('tts-1');
  const [isRunningTts, setIsRunningTts] = useState(false);
  // Subtitles
  const [subLanguage, setSubLanguage] = useState('auto');
  const [subHardcode, setSubHardcode] = useState(true);
  const [subFont, setSubFont] = useState('open-sans');
  const [subFontSize, setSubFontSize] = useState(60);
  const [subColor, setSubColor] = useState('#ffffff');
  const [subOutline, setSubOutline] = useState('#000000');
  const [subBold, setSubBold] = useState(false);
  const [subtitleSrt, setSubtitleSrt] = useState<string | null>(null);
  const [isRunningSubtitles, setIsRunningSubtitles] = useState(false);
  const [subTextIds, setSubTextIds] = useState<string[]>([]);
  // Lip sync
  const [lipSyncAudioKey, setLipSyncAudioKey] = useState<string>('');
  // Face swap
  const [faceSwapVideoKey, setFaceSwapVideoKey] = useState<string>(''); // clip vidéo cible
  const [faceSwapSourceKey, setFaceSwapSourceKey] = useState<string>(''); // image source (visage)
  // Extract frame
  const [showExtractModal, setShowExtractModal] = useState(false);
  const [extractFileName, setExtractFileName] = useState('');
  // ASS Subtitle editor
  const [showSubEditor, setShowSubEditor] = useState(false);
  const [assTextIds, setAssTextIds] = useState<string[]>([]);
  // Live recording
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const recordedChunksRef = useRef<Blob[]>([]);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordVideoRef = useRef<HTMLVideoElement | null>(null);
  const recordStreamRef = useRef<MediaStream | null>(null);

  // ── Undo/Redo history ────────────────────────────────────────────────────────
  const historyRef = useRef<Array<{ clips: Clip[], texts: TextOverlay[], audioLayers: AudioLayer[], videoOverlays: VideoOverlay[], extraSources: Record<string, ExtraSource> }>>([]);
  const historyIdxRef = useRef(-1);
  const [historyIdx, setHistoryIdx] = useState(-1);

  // ── Timeline markers ────────────────────────────────────────────────────────
  const [markers, setMarkers] = useState<TimelineMarker[]>([]);
  const [markerEditId, setMarkerEditId] = useState<string | null>(null);

  // ── Interactive elements ─────────────────────────────────────────────────────
  const [interactiveElements, setInteractiveElements] = useState<InteractiveElement[]>([]);
  const [showInteractivePanel, setShowInteractivePanel] = useState(false);
  const [selectedInteractiveId, setSelectedInteractiveId] = useState<string | null>(null);
  const [interactivePreviewMode, setInteractivePreviewMode] = useState(false);
  const [activeInteractive, setActiveInteractive] = useState<InteractiveElement | null>(null);
  const [interactiveScore, setInteractiveScore] = useState({ correct: 0, total: 0 });
  const [showEndScreen, setShowEndScreen] = useState(false);
  const [showAddInteractiveType, setShowAddInteractiveType] = useState(false);
  const [hotspotPopup, setHotspotPopup] = useState<{ el: InteractiveElement; content: string } | null>(null);
  const [interactiveAnswerState, setInteractiveAnswerState] = useState<{
    selectedChoiceId: string | null;
    showFeedback: boolean;
    isCorrect: boolean;
  }>({ selectedChoiceId: null, showFeedback: false, isCorrect: false });

  // ── Clipboard ────────────────────────────────────────────────────────────────
  const [clipboard, setClipboard] = useState<{ type: 'clip'; data: Clip } | { type: 'text'; data: TextOverlay } | { type: 'audio'; data: AudioLayer } | null>(null);

  // ── Multi-selection ──────────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // ── Render queue ─────────────────────────────────────────────────────────────
  const [renderQueue, setRenderQueue] = useState<RenderQueueItem[]>([]);
  const [showRenderQueue, setShowRenderQueue] = useState(false);
  const renderQueueRef = useRef<RenderQueueItem[]>([]);
  renderQueueRef.current = renderQueue;

  // ── Storyboard ───────────────────────────────────────────────────────────────
  const [showStoryboard, setShowStoryboard] = useState(false);

  // ── Thumbnails (clipId → dataURL) ────────────────────────────────────────────
  const [clipThumbnails, setClipThumbnails] = useState<Record<string, string[]>>({});

  // ── Waveforms (sourceKey → dataURL) ─────────────────────────────────────────
  const [waveformCache, setWaveformCache] = useState<Record<string, string>>({});

  // ── Render SSE progress ──────────────────────────────────────────────────────
  const [renderProgress, setRenderProgress] = useState<{ percent: number; message: string } | null>(null);

  // ── Snap state ───────────────────────────────────────────────────────────────
  const [snapEnabled, setSnapEnabled] = useState(true);
  const snapEnabledRef = useRef(true);
  snapEnabledRef.current = snapEnabled;

  const [primaryLoading, setPrimaryLoading] = useState(false); // true pendant le chargement d'une nouvelle source (évite frame noire après crossfade)

  const lastSrcRef       = useRef<string>('');
  const isSwitchingRef   = useRef(false);  // true pendant le chargement d'une nouvelle source
  const playingRef       = useRef(false);  // miroir de `playing` accessible sans deps dans les effets
  const gapStartClockRef = useRef<number | null>(null); // horloge murale pour les périodes sans clip (image/blanc)
  const gapStartTimeRef  = useRef<number>(0);           // currentTime au début du blanc
  const videoNextRef     = useRef<HTMLVideoElement>(null); // second élément pour cross-fade
  const transNextSrcRef  = useRef<string>('');             // dernière URL chargée dans videoNextRef

  const isCreationMode = !file.url; // éditeur vierge : pas de fichier source

  const dragRef = useRef<DragState | null>(null);
  const clipsRef = useRef<Clip[]>([]);
  const textsRef = useRef<TextOverlay[]>([]);
  const currentTimeRef = useRef(0);
  const extraSourcesRef = useRef(extraSources);
  const audioLayersRef = useRef(audioLayers);
  const videoOverlaysRef = useRef<VideoOverlay[]>([]);
  clipsRef.current        = clips;
  textsRef.current        = texts;
  currentTimeRef.current  = currentTime;
  extraSourcesRef.current = extraSources;
  audioLayersRef.current  = audioLayers;
  videoOverlaysRef.current = videoOverlays;
  playingRef.current      = playing;

  const interactiveElementsRef = useRef<InteractiveElement[]>([]);
  interactiveElementsRef.current = interactiveElements;
  const dismissedInteractiveRef = useRef<Set<string>>(new Set());

  // Map layerId → HTMLAudioElement for browser preview
  const audioElsRef = useRef<Map<string, HTMLAudioElement>>(new Map());
  // Map overlayId → HTMLVideoElement for overlay PiP preview
  const overlayVideoElsRef = useRef<Map<string, HTMLVideoElement>>(new Map());

  const PX = 60 * zoom;
  const PXRef = useRef(PX);
  PXRef.current = PX;
  const durationRef = useRef(duration);
  durationRef.current = duration;
  const totalDurationRef = useRef(duration); // mis à jour après calcul de tot

  // ─── Init ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onMeta = () => {
      const d = v.duration;
      setDuration(d);
      setClips([{
        id: uid(), srcStart: 0, srcEnd: d, trackStart: 0,
        filter: 'none', speed: 1, brightness: 100, contrast: 100,
        saturation: 100, reverse: false,
      }]);
      // Créer automatiquement la piste audio MAIN pour le fichier vidéo principal
      setExtraSources(s => ({
        ...s,
        '__main__': { key: '__main__', fileItem: file, type: 'video' as const },
      }));
      setAudioLayers(ls => {
        if (ls.some(l => l.sourceKey === '__main__')) return ls;
        return [...ls, { id: uid(), sourceKey: '__main__', trackStart: 0, duration: d, volume: 100, track: 'main' as const }];
      });
    };
    // { once: true } : ne s'exécute qu'une fois (source initiale).
    // Sans ça, chaque changement de source (addVideoSource, etc.) re-déclenche onMeta
    // et réinitialise clips[] en effaçant les clips ajoutés.
    v.addEventListener('loadedmetadata', onMeta, { once: true });
    lastSrcRef.current = file.url;
    v.src = file.url;
    return () => v.removeEventListener('loadedmetadata', onMeta);
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || playing || isSwitchingRef.current) return;
    // Convertir la position timeline en position dans le fichier source du clip actif
    const active = clipsRef.current.find(c => currentTime >= c.trackStart && currentTime < clipTrackEnd(c));
    v.currentTime = active
      ? active.srcStart + (currentTime - active.trackStart) * (active.speed || 1)
      : currentTime;
  }, [currentTime, playing]);

  useEffect(() => {
    if (!playing) { cancelAnimationFrame(animRef.current); return; }
    gapStartClockRef.current = null; // reset à chaque début de lecture
    const tick = () => {
      const v = videoRef.current;
      if (v && isSwitchingRef.current && playingRef.current) {
        // Source en cours de chargement — avancer via horloge murale pour que currentTime
        // progresse (évite fadeInProgress bloqué à 0 pendant transitionIn)
        const now = Date.now();
        if (gapStartClockRef.current === null) {
          gapStartClockRef.current = now;
          gapStartTimeRef.current  = currentTimeRef.current;
        }
        const newTime = Math.min(
          gapStartTimeRef.current + (now - gapStartClockRef.current) / 1000,
          totalDurationRef.current,
        );
        setCurrentTime(newTime);
      } else if (v && !isSwitchingRef.current) {
        // Arrêter à la fin de la timeline (clips vidéo + images)
        const clips  = clipsRef.current;
        const extras = extraSourcesRef.current;
        const videoDur  = totalDuration(clips);
        const imgMaxEnd = Object.values(extras)
          .filter(s => s.type === 'image')
          .reduce((acc, s) => Math.max(acc, (s.trackStart ?? 0) + (s.duration ?? 3)), 0);
        const total = Math.max(videoDur, imgMaxEnd);
        if (total > 0 && currentTimeRef.current >= total) {
          v.pause();
          setPlaying(false);
          gapStartClockRef.current = null;
          return;
        }

        // v.currentTime est relatif au fichier source — le convertir en position timeline
        const srcUrl = lastSrcRef.current;
        const active = clips.find(c => {
          const url = c.sourceKey ? (extras[c.sourceKey]?.fileItem.url ?? file.url) : file.url;
          return url === srcUrl
            && v.currentTime >= c.srcStart - 0.05
            && v.currentTime <= c.srcEnd + 0.1
            && currentTimeRef.current >= c.trackStart      // ne pas confondre avec un clip ultérieur
            && currentTimeRef.current < clipTrackEnd(c);  // éviter d'être bloqué quand v.ended
        });
        if (active) {
          gapStartClockRef.current = null; // on est sur un clip — reset le chrono de blanc
          // Si la vidéo est terminée (currentTime peut s'arrêter légèrement avant srcEnd), forcer srcEnd
          const srcTime = v.ended ? active.srcEnd : Math.min(v.currentTime, active.srcEnd);
          const pos = active.trackStart + (srcTime - active.srcStart) / (active.speed || 1);
          setCurrentTime(pos);
        } else if (playingRef.current && currentTimeRef.current < total) {
          // Période sans clip vidéo (image ou blanc) — avancer via horloge murale
          const now = Date.now();
          if (gapStartClockRef.current === null) {
            gapStartClockRef.current = now;
            gapStartTimeRef.current  = currentTimeRef.current;
          }
          const newTime = Math.min(
            gapStartTimeRef.current + (now - gapStartClockRef.current) / 1000,
            total,
          );
          setCurrentTime(newTime);
        }
      }
      animRef.current = requestAnimationFrame(tick);
    };
    animRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animRef.current);
  }, [playing]); // stable — file.url capturé à l'init, refs pour le reste

  // ─── Source switching : change video src when active clip changes source ──────
  const activeClipForSrc = clips.find(c => currentTime >= c.trackStart && currentTime < clipTrackEnd(c));
  const activeSrcUrl = activeClipForSrc
    ? (activeClipForSrc.sourceKey ? (extraSources[activeClipForSrc.sourceKey]?.fileItem.url ?? file.url) : file.url)
    : null; // null = période image/blanc — ne pas déclencher de changement de source

  useEffect(() => {
    if (!activeSrcUrl) return; // période image/blanc — ignorer
    const v = videoRef.current;
    if (!v) return;

    // Retrouver le clip cible pour positionner correctement v.currentTime
    const clip = clipsRef.current.find(c => {
      const url = c.sourceKey ? (extraSourcesRef.current[c.sourceKey]?.fileItem.url ?? file.url) : file.url;
      return url === activeSrcUrl && currentTimeRef.current >= c.trackStart && currentTimeRef.current < clipTrackEnd(c);
    });

    if (activeSrcUrl === lastSrcRef.current) {
      // Même source — recaler si on revient sur ce clip après un blanc/image
      if (clip && !isSwitchingRef.current && Math.abs(v.currentTime - clip.srcStart) > 0.3) {
        isSwitchingRef.current = true;
        v.currentTime = clip.srcStart;
        v.addEventListener('seeked', () => {
          isSwitchingRef.current = false;
          if (playingRef.current && v.paused) v.play().catch(() => {});
        }, { once: true });
      }
      return;
    }

    isSwitchingRef.current = true;  // suspendre le tick (tick wall-clock prend le relais)
    lastSrcRef.current = activeSrcUrl;
    setPrimaryLoading(true);

    // Utiliser playingRef (intention utilisateur) plutôt que !v.paused :
    // quand v.ended=true, le navigateur met v.paused=true même si on voulait continuer
    const wasPlaying = playingRef.current;
    if (!v.paused) v.pause();
    v.src = activeSrcUrl;
    v.load();

    v.addEventListener('loadedmetadata', () => {
      const ct = currentTimeRef.current;
      const activeClip = clipsRef.current.find(c => ct >= c.trackStart && ct < clipTrackEnd(c));
      v.currentTime = activeClip
        ? activeClip.srcStart + (ct - activeClip.trackStart) * (activeClip.speed || 1)
        : (clip?.srcStart ?? 0);
      // Attendre seeked avant de reprendre — évite frame à mauvaise position
      v.addEventListener('seeked', () => {
        isSwitchingRef.current = false; // reprendre le tick
        setPrimaryLoading(false);
        gapStartClockRef.current = null; // reset horloge murale utilisée pendant le chargement
        if (wasPlaying) v.play().catch(() => {});
      }, { once: true });
    }, { once: true });
  }, [activeSrcUrl]); // stable — lit refs pour extraSources/clips/currentTime

  // ─── Secondary video : préchargement et lecture lors des zones de transition ──
  // transInfoNextSrc change uniquement quand on entre dans une nouvelle zone
  const transInfoForEffect = (() => {
    for (let i = 1; i < clips.length; i++) {
      const prev = clips[i - 1];
      const curr = clips[i];
      if (!curr.transition) continue;
      const zoneStart = clipTrackEnd(prev) - curr.transition.duration;
      if (currentTime >= zoneStart && currentTime < clipTrackEnd(prev)) {
        const ns = curr.sourceKey ? (extraSources[curr.sourceKey]?.fileItem.url ?? file.url) : file.url;
        return { nextSrc: ns, nextSrcStart: curr.srcStart };
      }
    }
    return null;
  })();

  useEffect(() => {
    const vNext = videoNextRef.current;
    if (!vNext) return;
    if (!transInfoForEffect) { vNext.pause(); return; }
    if (transNextSrcRef.current === transInfoForEffect.nextSrc) {
      // Déjà chargé — repositionner au début du clip si on re-rentre dans la zone
      // (évite que vNext soit en milieu de clip après une sortie/re-entrée de la zone)
      vNext.currentTime = transInfoForEffect.nextSrcStart;
      return;
    }
    transNextSrcRef.current = transInfoForEffect.nextSrc;
    vNext.src = transInfoForEffect.nextSrc;
    vNext.load();
    vNext.addEventListener('loadedmetadata', () => {
      vNext.currentTime = transInfoForEffect.nextSrcStart;
      if (playingRef.current) vNext.play().catch(() => {});
    }, { once: true });
  }, [transInfoForEffect?.nextSrc]); // eslint-disable-line

  useEffect(() => {
    if (!playing) videoNextRef.current?.pause();
  }, [playing]);

  // ── Audio layer preview: create/destroy <audio> elements ──────────────────────
  useEffect(() => {
    const map = audioElsRef.current;
    // Create missing elements
    for (const layer of audioLayers) {
      const src = extraSources[layer.sourceKey];
      if (!src) continue;
      if (!map.has(layer.id)) {
        const el = new Audio(src.fileItem.url);
        el.preload = 'auto';
        map.set(layer.id, el);
      } else {
        // URL may have changed (e.g. after AudioEditor save)
        const el = map.get(layer.id)!;
        const srcUrl = src.fileItem.url;
        if (!el.src.endsWith(srcUrl) && el.src !== srcUrl) {
          el.pause();
          el.src = srcUrl;
        }
      }
    }
    // Remove stale elements
    Array.from(map.entries()).forEach(([id, el]) => {
      if (!audioLayers.find(l => l.id === id)) {
        el.pause();
        map.delete(id);
      }
    });
  }, [audioLayers, extraSources]);

  // Play/pause audio elements when playing state changes
  useEffect(() => {
    const map = audioElsRef.current;
    if (!playing) {
      Array.from(map.values()).forEach(el => el.pause());
      return;
    }
    for (const layer of audioLayersRef.current) {
      const el = map.get(layer.id);
      if (!el) continue;
      const t = currentTimeRef.current;
      const inRange = t >= layer.trackStart && t < layer.trackStart + layer.duration;
      if (inRange) {
        el.currentTime = t - layer.trackStart;
        el.volume = Math.min(1, layer.volume / 100);
        el.play().catch(() => {});
      } else {
        el.pause();
      }
    }
  }, [playing]); /* intentionally omit audioLayers — uses ref */

  // Sync video element volume: mute it when __main__ AudioLayer exists (audio comes from the
  // dedicated Audio element for that layer), or set it to audioVolume when there's no __main__.
  // This ensures that deleting the __main__ layer (which sets audioVolume=0) actually silences
  // the embedded video audio during preview, not just at render time.
  useEffect(() => {
    if (!videoRef.current) return;
    const hasMain = audioLayers.some(l => l.sourceKey === '__main__');
    videoRef.current.volume = hasMain ? 0 : Math.min(1, audioVolume / 100);
  }, [audioVolume, audioLayers]);

  // Cleanup stale overlay video element refs when overlays are removed
  useEffect(() => {
    const map = overlayVideoElsRef.current;
    Array.from(map.keys()).forEach(id => {
      if (!videoOverlaysRef.current.find(ov => ov.id === id)) {
        map.get(id)?.pause();
        map.delete(id);
      }
    });
  }, [videoOverlays]);

  // Sync overlay video elements (PiP) with timeline position and play/pause state
  useEffect(() => {
    const map = overlayVideoElsRef.current;
    for (const ov of videoOverlaysRef.current) {
      if (!ov.isVideo) continue;
      const el = map.get(ov.id);
      if (!el) continue;
      const relTime = currentTime - ov.trackStart;
      const inRange = relTime >= 0 && relTime < ov.duration;
      if (inRange) {
        // Resync if drift > 0.3s
        if (Math.abs(el.currentTime - relTime) > 0.3) el.currentTime = relTime;
        if (playing && el.paused) el.play().catch(() => {});
        else if (!playing && !el.paused) el.pause();
      } else {
        if (!el.paused) el.pause();
      }
    }
  }, [currentTime, playing]); // uses refs for overlays

  // ── Interactive element trigger ────────────────────────────────────────────
  useEffect(() => {
    if (!interactivePreviewMode || !playing || activeInteractive) return;
    const WINDOW = 0.3;
    const trigger = interactiveElementsRef.current.find(el => {
      if (dismissedInteractiveRef.current.has(el.id)) return false;
      if (el.type === 'hotspot') return false;
      return currentTime >= el.time && currentTime < el.time + WINDOW;
    });
    if (trigger) {
      setActiveInteractive(trigger);
      setInteractiveAnswerState({ selectedChoiceId: null, showFeedback: false, isCorrect: false });
      if (trigger.pauseVideo) {
        setPlaying(false);
        videoRef.current?.pause();
      }
    }
  }, [currentTime, interactivePreviewMode, playing, activeInteractive]);

  // ── End screen when video finishes in preview mode ─────────────────────────
  useEffect(() => {
    if (!interactivePreviewMode) return;
    const quizCount = interactiveElementsRef.current.filter(e => e.type === 'quiz' || e.type === 'trueFalse').length;
    if (!playing && quizCount > 0 && interactiveScore.total === quizCount && !activeInteractive) {
      setShowEndScreen(true);
    }
  }, [playing, interactivePreviewMode, interactiveScore.total, activeInteractive]);

  // ── Reset on exit preview mode ─────────────────────────────────────────────
  useEffect(() => {
    if (!interactivePreviewMode) {
      dismissedInteractiveRef.current = new Set();
      setActiveInteractive(null);
      setInteractiveAnswerState({ selectedChoiceId: null, showFeedback: false, isCorrect: false });
      setInteractiveScore({ correct: 0, total: 0 });
      setShowEndScreen(false);
      setHotspotPopup(null);
    }
  }, [interactivePreviewMode]);

  // Sync audio positions on currentTime change (seeking or playing)
  useEffect(() => {
    const map = audioElsRef.current;
    for (const layer of audioLayersRef.current) {
      const el = map.get(layer.id);
      if (!el) continue;
      const inRange = currentTime >= layer.trackStart && currentTime < layer.trackStart + layer.duration;
      if (playing && inRange) {
        // Start element if not already playing at approximately the right position
        const expectedPos = currentTime - layer.trackStart;
        if (el.paused) {
          el.currentTime = expectedPos;
          el.volume = Math.min(1, layer.volume / 100);
          el.play().catch(() => {});
        }
      } else if (playing && !inRange) {
        el.pause();
      } else if (!playing) {
        // Seeking while paused
        if (inRange) el.currentTime = currentTime - layer.trackStart;
        else el.pause();
      }
    }
  }, [currentTime, playing]);

  // ── File picker: load files when modal opens or path changes ─────────────────
  useEffect(() => {
    if (!showAddFile) return;
    setPickerLoading(true);
    storageService.listFiles(pickerPath)
      .then(files => setPickerFiles(files))
      .catch(() => setPickerFiles([]))
      .finally(() => setPickerLoading(false));
  }, [showAddFile, pickerPath]);

  // Reset picker when modal closes
  useEffect(() => {
    if (!showAddFile) { setPickerPath(currentPath || ''); setPickerSearch(''); }
  }, [showAddFile, currentPath]);

  // ── Load AI video models (same as CloudFileManager) ──────────────────────────
  useEffect(() => {
    let cancelled = false;
    const loadModels = async () => {
      setIsLoadingModels(true);
      try {
        const [keys, providersResp] = await Promise.all([
          apiKeyService.getApiKeys(),
          axios.get('/api/ai/providers').catch(() => ({ data: {} })),
        ]);
        const hasOpenAI  = keys.some((k: any) => k.service === 'OpenAI'   && k.isActive);
        const hasGoogleAI= keys.some((k: any) => k.service === 'GoogleAI' && k.isActive);
        const hasLTX     = keys.some((k: any) => k.service === 'LTX'      && k.isActive);
        const hasComfyUI = !!(providersResp.data?.comfyui);
        const hasFalAI   = !!(providersResp.data?.falai);
        const models: Array<{id: string; label: string; provider: string}> = [
          ...(hasOpenAI   ? [{ id: 'sora-2',                     label: 'Sora 2',                         provider: 'OpenAI' }] : []),
          ...(hasGoogleAI ? [{ id: 'veo-3.1-generate-preview',   label: 'Veo 3.1 (preview)',              provider: 'GoogleAI' }] : []),
          ...(hasLTX      ? [
            { id: 'ltx-2-fast', label: 'LTX 2 Fast', provider: 'LTX' },
            { id: 'ltx-2-pro',  label: 'LTX 2 Pro',  provider: 'LTX' },
          ] : []),
          ...(hasComfyUI  ? [{ id: 'wan2.1',                     label: 'Wan 2.1 T2V (vast.ai GPU)',      provider: 'ComfyUI' }] : []),
          ...(hasFalAI    ? [
            { id: 'fal-ai/wan/v2.1/14b', label: 'Wan 2.1 14B T2V (fal.ai)',       provider: 'FalAI' },
            { id: 'fal-ai/wan/v2.1/1.3b',label: 'Wan 2.1 1.3B T2V — rapide (fal.ai)', provider: 'FalAI' },
          ] : []),
        ];
        if (!cancelled) {
          setAiVideoModels(models);
          if (models.length > 0) setSelectedAiModel(models[0].id);
        }
      } catch { /* ignore */ } finally {
        if (!cancelled) setIsLoadingModels(false);
      }
    };
    loadModels();
    return () => { cancelled = true; };
  }, []);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (playing) { v.pause(); setPlaying(false); }
    else if (clipsRef.current.length > 0) { v.play(); setPlaying(true); }
  };

  // ─── Timeline mouse ────────────────────────────────────────────────────────

  const onTimelineMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (dragRef.current) return;
    const rect = timelineRef.current!.getBoundingClientRect();
    // Soustraire 60px (largeur du label) pour obtenir la position relative au contenu clips
    const x = e.clientX - rect.left + (timelineRef.current!.scrollLeft) - 60;
    const t = Math.max(0, x / PX);
    setCurrentTime(t);
  };

  const onPlayheadMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    dragRef.current = { type: 'playhead', id: '', startX: e.clientX, origVal: currentTime };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const startClipDrag = (e: React.MouseEvent, type: DragState['type'], id: string, origVal: number, origVal2?: number) => {
    e.stopPropagation();
    e.preventDefault();
    dragRef.current = { type, id, startX: e.clientX, origVal, origVal2 };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dt = dx / PXRef.current;

    if (d.type === 'playhead') {
      setCurrentTime(Math.max(0, Math.min(d.origVal + dt, totalDurationRef.current)));
      return;
    }
    if (d.type === 'clip') {
      // Seuil de 5px : en dessous → scrub du playhead (pas de déplacement de clip)
      if (Math.abs(dx) < 5) {
        const rect = timelineRef.current?.getBoundingClientRect();
        if (rect && timelineRef.current) {
          const x = e.clientX - rect.left + timelineRef.current.scrollLeft - 60;
          setCurrentTime(Math.max(0, x / PXRef.current));
        }
        return;
      }
      setClips(cs => cs.map(c => c.id === d.id
        ? { ...c, trackStart: Math.max(0, snapTime(d.origVal + dt, d.id)) }
        : c));
      return;
    }
    if (d.type === 'trim-r') {
      setClips(cs => cs.map(c => {
        if (c.id !== d.id) return c;
        const newSrcEnd = Math.min(durationRef.current, Math.max(c.srcStart + 0.2, d.origVal + dt * c.speed));
        return { ...c, srcEnd: newSrcEnd };
      }));
      return;
    }
    if (d.type === 'trim-l') {
      setClips(cs => cs.map(c => {
        if (c.id !== d.id) return c;
        const newSrcStart = Math.max(0, Math.min(c.srcEnd - 0.2, d.origVal + dt * c.speed));
        const newTrackStart = Math.max(0, (d.origVal2 ?? c.trackStart) + dt);
        return { ...c, srcStart: newSrcStart, trackStart: newTrackStart };
      }));
      return;
    }
    if (d.type === 'text') {
      if (Math.abs(dx) < 5) return; // seuil de 5px : évite le déplacement accidentel au clic
      setTexts(ts => ts.map(t => t.id === d.id
        ? { ...t, trackStart: Math.max(0, snapTime(d.origVal + dt, d.id)) }
        : t));
      return;
    }
    if (d.type === 'text-trim-r') {
      setTexts(ts => ts.map(t => t.id === d.id
        ? { ...t, duration: Math.max(0.5, d.origVal + dt) }
        : t));
      return;
    }
    if (d.type === 'text-trim-l') {
      setTexts(ts => ts.map(t => t.id === d.id
        ? { ...t, trackStart: Math.max(0, d.origVal + dt), duration: Math.max(0.5, (d.origVal2 ?? t.duration) - dt) }
        : t));
    }
    if (d.type === 'audio-layer') {
      setAudioLayers(ls => ls.map(l => l.id === d.id
        ? { ...l, trackStart: Math.max(0, snapTime(d.origVal + dt, d.id)) }
        : l));
      return;
    }
    if (d.type === 'audio-layer-trim-r') {
      setAudioLayers(ls => ls.map(l => l.id === d.id
        ? { ...l, duration: Math.max(0.5, d.origVal + dt) }
        : l));
      return;
    }
    if (d.type === 'audio-layer-trim-l') {
      setAudioLayers(ls => ls.map(l => l.id === d.id
        ? { ...l, trackStart: Math.max(0, d.origVal + dt), duration: Math.max(0.5, (d.origVal2 ?? l.duration) - dt) }
        : l));
      return;
    }
    if (d.type === 'image-block') {
      setExtraSources(prev => {
        const src = prev[d.id];
        if (!src) return prev;
        return { ...prev, [d.id]: { ...src, trackStart: Math.max(0, d.origVal + dt) } };
      });
      return;
    }
    if (d.type === 'image-trim-r') {
      setExtraSources(prev => {
        const src = prev[d.id];
        if (!src) return prev;
        return { ...prev, [d.id]: { ...src, duration: Math.max(0.5, d.origVal + dt) } };
      });
      return;
    }
    if (d.type === 'overlay-block') {
      setVideoOverlays(ovs => ovs.map(ov => ov.id === d.id
        ? { ...ov, trackStart: Math.max(0, snapTime(d.origVal + dt, d.id)) }
        : ov));
      return;
    }
    if (d.type === 'overlay-trim-r') {
      setVideoOverlays(ovs => ovs.map(ov => ov.id === d.id
        ? { ...ov, duration: Math.max(0.5, d.origVal + dt) }
        : ov));
      return;
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    if (dragRef.current) {
      // Push history on end of drag
      pushHistory();
    }
    dragRef.current = null;
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]); // pushHistory added below

  // ─── Undo/Redo ─────────────────────────────────────────────────────────────

  // pushHistory enregistre l'état APRÈS l'action (snapshot post-mutation).
  const pushHistory = useCallback((
    newClips: Clip[] = clipsRef.current,
    newTexts: TextOverlay[] = textsRef.current,
    newAudioLayers: AudioLayer[] = audioLayersRef.current,
    newVideoOverlays: VideoOverlay[] = videoOverlaysRef.current,
    newExtraSources: Record<string, ExtraSource> = extraSourcesRef.current,
  ) => {
    const snap = { clips: newClips, texts: newTexts, audioLayers: newAudioLayers, videoOverlays: newVideoOverlays, extraSources: newExtraSources };
    const trimmed = historyRef.current.slice(0, historyIdxRef.current + 1);
    trimmed.push(snap);
    if (trimmed.length > 61) trimmed.shift();
    historyRef.current = trimmed;
    historyIdxRef.current = trimmed.length - 1;
    setHistoryIdx(trimmed.length - 1);
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      // ── Undo / Redo ──────────────────────────────────────────────────────────
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (historyIdxRef.current > 0) {
          historyIdxRef.current--;
          const s = historyRef.current[historyIdxRef.current];
          setClips(s.clips); setTexts(s.texts); setAudioLayers(s.audioLayers);
          if (s.videoOverlays) setVideoOverlays(s.videoOverlays);
          if (s.extraSources) setExtraSources(s.extraSources);
          setHistoryIdx(historyIdxRef.current);
        }
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        if (historyIdxRef.current < historyRef.current.length - 1) {
          historyIdxRef.current++;
          const s = historyRef.current[historyIdxRef.current];
          setClips(s.clips); setTexts(s.texts); setAudioLayers(s.audioLayers);
          if (s.videoOverlays) setVideoOverlays(s.videoOverlays);
          if (s.extraSources) setExtraSources(s.extraSources);
          setHistoryIdx(historyIdxRef.current);
        }
        return;
      }

      // ── Copy / Paste ─────────────────────────────────────────────────────────
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        const sid = selectedId;
        if (!sid) return;
        const clip = clipsRef.current.find(c => c.id === sid);
        if (clip) { setClipboard({ type: 'clip', data: { ...clip } }); return; }
        const text = textsRef.current.find(t => t.id === sid);
        if (text) { setClipboard({ type: 'text', data: { ...text } }); return; }
        const audio = audioLayersRef.current.find(l => l.id === sid);
        if (audio) { setClipboard({ type: 'audio', data: { ...audio } }); return; }
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        e.preventDefault();
        setClipboard(cb => {
          if (!cb) return cb;
          const newId = uid();
          if (cb.type === 'clip') {
            const insertAt = currentTimeRef.current;
            const newClip = { ...cb.data, id: newId, trackStart: insertAt };
            const newClips = [...clipsRef.current, newClip];
            setClips(newClips);
            setSelectedId(newId);
            pushHistory(newClips, textsRef.current, audioLayersRef.current);
          } else if (cb.type === 'text') {
            const newText = { ...cb.data, id: newId, trackStart: currentTimeRef.current };
            const newTexts = [...textsRef.current, newText];
            setTexts(newTexts);
            setSelectedId(newId);
            pushHistory(clipsRef.current, newTexts, audioLayersRef.current);
          } else if (cb.type === 'audio') {
            const newLayer = { ...cb.data, id: newId, trackStart: currentTimeRef.current };
            const newLayers = [...audioLayersRef.current, newLayer];
            setAudioLayers(newLayers);
            setSelectedId(newId);
            pushHistory(clipsRef.current, textsRef.current, newLayers);
          }
          return cb;
        });
        return;
      }

      // ── Play / Pause (Space) ─────────────────────────────────────────────────
      if (e.key === ' ' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setPlaying(p => {
          const next = !p;
          if (videoRef.current) { if (next) videoRef.current.play().catch(() => {}); else videoRef.current.pause(); }
          return next;
        });
        return;
      }

      // ── Split (S) ────────────────────────────────────────────────────────────
      if (e.key === 's' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        splitAtPlayhead();
        return;
      }

      // ── Add marker (M) ───────────────────────────────────────────────────────
      if (e.key === 'm' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        const newMarker: TimelineMarker = { id: uid(), time: currentTimeRef.current, label: '', color: '#f59e0b' };
        setMarkers(ms => [...ms, newMarker]);
        return;
      }

      // ── Delete selected ──────────────────────────────────────────────────────
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const sid = selectedId;
        if (!sid) return;
        e.preventDefault();
        const isRipple = e.shiftKey;
        if (clipsRef.current.find(c => c.id === sid)) {
          if (isRipple) {
            const clip = clipsRef.current.find(c => c.id === sid)!;
            const dur = clipDuration(clip);
            const newClips = clipsRef.current
              .filter(c => c.id !== sid)
              .map(c => c.trackStart >= clipTrackEnd(clip) ? { ...c, trackStart: c.trackStart - dur } : c);
            setClips(newClips); setSelectedId(null);
            pushHistory(newClips, textsRef.current, audioLayersRef.current);
          } else {
            deleteClip(sid);
          }
          return;
        }
        if (textsRef.current.find(t => t.id === sid)) { deleteText(sid); return; }
        if (audioLayersRef.current.find(l => l.id === sid)) { deleteAudioLayer(sid); return; }
        return;
      }

      // ── Arrow nudge (←/→, Shift for ×10) ────────────────────────────────────
      if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && !e.ctrlKey && !e.metaKey) {
        const sid = selectedId;
        if (!sid) {
          // Move playhead by 1 frame (assuming 30fps)
          e.preventDefault();
          const delta = (e.key === 'ArrowLeft' ? -1 : 1) * (e.shiftKey ? 1 : 1/30);
          setCurrentTime(t => Math.max(0, t + delta));
          return;
        }
        e.preventDefault();
        const delta = (e.key === 'ArrowLeft' ? -1 : 1) * (e.shiftKey ? 1 : 1/30);
        const clip = clipsRef.current.find(c => c.id === sid);
        if (clip) {
          const newClips = clipsRef.current.map(c => c.id === sid ? { ...c, trackStart: Math.max(0, c.trackStart + delta) } : c);
          setClips(newClips);
          return;
        }
        const text = textsRef.current.find(t => t.id === sid);
        if (text) {
          const newTexts = textsRef.current.map(t => t.id === sid ? { ...t, trackStart: Math.max(0, t.trackStart + delta) } : t);
          setTexts(newTexts);
          return;
        }
        const audio = audioLayersRef.current.find(l => l.id === sid);
        if (audio) {
          const newLayers = audioLayersRef.current.map(l => l.id === sid ? { ...l, trackStart: Math.max(0, l.trackStart + delta) } : l);
          setAudioLayers(newLayers);
          return;
        }
        return;
      }

      // ── J/K/L navigation ─────────────────────────────────────────────────────
      if (e.key === 'j') { e.preventDefault(); setCurrentTime(t => Math.max(0, t - 5)); return; }
      if (e.key === 'k') {
        e.preventDefault();
        setPlaying(p => {
          if (p && videoRef.current) videoRef.current.pause();
          return false;
        });
        return;
      }
      if (e.key === 'l') { e.preventDefault(); setCurrentTime(t => t + 5); return; }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedId]); // eslint-disable-line

  // Sauvegarder l'état initial au montage pour que la 1ère action soit annulable
  useEffect(() => { pushHistory(); }, []); // eslint-disable-line

  // Générer thumbnails pour les clips vidéo
  useEffect(() => {
    clips.forEach(c => {
      if (clipThumbnails[c.id]) return;
      const src = c.sourceKey ? extraSources[c.sourceKey] : null;
      const url = src ? src.fileItem.url : file.url;
      if (url) generateThumbnailStrip(c.id, url, c.srcStart, c.srcEnd);
    });
  }, [clips, extraSources]); // eslint-disable-line

  // Générer waveforms pour les pistes audio
  useEffect(() => {
    audioLayers.forEach(l => {
      if (waveformCache[l.sourceKey]) return;
      const src = extraSources[l.sourceKey];
      if (src) generateWaveform(l.sourceKey, src.fileItem.url);
    });
  }, [audioLayers, extraSources]); // eslint-disable-line

  // ─── Waveform generation ───────────────────────────────────────────────────

  const generateWaveform = useCallback(async (sourceKey: string, url: string) => {
    if (waveformCache[sourceKey]) return;
    try {
      const resp = await fetch(url);
      if (!resp.ok) return;
      const arrayBuf = await resp.arrayBuffer();
      const ctx = new AudioContext();
      let audioBuf: AudioBuffer;
      try {
        audioBuf = await ctx.decodeAudioData(arrayBuf);
      } catch {
        return;
      } finally {
        ctx.close();
      }
      const W = 400; const H = 32;
      const canvas = document.createElement('canvas');
      canvas.width = W; canvas.height = H;
      const c2d = canvas.getContext('2d')!;
      c2d.fillStyle = 'rgba(0,0,0,0)';
      c2d.fillRect(0, 0, W, H);
      c2d.strokeStyle = 'rgba(255,255,255,0.7)';
      c2d.lineWidth = 1;
      const data = audioBuf.getChannelData(0);
      const step = Math.ceil(data.length / W);
      const mid = H / 2;
      c2d.beginPath();
      for (let i = 0; i < W; i++) {
        let min = 0; let max = 0;
        for (let j = 0; j < step; j++) {
          const v = data[i * step + j] ?? 0;
          if (v < min) min = v; if (v > max) max = v;
        }
        c2d.moveTo(i, mid + min * mid);
        c2d.lineTo(i, mid + max * mid);
      }
      c2d.stroke();
      const dataUrl = canvas.toDataURL('image/png');
      setWaveformCache(prev => ({ ...prev, [sourceKey]: dataUrl }));
    } catch { /* silently skip */ }
  }, [waveformCache]);

  // ─── Thumbnail strip generation ────────────────────────────────────────────

  const generateThumbnailStrip = useCallback((clipId: string, videoUrl: string, srcStart: number, srcEnd: number) => {
    if (clipThumbnails[clipId]) return;
    const TW = 80, TH = 45; // 16:9 miniature
    const dur = Math.max(0.1, srcEnd - srcStart);
    const numThumbs = Math.min(20, Math.max(1, Math.ceil(dur / 2)));
    const seekTimes = Array.from({ length: numThumbs }, (_, i) =>
      srcStart + (dur / numThumbs) * i + (dur / numThumbs) * 0.5,
    );
    const results: string[] = [];
    let idx = 0;

    const vid = document.createElement('video');
    vid.crossOrigin = 'anonymous';
    vid.muted = true;
    vid.preload = 'metadata';
    vid.src = videoUrl;

    const captureNext = () => {
      if (idx >= seekTimes.length) {
        setClipThumbnails(prev => ({ ...prev, [clipId]: results }));
        return;
      }
      vid.currentTime = Math.max(0, Math.min(seekTimes[idx], vid.duration - 0.01));
    };

    vid.addEventListener('loadedmetadata', captureNext);
    vid.addEventListener('seeked', () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = TW; canvas.height = TH;
        const ctx = canvas.getContext('2d');
        if (ctx) { ctx.drawImage(vid, 0, 0, TW, TH); results.push(canvas.toDataURL('image/jpeg', 0.65)); }
        else results.push('');
      } catch { results.push(''); }
      idx++;
      captureNext();
    });
    vid.load();
  }, [clipThumbnails]);

  // ─── Snap helper ───────────────────────────────────────────────────────────

  const snapTime = useCallback((t: number, excludeId: string): number => {
    if (!snapEnabledRef.current) return t;
    const threshold = 5 / PXRef.current;
    const targets = [
      0,
      currentTimeRef.current,
      ...clipsRef.current
        .filter(c => c.id !== excludeId)  // exclure l'élément en cours de déplacement
        .flatMap(c => [c.trackStart, c.trackStart + (c.srcEnd - c.srcStart) / c.speed]),
      ...audioLayersRef.current
        .filter(l => l.id !== excludeId)
        .flatMap(l => [l.trackStart, l.trackStart + l.duration]),
      ...textsRef.current
        .filter(tx => tx.id !== excludeId)
        .flatMap(tx => [tx.trackStart, tx.trackStart + tx.duration]),
    ];
    for (const target of targets) {
      if (Math.abs(t - target) < threshold) return target;
    }
    return t;
  }, []);

  // ─── Clip operations ───────────────────────────────────────────────────────

  const splitAtPlayhead = () => {
    const t = currentTimeRef.current;

    // If selected element is an audio layer → split the audio layer
    const selAudio = audioLayersRef.current.find(l => l.id === selectedId);
    if (selAudio) {
      const end = selAudio.trackStart + selAudio.duration;
      if (t > selAudio.trackStart + 0.1 && t < end - 0.1) {
        const firstDur = t - selAudio.trackStart;
        const secondDur = end - t;
        const newLayers = audioLayersRef.current.flatMap(l =>
          l.id === selAudio.id
            ? [{ ...l, duration: firstDur }, { ...l, id: uid(), trackStart: t, duration: secondDur }]
            : [l]
        );
        setAudioLayers(newLayers);
        pushHistory(clipsRef.current, textsRef.current, newLayers);
      }
      return;
    }

    // Otherwise split video clips (selected clip only if one is selected, else all at playhead)
    const newClips: Clip[] = [];
    for (const c of clipsRef.current) {
      const end = clipTrackEnd(c);
      if (t > c.trackStart + 0.1 && t < end - 0.1 && (!selectedId || c.id === selectedId)) {
        const splitSrc = c.srcStart + (t - c.trackStart) * c.speed;
        newClips.push({ ...c, srcEnd: splitSrc });
        newClips.push({ ...c, id: uid(), srcStart: splitSrc, trackStart: t });
      } else {
        newClips.push(c);
      }
    }
    setClips(newClips);
    pushHistory(newClips, textsRef.current, audioLayersRef.current);
  };

  const deleteClip = (id: string) => {
    // Si en lecture sur ce clip, arrêter la lecture
    const clip = clipsRef.current.find(c => c.id === id);
    if (clip && playingRef.current &&
        currentTimeRef.current >= clip.trackStart && currentTimeRef.current < clipTrackEnd(clip)) {
      videoRef.current?.pause();
      setPlaying(false);
    }
    const newClips = clipsRef.current.filter(c => c.id !== id);
    setClips(newClips);
    if (selectedId === id) setSelectedId(null);
    pushHistory(newClips, textsRef.current, audioLayersRef.current);
  };

  const deleteText = (id: string) => {
    const newTexts = textsRef.current.filter(t => t.id !== id);
    setTexts(newTexts);
    if (selectedId === id) setSelectedId(null);
    pushHistory(clipsRef.current, newTexts, audioLayersRef.current);
  };

  const addText = () => {
    const t: TextOverlay = {
      id: uid(), content: 'Votre texte', trackStart: currentTime,
      duration: 4, x: 50, y: 85, fontSize: 48,
      color: '#ffffff', bold: true, shadow: true, font: 'bebas', bgColor: '#000000', bgOpacity: 0,
    };
    const newTexts = [...textsRef.current, t];
    setTexts(newTexts);
    setSelectedId(t.id);
    pushHistory(clipsRef.current, newTexts, audioLayersRef.current);
  };

  const updateClip = (id: string, patch: Partial<Clip>) => {
    setClips(cs => cs.map(c => c.id === id ? { ...c, ...patch } : c));
  };

  const updateText = (id: string, patch: Partial<TextOverlay>) => {
    setTexts(ts => ts.map(t => t.id === id ? { ...t, ...patch } : t));
  };

  // ─── Audio layer operations ─────────────────────────────────────────────────

  const addVideoSource = async (fileItem: FileItem) => {
    const key = uid();
    const clipId = uid();
    const audioLayerId = uid();
    const trackStart = totalDuration(clipsRef.current);

    // Insérer immédiatement avec une durée provisoire (30s) pour que le bloc apparaisse de suite
    setExtraSources(s => ({ ...s, [key]: { key, fileItem, type: 'video' } }));
    setClips(cs => [...cs, {
      id: clipId, srcStart: 0, srcEnd: 30, trackStart,
      filter: 'none', speed: 1, brightness: 100, contrast: 100,
      saturation: 100, reverse: false, sourceKey: key,
    }]);
    setAudioLayers(ls => [...ls, {
      id: audioLayerId, sourceKey: key, trackStart, duration: 30, volume: 100, track: 'main' as const,
    }]);

    // Corriger la durée réelle une fois sondée
    const dur = await getMediaDuration(fileItem.url, 'video');
    if (dur !== 30) {
      setClips(cs => cs.map(c => c.id === clipId ? { ...c, srcEnd: dur } : c));
      setAudioLayers(ls => ls.map(l => l.id === audioLayerId ? { ...l, duration: dur } : l));
    }
  };

  // Replace the main-video clips + __main__ audio layer with the lip-sync result.
  // The output IS the same video reanimated — same positions/trim, just a new source.
  const replaceLipSyncResult = async (fileItem: FileItem) => {
    const key = uid();
    const dur = await getMediaDuration(fileItem.url, 'video');
    setExtraSources(s => ({ ...s, [key]: { key, fileItem, type: 'video' as const } }));

    // Replace all clips that used the main video (no sourceKey) with the lip-synced source.
    // Keep their trim/position intact — the lip-sync output has the same duration.
    const newClips = clipsRef.current.map(c =>
      c.sourceKey === undefined
        ? { ...c, sourceKey: key, srcEnd: Math.min(c.srcEnd, dur) }
        : c
    );
    setClips(newClips);

    // Remove the __main__ AudioLayer and any audio layer already pointing at the same source,
    // then add a single audio layer covering the full lip-synced clip.
    const mainClipTrackStart = clipsRef.current.find(c => c.sourceKey === undefined)?.trackStart ?? 0;
    const withoutMain = audioLayersRef.current.filter(l => l.sourceKey !== '__main__');
    const newAudioLayer: AudioLayer = {
      id: uid(), sourceKey: key,
      trackStart: mainClipTrackStart, duration: dur,
      volume: 100, track: 'main' as const,
    };
    const newAudioLayers = [...withoutMain, newAudioLayer];
    setAudioLayers(newAudioLayers);
    setAudioVolume(0); // original embedded audio is gone — lip-sync result carries its own

    pushHistory(newClips, textsRef.current, newAudioLayers);
  };

  // Replace the target clip with the face-swap result.
  // Face swap preserves the original audio, so we only touch the video source — audio layers
  // and audioVolume are left intact.
  const replaceFaceSwapResult = async (fileItem: FileItem, targetVideoKey: string) => {
    const key = uid();
    const dur = await getMediaDuration(fileItem.url, 'video');
    setExtraSources(s => ({ ...s, [key]: { key, fileItem, type: 'video' as const } }));

    // Use functional updater so we always read the latest clips state, not a stale closure
    setClips(prevClips => {
      const newClips = prevClips.map(c => {
        const isTarget = targetVideoKey ? c.sourceKey === targetVideoKey : c.sourceKey === undefined;
        return isTarget ? { ...c, sourceKey: key, srcEnd: Math.min(c.srcEnd, dur) } : c;
      });
      // Push history inside the updater so it runs on the same snapshot
      setTimeout(() => pushHistory(newClips, textsRef.current, audioLayersRef.current), 0);
      return newClips;
    });
  };

  const insertAudioLayer = async (fileItem: FileItem, track: 'main' | 'audio' | 'music') => {
    const key = uid();
    const layerId = uid();
    const dur = await getMediaDuration(fileItem.url, 'audio');
    setExtraSources(s => ({ ...s, [key]: { key, fileItem, type: 'audio' } }));
    setAudioLayers(ls => {
      const end = totalDuration(clipsRef.current);
      return [...ls, { id: layerId, sourceKey: key, trackStart: end, duration: dur, volume: 100, track }];
    });
    setSelectedId(layerId);
  };

  const addAudioSource = (fileItem: FileItem) => {
    setTrackPicker({ cb: (t) => insertAudioLayer(fileItem, t) });
  };

  const addTextFile = (fileItem: FileItem) => {
    fetch(fileItem.url).then(r => r.text()).then(content => {
      const t: TextOverlay = {
        id: uid(), content: content.trim().slice(0, 200),
        trackStart: currentTimeRef.current, duration: 6,
        x: 50, y: 80, fontSize: 40, color: '#ffffff',
        bold: false, shadow: true, font: 'roboto', bgColor: '#000000', bgOpacity: 0,
      };
      setTexts(ts => [...ts, t]);
      setSelectedId(t.id);
    }).catch(() => {});
  };

  const deleteAudioLayer = (id: string) => {
    const layer = audioLayersRef.current.find(l => l.id === id);
    // Deleting the __main__ layer means the user wants to remove the video's embedded audio.
    // Suppress the clips' embedded audio too (audioVolume) so it doesn't reappear on render.
    if (layer?.sourceKey === '__main__') setAudioVolume(0);
    const newLayers = audioLayersRef.current.filter(l => l.id !== id);
    setAudioLayers(newLayers);
    if (selectedId === id) setSelectedId(null);
    pushHistory(clipsRef.current, textsRef.current, newLayers);
  };

  const deleteImage = (key: string) => {
    setExtraSources(prev => {
      const n = { ...prev };
      delete n[key];
      return n;
    });
    setAiSelectedImage(prev => (prev?.url === extraSources[key]?.fileItem.url ? null : prev));
    if (selectedId === key) setSelectedId(null);
  };

  const addVideoOverlay = async (fileItem: FileItem) => {
    const isVid = isVideoFile(fileItem.name);
    const dur = isVid ? await getMediaDuration(fileItem.url, 'video') : 5;
    const ov: VideoOverlay = {
      id: uid(), fileItem, isVideo: isVid,
      trackStart: currentTimeRef.current,
      duration: dur,
      x: 80, y: 15, width: 25, opacity: 100,
      borderWidth: 0, borderColor: '#ffffff',
    };
    setVideoOverlays(ovs => [...ovs, ov]);
    setSelectedId(ov.id);
  };

  const updateVideoOverlay = (id: string, patch: Partial<VideoOverlay>) => {
    setVideoOverlays(ovs => ovs.map(ov => ov.id === id ? { ...ov, ...patch } : ov));
  };

  const deleteVideoOverlay = (id: string) => {
    setVideoOverlays(ovs => ovs.filter(ov => ov.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const updateAudioLayer = (id: string, patch: Partial<AudioLayer>) => {
    setAudioLayers(ls => ls.map(l => l.id === id ? { ...l, ...patch } : l));
  };

  // ─── AI Studio handlers ─────────────────────────────────────────────────────

  const handleAIVideoGenerate = async () => {
    if (!aiPrompt.trim()) return;
    // Pour "Continuer", forcer un modèle LTX si selectedAiModel ne pointe pas vers un LTX
    const effectiveModelId = (aiPanelTask === 'continue-video')
      // continue-video → /v1/extend → nécessite ltx-2-pro uniquement
      ? (aiVideoModels.find(m => m.provider === 'LTX' && m.id === 'ltx-2-pro')?.id
         ?? aiVideoModels.find(m => m.provider === 'LTX')?.id
         ?? selectedAiModel)
      : selectedAiModel;
    const model = aiVideoModels.find(m => m.id === effectiveModelId);
    if (!model) { alert('Aucun modèle IA disponible.'); return; }
    setIsGeneratingAI(true);
    setAiProgress(null);
    try {
      const contextFiles: Array<{name: string; url: string}> = [];
      if (aiPanelTask === 'continue-video') {
        // Utiliser le clip sélectionné s'il a une source extra (face swap, IA, etc.), sinon le fichier principal
        const selectedClip = clips.find(c => c.id === selectedId);
        const activeExtraSource = selectedClip?.sourceKey ? extraSources[selectedClip.sourceKey] : null;
        const continueFile = activeExtraSource
          ? { name: activeExtraSource.fileItem.name, url: activeExtraSource.fileItem.url }
          : { name: file.name, url: file.url };
        contextFiles.push(continueFile);
      } else if (aiPanelTask === 'video-img-comfy' && aiSelectedImage) {
        contextFiles.push({ name: aiSelectedImage.name, url: aiSelectedImage.url });
      }
      const ltxOpts = (model.provider === 'LTX' && aiPanelTask === 'continue-video')
        ? { retakeMode: 'extend' }
        : undefined;
      const response: any = await generateAIMedia(
        aiPrompt, 'video', currentPath, contextFiles,
        model.id, model.provider,
        ltxOpts,
        (model.provider === 'ComfyUI' || model.provider === 'FalAI')
          ? { useI2V: aiPanelTask === 'video-img-comfy' }
          : undefined
      );
      if (response && (response.processingId || response.jobId)) {
        const jobId = response.processingId || response.jobId;
        let attempts = 0;
        while (attempts < 120) {
          attempts++;
          await new Promise(r => setTimeout(r, 5000));
          let statusData: any = { status: 'in_progress' };
          try { statusData = await checkAIMediaStatus(jobId); } catch { /* retry */ }
          if (statusData.progress !== undefined) setAiProgress(statusData.progress);
          if (statusData.status === 'completed' || statusData.status === 'done') {
            // Insérer automatiquement la vidéo générée dans la timeline
            if (statusData.url && statusData.filename) {
              const generatedFile: FileItem = {
                name: statusData.filename,
                url: statusData.url,
                size: 0,
                type: 'video/mp4',
                lastModified: Date.now(),
              } as any;
              await addVideoSource(generatedFile);
            } else {
              alert(`✅ Vidéo générée et sauvegardée dans votre espace cloud.`);
            }
            break;
          }
          if (statusData.status === 'failed' || statusData.status === 'error') {
            throw new Error(statusData.error || 'Génération échouée');
          }
        }
      } else if (response?.url && response?.filename) {
        const generatedFile: FileItem = {
          name: response.filename,
          url: response.url,
          size: 0,
          type: 'video/mp4',
          lastModified: Date.now(),
        } as any;
        await addVideoSource(generatedFile);
      } else {
        alert(`✅ Vidéo générée et sauvegardée dans votre espace cloud.`);
      }
    } catch (err: any) {
      alert(`Erreur génération IA : ${err.response?.data?.error || err.message}`);
    } finally {
      setIsGeneratingAI(false);
      setAiProgress(null);
    }
  };

  const handleTtsVideoAI = async () => {
    if (!ttsText.trim()) return;
    setIsRunningTts(true);
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token') || sessionStorage.getItem('token') || '';
      const { data } = await axios.post('/api/ai/tts-video', {
        videoUrl: file.url,
        text: ttsText,
        voice: ttsVoice,
        model: ttsModelQ,
        currentPath: currentPath || '/',
        audioOnly: true,
      }, { headers: { Authorization: `Bearer ${token}` }, timeout: 0 });

      if (data.url) {
        // Remove the __main__ AudioLayer so the original embedded video audio is suppressed
        const mainLayer = audioLayersRef.current.find(l => l.sourceKey === '__main__');
        const withoutMain = mainLayer
          ? audioLayersRef.current.filter(l => l.id !== mainLayer.id)
          : audioLayersRef.current;
        if (mainLayer) setAudioVolume(0);

        // Insert TTS audio as a MAIN-track AudioLayer starting at t=0
        const key = uid();
        const layerId = uid();
        const dur = await getMediaDuration(data.url, 'audio');
        const fakeFile: FileItem = { name: data.filename, url: data.url, size: 0, type: 'audio/mpeg', lastModified: Date.now() } as any;
        setExtraSources(s => ({ ...s, [key]: { key, fileItem: fakeFile, type: 'audio' as const } }));
        const newLayer: AudioLayer = { id: layerId, sourceKey: key, trackStart: 0, duration: dur, volume: 100, track: 'main' as const };
        const newLayers = [...withoutMain, newLayer];
        setAudioLayers(newLayers);
        pushHistory(clipsRef.current, textsRef.current, newLayers);
        setSelectedId(layerId);
      }
    } catch (err: any) {
      alert(`Erreur TTS : ${err.response?.data?.error || err.message}`);
    } finally {
      setIsRunningTts(false);
    }
  };

  const handleSubtitlesAI = async () => {
    setIsRunningSubtitles(true);
    setSubtitleSrt(null);
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token') || sessionStorage.getItem('token') || '';
      // If the original audio was deleted (audioVolume=0), transcribe from the first active
      // AudioLayer (e.g. TTS track) instead of the original video file.
      let transcriptionUrl: string = file.url;
      if (audioVolume === 0) {
        const nonMain = audioLayersRef.current.find(l => l.sourceKey !== '__main__');
        if (nonMain) {
          const src = extraSources[nonMain.sourceKey];
          if (src) transcriptionUrl = src.fileItem.url;
        }
      }
      const { data } = await axios.post('/api/ai/subtitles', {
        videoUrl: transcriptionUrl,
        language: subLanguage,
        hardcode: false,  // Always SRT — subtitles go to the text track
        currentPath: currentPath || '/',
      }, { headers: { Authorization: `Bearer ${token}` }, timeout: 0 });
      setSubtitleSrt(data.srt || null);
      if (data.srt) {
        const overlays = parseSrt(data.srt, subFontSize, subColor, subBold, subFont);
        if (overlays.length > 0) {
          // Replace previous subtitle overlays instead of stacking them
          const prevIds = subTextIds;
          const newTexts = [...textsRef.current.filter(t => !prevIds.includes(t.id)), ...overlays];
          setSubTextIds(overlays.map(o => o.id));
          setTexts(newTexts);
          pushHistory(clipsRef.current, newTexts, audioLayersRef.current);
        }
      } else {
        alert(`✅ Sous-titrage terminé : ${data.filename}`);
      }
    } catch (err: any) {
      alert(`Erreur sous-titrage : ${err.response?.data?.error || err.message}`);
    } finally {
      setIsRunningSubtitles(false);
    }
  };

  const handleLipSync = async () => {
    const audioSrc = extraSources[lipSyncAudioKey];
    if (!audioSrc) { alert('Sélectionnez une piste audio source.'); return; }
    setIsGeneratingAI(true);
    setAiProgress(null);
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token') || sessionStorage.getItem('token') || '';
      const { data } = await axios.post('/api/ai/lip-sync', {
        videoUrl: file.url,
        audioUrl: audioSrc.fileItem.url,
        currentPath: currentPath || '/',
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (data.jobId) {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('token') || sessionStorage.getItem('token') || '';
        let attempts = 0;
        while (attempts < 240) { // 20 min max (CPU lent)
          attempts++;
          await new Promise(r => setTimeout(r, 5000));
          let statusData: any = { status: 'in_progress' };
          try {
            const { data: sd } = await axios.get(`/api/ai/lip-sync/status/${data.jobId}`, { headers: { Authorization: `Bearer ${token}` } });
            statusData = sd;
          } catch { /* retry */ }
          if (statusData.progress !== undefined) setAiProgress(statusData.progress);
          if (statusData.status === 'completed' && statusData.url && statusData.filename) {
            const genFile: FileItem = { name: statusData.filename, url: statusData.url, size: 0, type: 'video/mp4', lastModified: Date.now() } as any;
            await replaceLipSyncResult(genFile);
            break;
          }
          if (statusData.status === 'failed') throw new Error(statusData.error || 'Lip sync échoué');
        }
      }
    } catch (err: any) {
      alert(`Erreur Lip Sync : ${err.response?.data?.error || err.message}`);
    } finally {
      setIsGeneratingAI(false);
      setAiProgress(null);
    }
  };

  const handleExtractFrame = async () => {
    const v = videoRef.current;
    if (!v) return;
    const name = extractFileName.trim().replace(/\.jpe?g$/i, '') || `frame-${Math.round(currentTime * 1000)}ms`;
    const filename = name + '.jpg';
    const canvas = document.createElement('canvas');
    canvas.width  = v.videoWidth  || 1280;
    canvas.height = v.videoHeight || 720;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(async (blob) => {
      if (!blob) { alert('Impossible de capturer la frame.'); return; }
      try {
        const f = new File([blob], filename, { type: 'image/jpeg' });
        await storageService.uploadFile(f, currentPath || '/', filename);
        alert(`✅ Image "${filename}" enregistrée dans ${currentPath || '/'}`);
        setShowExtractModal(false);
        setExtractFileName('');
      } catch (err: any) {
        alert(`Erreur : ${err.message}`);
      }
    }, 'image/jpeg', 0.92);
  };

  const handleFaceSwap = async () => {
    const srcImg = extraSources[faceSwapSourceKey];
    if (!srcImg) { alert('Sélectionnez une image source (visage à insérer).'); return; }
    // Vidéo cible : clip sélectionné ou fichier principal
    const targetVideo = faceSwapVideoKey
      ? extraSources[faceSwapVideoKey]?.fileItem
      : (file.url ? file : null);
    if (!targetVideo?.url) { alert('Sélectionnez une vidéo cible.'); return; }

    // Compute swap start time from the source image's trackStart position.
    // The image's timeline position tells us from when the face should be swapped.
    const imageTrackStart = (srcImg as any).trackStart ?? 0;
    let startTime = 0;
    if (imageTrackStart > 0.1) {
      // Find the clip covering that timeline position and convert to video-file offset
      const targetClip = clipsRef.current.find(c => {
        const srcUrl = c.sourceKey ? extraSources[c.sourceKey]?.fileItem.url : file.url;
        return srcUrl === targetVideo.url
          && imageTrackStart >= c.trackStart
          && imageTrackStart < clipTrackEnd(c);
      }) ?? clipsRef.current.find(c => !c.sourceKey); // fallback: first main clip
      if (targetClip) {
        startTime = targetClip.srcStart + (imageTrackStart - targetClip.trackStart) * (targetClip.speed || 1);
      }
    }

    setIsGeneratingAI(true);
    setAiProgress(null);
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token') || sessionStorage.getItem('token') || '';
      const { data } = await axios.post('/api/ai/face-swap', {
        videoUrl: targetVideo.url,
        sourceImageUrl: srcImg.fileItem.url,
        startTime,
        currentPath: currentPath || '/',
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (data.jobId) {
        let attempts = 0;
        while (attempts < 240) {
          attempts++;
          await new Promise(r => setTimeout(r, 5000));
          let s: any = { status: 'in_progress' };
          try {
            const { data: sd } = await axios.get(`/api/ai/face-swap/status/${data.jobId}`, { headers: { Authorization: `Bearer ${token}` } });
            s = sd;
          } catch { /* retry */ }
          if (s.progress !== undefined) setAiProgress(s.progress);
          if (s.status === 'completed' && s.url && s.filename) {
            await replaceFaceSwapResult({ name: s.filename, url: s.url, size: 0, type: 'video/mp4', lastModified: Date.now() } as any, faceSwapVideoKey);
            break;
          }
          if (s.status === 'failed') throw new Error(s.error || 'Face swap échoué');
        }
      }
    } catch (err: any) {
      alert(`Erreur Face Swap : ${err.response?.data?.error || err.message}`);
    } finally {
      setIsGeneratingAI(false);
      setAiProgress(null);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      recordStreamRef.current = stream;
      if (recordVideoRef.current) {
        recordVideoRef.current.srcObject = stream;
        recordVideoRef.current.play();
      }
      recordedChunksRef.current = [];
      const mr = new MediaRecorder(stream, { mimeType: 'video/webm;codecs=vp9,opus' });
      mr.ondataavailable = e => { if (e.data.size > 0) recordedChunksRef.current.push(e.data); };
      mr.start(1000);
      mediaRecorderRef.current = mr;
      setIsRecording(true);
    } catch (err: any) {
      alert(`Impossible d'accéder à la caméra : ${err.message}`);
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    recordStreamRef.current?.getTracks().forEach(t => t.stop());
    setIsRecording(false);
    if (recordVideoRef.current) recordVideoRef.current.srcObject = null;
    mediaRecorderRef.current!.onstop = async () => {
      const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
      const url = URL.createObjectURL(blob);
      const actualDur = await getMediaDuration(url, 'video');
      const key = `rec_${Date.now()}`;
      const fakeFile: FileItem = { name: `enregistrement_${Date.now()}.webm`, url, size: blob.size, type: 'video/webm', lastModified: Date.now() } as any;
      setExtraSources(prev => ({ ...prev, [key]: { key, fileItem: fakeFile, type: 'video' } }));
      setClips(cs => {
        const trackStart = cs.reduce((acc, c) => Math.max(acc, c.trackStart + (c.srcEnd - c.srcStart) / c.speed), 0);
        return [...cs, {
          id: `clip_${Date.now()}`,
          srcStart: 0, srcEnd: actualDur,
          trackStart,
          filter: 'none', speed: 1, brightness: 100, contrast: 100, saturation: 100, reverse: false,
          sourceKey: key,
        }];
      });
      setShowRecordModal(false);
    };
  };

  const startVoiceRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      voiceStreamRef.current = stream;
      voiceRecordedChunksRef.current = [];
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus' : 'audio/webm';
      const mr = new MediaRecorder(stream, { mimeType });
      mr.ondataavailable = e => { if (e.data.size > 0) voiceRecordedChunksRef.current.push(e.data); };
      mr.start(1000);
      voiceRecorderRef.current = mr;
      setIsVoiceRecording(true);
    } catch (err: any) {
      alert(`Impossible d'accéder au microphone : ${err.message}`);
    }
  };

  const stopVoiceRecording = () => {
    voiceRecorderRef.current?.stop();
    voiceStreamRef.current?.getTracks().forEach(t => t.stop());
    setIsVoiceRecording(false);
    if (voiceRecorderRef.current) {
      voiceRecorderRef.current.onstop = async () => {
        const blob = new Blob(voiceRecordedChunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        const actualDur = await getMediaDuration(url, 'audio');
        const key = `voice_${Date.now()}`;
        const track = voiceTrack;
        const fakeFile: FileItem = { name: `voix_${Date.now()}.webm`, url, size: blob.size, type: 'audio/webm', lastModified: Date.now() } as any;
        setExtraSources(prev => ({ ...prev, [key]: { key, fileItem: fakeFile, type: 'audio' } }));
        setAudioLayers(ls => {
          const endTime = ls.reduce((acc, l) => Math.max(acc, l.trackStart + l.duration), 0);
          return [...ls, { id: `audio_${Date.now()}`, sourceKey: key, trackStart: endTime, duration: actualDur, volume: 100, track }];
        });
        setShowVoiceRecordModal(false);
      };
    }
  };

  // ─── Export ────────────────────────────────────────────────────────────────

  const handleExport = async (filename: string) => {
    setError('');
    setProcessing(true);
    setSaveModal(false);
    try {
      setProcessMsg('Chargement de la source...');
      const srcResp = await fetch(file.url);
      if (!srcResp.ok) throw new Error('Impossible de charger la vidéo source');
      const srcBlob = await srcResp.blob();

      // Collecter les sources supplémentaires (vidéos, audios, images)
      const imageKeys = Object.values(extraSources)
        .filter(s => s.type === 'image')
        .map(s => s.key);
      const usedKeys = new Set([
        ...clips.filter(c => c.sourceKey).map(c => c.sourceKey!),
        ...audioLayers.map(l => l.sourceKey),
        ...imageKeys,
      ]);
      const sourceKeyToIndex: Record<string, number> = {};
      const extraBlobs: Array<{ fieldname: string; blob: Blob; name: string }> = [];
      let sIdx = 1;
      for (const key of Array.from(usedKeys)) {
        const src = extraSources[key];
        if (!src) {
          throw new Error(`Source manquante dans le montage (clé: ${key}). Rechargez l'éditeur.`);
        }
        setProcessMsg(`Chargement source ${sIdx} (${src.fileItem.name})...`);
        let blob: Blob;
        try {
          const r = await fetch(src.fileItem.url);
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          blob = await r.blob();
        } catch (fetchErr: any) {
          throw new Error(`Impossible de charger "${src.fileItem.name}" : ${fetchErr.message}. Les enregistrements en direct ne survivent pas au rechargement de la page.`);
        }
        extraBlobs.push({ fieldname: `source_${sIdx}`, blob, name: src.fileItem.name });
        sourceKeyToIndex[key] = sIdx;
        sIdx++;
      }

      // Collecter les sources d'incrustation (overlay PiP) — uploadées séparément
      const overlayIndexMap: Record<string, number> = {};
      for (const ov of videoOverlaysRef.current) {
        setProcessMsg(`Chargement incrustation (${ov.fileItem.name})...`);
        let blob: Blob;
        try {
          const r = await fetch(ov.fileItem.url);
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          blob = await r.blob();
        } catch (fetchErr: any) {
          throw new Error(`Impossible de charger l'incrustation "${ov.fileItem.name}" : ${fetchErr.message}`);
        }
        extraBlobs.push({ fieldname: `source_${sIdx}`, blob, name: ov.fileItem.name });
        overlayIndexMap[ov.id] = sIdx;
        sIdx++;
      }

      setProcessMsg('Envoi au serveur (FFmpeg)...');
      const edlClips = clips.map(c => ({ ...c, sourceIndex: c.sourceKey ? (sourceKeyToIndex[c.sourceKey] ?? 0) : 0, pitchShift: c.pitchShift }));
      const trackMasterVol = (t: AudioLayer['track']) =>
        t === 'main' ? audioMainVolume : t === 'audio' ? audioTrackVolume : musicTrackVolume;
      const edlAudioLayers = audioLayers.map(l => ({
        ...l,
        sourceIndex: sourceKeyToIndex[l.sourceKey] ?? 0,
        volume: Math.round(l.volume * trackMasterVol(l.track) / 100),
        fadeIn: l.fadeIn,
        fadeOut: l.fadeOut,
      }));

      // Clips images : frame statique positionnée sur la timeline
      const imageEDLClips = imageKeys
        .filter(key => extraSources[key])
        .map(key => {
          const s = extraSources[key];
          const dur = s.duration ?? 3;
          return {
            id: s.key,
            srcStart: 0, srcEnd: dur,
            trackStart: s.trackStart ?? 0,
            filter: 'none' as const, speed: 1 as SpeedVal,
            brightness: 100, contrast: 100, saturation: 100, reverse: false,
            isImage: true,
            imageDuration: dur,
            sourceIndex: sourceKeyToIndex[key] ?? 0,
            transition: s.transition,
          };
        });

      // Infos image pour le backend (ajout de -loop 1 -t dur avant chaque -i image)
      const imageSources = imageKeys
        .filter(key => extraSources[key])
        .map(key => ({
          sourceIndex: sourceKeyToIndex[key] ?? 0,
          duration: extraSources[key].duration ?? 3,
          isGif: (extraSources[key].fileItem.name ?? '').toLowerCase().endsWith('.gif'),
        }));

      // Overlay images : aussi besoin de -loop 1 -t dur dans le backend
      const overlayImageSources = videoOverlaysRef.current
        .filter(ov => !ov.isVideo)
        .map(ov => ({
          sourceIndex: overlayIndexMap[ov.id] ?? 0,
          duration: ov.duration,
        }));

      // Trier tous les clips par trackStart
      const allEdlClips = [...edlClips, ...imageEDLClips]
        .sort((a, b) => a.trackStart - b.trackStart);

      const fmt = saveFormat.format ?? 'mp4';
      // If a __main__ AudioLayer exists it is the sole source of the video's embedded audio.
      // Set clips' audioVolume to 0 to avoid mixing the same stream twice.
      const effectiveAudioVolume = audioLayers.some(l => l.sourceKey === '__main__') ? 0 : audioVolume;
      const edl = {
        clips: allEdlClips, texts: texts.map(t => ({ ...t, animIn: t.animIn, animOut: t.animOut })),
        audioLayers: edlAudioLayers, audioVolume: effectiveAudioVolume, format: fmt,
        resolution: saveFormat.resolution,
        customWidth: saveFormat.customWidth,
        customHeight: saveFormat.customHeight,
        quality: saveFormat.quality,
        fps: saveFormat.fps,
        imageSources,
        waveformOverlay: saveFormat.waveformOverlay,
        waveformColor: saveFormat.waveformColor,
        waveformHeight: saveFormat.waveformHeight,
        videoOverlays: videoOverlaysRef.current.length > 0 ? videoOverlaysRef.current.map(ov => ({
          sourceIndex: overlayIndexMap[ov.id] ?? 0,
          trackStart: ov.trackStart,
          duration: ov.duration,
          x: ov.x,
          y: ov.y,
          width: ov.width,
          opacity: ov.opacity,
          isVideo: ov.isVideo,
          borderWidth: ov.borderWidth,
          borderColor: ov.borderColor,
        })) : undefined,
        overlayImageSources: overlayImageSources.length > 0 ? overlayImageSources : undefined,
      };

      const fd = new FormData();
      fd.append('video', srcBlob, file.name);
      for (const eb of extraBlobs) fd.append(eb.fieldname, eb.blob, eb.name);
      fd.append('edl', JSON.stringify(edl));

      const token = localStorage.getItem('accessToken') || localStorage.getItem('token') || sessionStorage.getItem('token') || '';
      setRenderProgress({ percent: 0, message: 'Envoi au serveur…' });
      const resp = await fetch('/api/video/render', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ message: resp.statusText }));
        throw new Error(err.message || 'Erreur serveur');
      }

      // Stream SSE-style progress from response headers + body stream
      // Backend sends progress as multipart/mixed or chunked JSON lines before the final blob
      const contentType = resp.headers.get('content-type') ?? '';
      let blob: Blob;
      if (contentType.includes('text/event-stream') || contentType.includes('application/x-ndjson')) {
        const reader = resp.body!.getReader();
        const chunks: Uint8Array[] = [];
        const decoder = new TextDecoder();
        let done = false;
        let isBinary = false;
        const binaryChunks: Uint8Array[] = [];
        while (!done) {
          const { value, done: d } = await reader.read();
          done = d;
          if (!value) continue;
          if (!isBinary) {
            const text = decoder.decode(value, { stream: true });
            const lines = text.split('\n').filter(Boolean);
            for (const line of lines) {
              try {
                const json = JSON.parse(line.replace(/^data:\s*/, ''));
                if (typeof json.percent === 'number') {
                  setRenderProgress({ percent: json.percent, message: json.message ?? 'Rendu…' });
                  setProcessMsg(json.message ?? `Rendu ${json.percent}%…`);
                } else if (json.done) {
                  isBinary = true;
                }
              } catch { /* binary data starts */ isBinary = true; }
            }
            if (isBinary) binaryChunks.push(value);
          } else {
            binaryChunks.push(value);
          }
          chunks.push(value);
        }
        if (binaryChunks.length > 0) {
          blob = new Blob(binaryChunks.map(c => c.buffer as ArrayBuffer));
        } else {
          blob = new Blob(chunks.map(c => c.buffer as ArrayBuffer));
        }
      } else {
        // Standard response — simulate progress with indeterminate bar
        setRenderProgress({ percent: -1, message: 'Rendu en cours…' });
        blob = await resp.blob();
      }
      setRenderProgress(null);
      setProcessMsg('Finalisation...');
      // Ensure filename has the correct extension
      const exportFmt = saveFormat.format ?? 'mp4';
      const baseFilename = filename.replace(/\.(mp4|webm)$/i, '');
      const finalFilename = `${baseFilename}.${exportFmt}`;
      onSave(blob, finalFilename);
      setDisplayName(finalFilename);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setProcessing(false);
      setProcessMsg('');
    }
  };

  // ─── Selected item ─────────────────────────────────────────────────────────

  const selClip = clips.find(c => c.id === selectedId) ?? null;
  const selText = texts.find(t => t.id === selectedId) ?? null;
  const selAudioLayer = audioLayers.find(l => l.id === selectedId) ?? null;
  const selImage = (selectedId && extraSources[selectedId]?.type === 'image') ? extraSources[selectedId] : null;
  const selOverlay = videoOverlays.find(ov => ov.id === selectedId) ?? null;

  const activeClip = activeClipForSrc;
  const videoFilter = (() => {
    if (!activeClip) return '';
    const parts: string[] = [];
    if (activeClip.brightness !== 100) parts.push(`brightness(${activeClip.brightness / 100})`);
    if (activeClip.contrast !== 100) parts.push(`contrast(${activeClip.contrast / 100})`);
    if (activeClip.saturation !== 100) parts.push(`saturate(${activeClip.saturation / 100})`);
    if (activeClip.filter === 'grayscale') parts.push('grayscale(1)');
    else if (activeClip.filter === 'sepia') parts.push('sepia(1)');
    else if (activeClip.filter === 'blur') parts.push('blur(4px)');
    else if (activeClip.filter === 'invert') parts.push('invert(1)');
    // Color grading
    const cg = activeClip.colorGrading;
    if (cg) {
      if (cg.hue !== 0) parts.push(`hue-rotate(${cg.hue}deg)`);
      if (cg.temp !== 0) {
        // Temperature: warm = sepia-like tint, cold = blue-ish
        const warm = cg.temp > 0 ? cg.temp / 100 : 0;
        const cool = cg.temp < 0 ? -cg.temp / 100 : 0;
        if (warm > 0) parts.push(`sepia(${warm * 0.4})`);
        if (cool > 0) parts.push(`hue-rotate(${cool * 20}deg) saturate(${1 + cool * 0.3})`);
      }
      if (cg.lift !== 0 || cg.gamma !== 0 || cg.gain !== 0) {
        const brightness = 1 + (cg.gain / 200);
        const contrast = 1 + (cg.gamma / 200);
        const blackPoint = 1 + (cg.lift / 200);
        if (Math.abs(brightness - 1) > 0.01) parts.push(`brightness(${(brightness * blackPoint).toFixed(3)})`);
        if (Math.abs(contrast - 1) > 0.01) parts.push(`contrast(${contrast.toFixed(3)})`);
      }
    }
    return parts.join(' ');
  })();

  // ─── Render helpers ────────────────────────────────────────────────────────

  const renderRuler = () => {
    const imgMaxEnd = Object.values(extraSources).filter(s => s.type === 'image')
      .reduce((acc, s) => Math.max(acc, (s.trackStart ?? 0) + (s.duration ?? 3)), 0);
    const tot = Math.max(totalDuration(clips), duration, imgMaxEnd, 10);
    const w = tot * PX + 80;
    // Adaptive step based on pixel-per-second
    const step = PX >= 240 ? 0.5 : PX >= 120 ? 1 : PX >= 60 ? 2 : PX >= 30 ? 5 : PX >= 12 ? 10 : PX >= 6 ? 30 : 60;
    const majorStep = step * 5;
    const marks: JSX.Element[] = [];
    for (let s = 0; s <= tot + step; s = Math.round((s + step) * 1000) / 1000) {
      const isMajor = Math.abs(s % majorStep) < step * 0.01;
      marks.push(
        <div key={s} style={{ position: 'absolute', left: s * PX, top: isMajor ? 0 : 10, bottom: 0, borderLeft: `1px solid ${isMajor ? '#64748b' : '#334155'}`, paddingLeft: 3 }}>
          {isMajor && <span style={{ fontSize: 9, color: '#64748b', whiteSpace: 'nowrap' }}>{fmt(s)}</span>}
        </div>
      );
    }
    // Marker indicators on ruler
    markers.forEach(m => {
      marks.push(
        <div key={`mk_${m.id}`} title={m.label || fmt(m.time)}
          onClick={() => setCurrentTime(m.time)}
          style={{ position: 'absolute', left: m.time * PX - 5, top: 0, width: 10, height: 24, cursor: 'pointer', zIndex: 15, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '8px solid #f59e0b' }} />
          {m.label && PX > 30 && <span style={{ fontSize: 8, color: '#f59e0b', whiteSpace: 'nowrap', marginTop: 1, maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.label}</span>}
        </div>
      );
    });
    return (
      <div style={{ position: 'relative', height: 24, minWidth: w, background: '#0f172a', borderBottom: '1px solid #1e293b', userSelect: 'none' }}>
        {marks}
        <div style={{ position: 'absolute', left: currentTime * PX, top: 0, bottom: 0, width: 2, background: '#facc15', zIndex: 10, pointerEvents: 'none' }} />
      </div>
    );
  };

  const renderTrack = (label: string, height: number, children: React.ReactNode, bg = '#1e293b', dropAccept?: 'video' | 'audio' | 'image', dropTrack?: 'main' | 'audio' | 'music') => (
    <div style={{ display: 'flex', minHeight: height, borderBottom: '1px solid #0f172a' }}>
      <div style={{ width: 60, minWidth: 60, background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </div>
      <div style={{ flex: 1, position: 'relative', background: bg, overflow: 'visible' }}
        onMouseDown={onTimelineMouseDown}
        onDragOver={dropAccept ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; } : undefined}
        onDrop={dropAccept ? (e) => {
          e.preventDefault();
          const fileItem = (() => {
            try { return JSON.parse(e.dataTransfer.getData('application/x-flexihub-file')); } catch { return null; }
          })();
          if (!fileItem) return;
          const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
          const dropTime = Math.max(0, (e.clientX - rect.left) / PXRef.current);
          if (dropAccept === 'video') {
            addVideoSource({ ...fileItem }).then(() => {
              // position the last added clip at drop time
              setClips(prev => {
                const last = prev[prev.length - 1];
                if (!last) return prev;
                return prev.map(c => c.id === last.id ? { ...c, trackStart: dropTime } : c);
              });
            });
          } else if (dropAccept === 'audio') {
            insertAudioLayer(fileItem, dropTrack ?? 'main').then(() => {
              setAudioLayers(prev => {
                const last = prev[prev.length - 1];
                if (!last) return prev;
                return prev.map(l => l.id === last.id ? { ...l, trackStart: dropTime } : l);
              });
            });
          } else if (dropAccept === 'image') {
            const key = `img_${Date.now()}`;
            setExtraSources(prev => ({ ...prev, [key]: { key, fileItem, type: 'image', trackStart: dropTime, duration: 3 } }));
          }
        } : undefined}
      >
        {children}
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: currentTime * PX, width: 2, background: '#facc15', zIndex: 20, pointerEvents: 'none' }} />
      </div>
    </div>
  );

  const renderClipBlock = (c: Clip, idx: number) => {
    const w = Math.max(4, clipDuration(c) * PX);
    const left = c.trackStart * PX;
    const isSelected = selectedId === c.id || selectedIds.includes(c.id);
    const color = c.sourceKey ? '#7c3aed' : clipColor(idx);
    const hasTrans = !!c.transition;

    return (
      <React.Fragment key={c.id}>
        {hasTrans && (
          <div
            onClick={() => setSelectedId(c.id)}
            style={{
              position: 'absolute', left: left - 8, top: 6, width: 16, height: 32,
              background: '#facc15', borderRadius: 3, zIndex: 5, cursor: 'pointer', opacity: 0.9,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10,
            }}
            title={`Transition: ${TRANSITION_LABELS[c.transition!.type]} (${c.transition!.duration}s)`}
          >
            ⟷
          </div>
        )}
        <div
          style={{
            position: 'absolute', left, top: 4, width: w, height: 36,
            background: color, borderRadius: 4, cursor: 'grab', userSelect: 'none',
            border: isSelected ? '2px solid #facc15' : '2px solid transparent',
            overflow: 'hidden', display: 'flex', alignItems: 'center',
            boxShadow: isSelected ? '0 0 0 1px #facc15' : 'none', zIndex: isSelected ? 8 : 4,
            opacity: c.reverse ? 0.85 : 1,
          }}
          onMouseDown={e => {
            if (timelineRef.current) {
              const rect = timelineRef.current.getBoundingClientRect();
              const x = e.clientX - rect.left + timelineRef.current.scrollLeft - 60;
              setCurrentTime(Math.max(0, x / PXRef.current));
            }
            if (e.shiftKey) {
              setSelectedIds(prev => prev.includes(c.id) ? prev.filter(id => id !== c.id) : [...prev, c.id]);
            } else {
              setSelectedId(c.id);
              setSelectedIds([]);
            }
            startClipDrag(e, 'clip', c.id, c.trackStart);
          }}
        >
          {/* Filmstrip */}
          {(() => {
            const thumbs = clipThumbnails[c.id];
            if (!thumbs || thumbs.length === 0) return null;
            // Chaque miniature fait ~64px de large (80/45 * 36)
            const thumbPx = Math.round((80 / 45) * 36);
            const needed  = Math.min(60, Math.ceil(w / thumbPx) + 1);
            return (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', overflow: 'hidden', pointerEvents: 'none', opacity: 0.75 }}>
                {Array.from({ length: needed }, (_, i) => {
                  const src = thumbs[i % thumbs.length];
                  return src
                    ? <img key={i} src={src} alt="" draggable={false}
                        style={{ height: '100%', width: 'auto', flexShrink: 0, objectFit: 'cover', display: 'block' }} />
                    : null;
                })}
              </div>
            );
          })()}
          <span style={{ position: 'relative', zIndex: 1, fontSize: 10, color: '#fff', paddingLeft: 6, pointerEvents: 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, textShadow: '0 1px 3px rgba(0,0,0,0.9)' }}>
            {c.filter !== 'none' ? `[${FILTER_LABELS[c.filter]}] ` : ''}{c.reverse ? '◀ ' : ''}{c.speed !== 1 ? `${c.speed}× ` : ''}{fmt(c.srcStart)}–{fmt(c.srcEnd)}
          </span>
          <div
            style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 8, cursor: 'ew-resize', background: 'rgba(255,255,255,0.25)', zIndex: 2 }}
            onMouseDown={e => startClipDrag(e, 'trim-r', c.id, c.srcEnd)}
          />
          <div
            style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 8, cursor: 'ew-resize', background: 'rgba(255,255,255,0.25)', zIndex: 2 }}
            onMouseDown={e => startClipDrag(e, 'trim-l', c.id, c.srcStart, c.trackStart)}
          />
          {/* Indicateurs transitionIn / transitionOut */}
          {c.transitionIn && (
            <div style={{ position: 'absolute', left: 8, top: 2, fontSize: 8, color: '#fde68a', pointerEvents: 'none', lineHeight: 1 }}
              title={`Départ: ${TRANSITION_LABELS[c.transitionIn.type]} ${c.transitionIn.duration.toFixed(1)}s`}>▶F</div>
          )}
          {c.transitionOut && (
            <div style={{ position: 'absolute', right: 10, top: 2, fontSize: 8, color: '#fde68a', pointerEvents: 'none', lineHeight: 1 }}
              title={`Fin: ${TRANSITION_LABELS[c.transitionOut.type]} ${c.transitionOut.duration.toFixed(1)}s`}>F⏹</div>
          )}
        </div>
      </React.Fragment>
    );
  };

  const renderAudioBlock = (c: Clip, _idx: number) => {
    const w = Math.max(4, clipDuration(c) * PX);
    const muted = c.muteAudio;
    return (
      <div key={c.id} style={{
        position: 'absolute', left: c.trackStart * PX, top: 6, width: w, height: 24,
        background: muted ? '#374151' : (audioVolume === 0 ? '#374151' : '#059669'), borderRadius: 3,
        opacity: 0.8, pointerEvents: 'none',
      }}>
        <span style={{ fontSize: 9, color: '#fff', paddingLeft: 4 }}>
          {muted ? '🔇' : (audioVolume !== 100 ? `${audioVolume}%` : '♪')}
        </span>
      </div>
    );
  };

  const renderTextBlock = (t: TextOverlay) => {
    const isSelected = selectedId === t.id;
    const w = Math.max(4, t.duration * PX);
    return (
      <div key={t.id} style={{
        position: 'absolute', left: t.trackStart * PX, top: 4, width: w, height: 28,
        background: '#7c3aed', borderRadius: 4, cursor: 'grab', userSelect: 'none',
        border: isSelected ? '2px solid #facc15' : '2px solid transparent',
        display: 'flex', alignItems: 'center', overflow: 'hidden', zIndex: isSelected ? 8 : 4,
      }}
        onMouseDown={e => { setSelectedId(t.id); setCurrentTime(t.trackStart); startClipDrag(e, 'text', t.id, t.trackStart); }}
      >
        <span style={{ fontSize: 10, color: '#fff', paddingLeft: 6, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
          T: {t.content}
        </span>
        <div
          style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 8, cursor: 'ew-resize', background: 'rgba(255,255,255,0.3)' }}
          onMouseDown={e => startClipDrag(e, 'text-trim-r', t.id, t.duration)}
        />
        <div
          style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 8, cursor: 'ew-resize', background: 'rgba(255,255,255,0.3)' }}
          onMouseDown={e => startClipDrag(e, 'text-trim-l', t.id, t.trackStart, t.duration)}
        />
      </div>
    );
  };

  const renderAudioLayerBlock = (layer: AudioLayer, _idx: number) => {
    const isSelected = selectedId === layer.id;
    const w = Math.max(4, layer.duration * PX);
    const src = extraSources[layer.sourceKey];
    const blockColor = layer.track === 'main' ? '#15803d' : layer.track === 'audio' ? '#0e7490' : '#b45309';
    return (
      <div key={layer.id} style={{
        position: 'absolute', left: layer.trackStart * PX, top: 4, width: w, height: 28,
        background: blockColor, borderRadius: 4, cursor: 'grab', userSelect: 'none',
        border: isSelected ? '2px solid #facc15' : '2px solid transparent',
        display: 'flex', alignItems: 'center', overflow: 'hidden', zIndex: isSelected ? 8 : 4,
      }}
        onMouseDown={e => { setSelectedId(layer.id); startClipDrag(e, 'audio-layer', layer.id, layer.trackStart); }}
      >
        {/* Waveform background */}
        {waveformCache[layer.sourceKey] && (
          <img src={waveformCache[layer.sourceKey]} alt="" draggable={false}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'fill', opacity: 0.5, pointerEvents: 'none' }} />
        )}
        <span style={{ position: 'relative', fontSize: 10, color: '#fef3c7', paddingLeft: 6, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
          🎵 {src?.fileItem.name ?? layer.sourceKey}
        </span>
        <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 8, cursor: 'ew-resize', background: 'rgba(255,255,255,0.3)' }}
          onMouseDown={e => startClipDrag(e, 'audio-layer-trim-r', layer.id, layer.duration)} />
        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 8, cursor: 'ew-resize', background: 'rgba(255,255,255,0.3)' }}
          onMouseDown={e => startClipDrag(e, 'audio-layer-trim-l', layer.id, layer.trackStart, layer.duration)} />
      </div>
    );
  };

  // ─── Style constants ───────────────────────────────────────────────────────

  const S = {
    label:    { fontSize: 10, color: '#64748b', textTransform: 'uppercase' as const, letterSpacing: 0.5, marginBottom: 2 } as React.CSSProperties,
    srow:     { display: 'flex', alignItems: 'center', gap: 6 } as React.CSSProperties,
    val:      { fontSize: 11, color: '#94a3b8', width: 36, textAlign: 'right' as const },
    input:    { background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 4, padding: '4px 6px', fontSize: 12, width: '100%', resize: 'vertical' as const } as React.CSSProperties,
    title:    { fontSize: 12, fontWeight: 700, color: '#38bdf8', textTransform: 'uppercase' as const, letterSpacing: 0.5 } as React.CSSProperties,
    range:    { flex: 1, accentColor: '#6366f1' } as React.CSSProperties,
    danger:   { background: '#7f1d1d', color: '#fca5a5', border: 'none', borderRadius: 4, padding: '5px 10px', cursor: 'pointer', fontSize: 11, width: '100%' } as React.CSSProperties,
    btn:      { background: '#1e293b', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 4, padding: '5px 10px', cursor: 'pointer', fontSize: 11, width: '100%' } as React.CSSProperties,
  };

  const PBtn = ({ children, active, onClick }: { children: React.ReactNode; active?: boolean; onClick?: () => void }) => (
    <button onClick={onClick} style={{ fontSize: 10, padding: '3px 7px', borderRadius: 4, border: 'none', cursor: 'pointer', background: active ? '#6366f1' : '#334155', color: active ? '#fff' : '#94a3b8', fontWeight: active ? 700 : 400 }}>
      {children}
    </button>
  );

  // ─── Properties panel ──────────────────────────────────────────────────────

  const renderProperties = () => {
    if (selOverlay) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={S.title}>{selOverlay.isVideo ? '🎞 Incrustation vidéo' : '🖼 Incrustation image'}</div>
          <div style={{ fontSize: 11, color: '#94a3b8', wordBreak: 'break-all' }}>{selOverlay.fileItem.name}</div>
          <div>
            <div style={S.label}>Position X (centre)</div>
            <div style={S.srow}>
              <input type="range" min={0} max={100} value={selOverlay.x} onChange={e => updateVideoOverlay(selOverlay.id, { x: +e.target.value })} style={S.range} />
              <span style={S.val}>{selOverlay.x}%</span>
            </div>
          </div>
          <div>
            <div style={S.label}>Position Y (centre)</div>
            <div style={S.srow}>
              <input type="range" min={0} max={100} value={selOverlay.y} onChange={e => updateVideoOverlay(selOverlay.id, { y: +e.target.value })} style={S.range} />
              <span style={S.val}>{selOverlay.y}%</span>
            </div>
          </div>
          <div>
            <div style={S.label}>Largeur</div>
            <div style={S.srow}>
              <input type="range" min={5} max={100} value={selOverlay.width} onChange={e => updateVideoOverlay(selOverlay.id, { width: +e.target.value })} style={S.range} />
              <span style={S.val}>{selOverlay.width}%</span>
            </div>
          </div>
          <div>
            <div style={S.label}>Opacité</div>
            <div style={S.srow}>
              <input type="range" min={0} max={100} value={selOverlay.opacity} onChange={e => updateVideoOverlay(selOverlay.id, { opacity: +e.target.value })} style={S.range} />
              <span style={S.val}>{selOverlay.opacity}%</span>
            </div>
          </div>
          <div style={{ borderTop: '1px solid #334155', paddingTop: 8 }}>
            <div style={S.label}>Encadrement — épaisseur</div>
            <div style={S.srow}>
              <input type="range" min={0} max={20} value={selOverlay.borderWidth}
                onChange={e => updateVideoOverlay(selOverlay.id, { borderWidth: +e.target.value })} style={S.range} />
              <span style={S.val}>{selOverlay.borderWidth}px</span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={S.label}>Couleur encadrement</div>
            <input type="color" value={selOverlay.borderColor}
              onChange={e => updateVideoOverlay(selOverlay.id, { borderColor: e.target.value })}
              style={{ width: 36, height: 24, border: 'none', borderRadius: 4, cursor: 'pointer' }} />
            <span style={{ fontSize: 10, color: '#64748b' }}>{selOverlay.borderColor}</span>
          </div>
          <div style={{ borderTop: '1px solid #334155', paddingTop: 8 }}>
            <div style={S.label}>Position timeline (s)</div>
            <input type="number" min={0} step={0.1} value={selOverlay.trackStart.toFixed(1)}
              onChange={e => updateVideoOverlay(selOverlay.id, { trackStart: Math.max(0, +e.target.value) })}
              style={{ ...S.input, width: 80 }} />
          </div>
          <div>
            <div style={S.label}>Durée (s)</div>
            <input type="number" min={0.5} step={0.5} value={selOverlay.duration.toFixed(1)}
              onChange={e => updateVideoOverlay(selOverlay.id, { duration: Math.max(0.5, +e.target.value) })}
              style={{ ...S.input, width: 80 }} />
          </div>
          <button onClick={() => deleteVideoOverlay(selOverlay.id)} style={S.danger}>🗑 Supprimer l'incrustation</button>
        </div>
      );
    }
    if (selImage) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={S.title}>🖼 Image</div>
          <div style={{ fontSize: 11, color: '#94a3b8', wordBreak: 'break-all' }}>{selImage.fileItem.name}</div>
          <div>
            <div style={S.label}>Position (s)</div>
            <input type="number" min={0} step={0.1} value={(selImage.trackStart ?? 0).toFixed(1)}
              onChange={e => setExtraSources(prev => ({ ...prev, [selImage.key]: { ...prev[selImage.key], trackStart: Math.max(0, +e.target.value) } }))}
              style={{ ...S.input, width: 80 }} />
          </div>
          <div>
            <div style={S.label}>Durée (s)</div>
            <input type="number" min={0.5} step={0.5} value={(selImage.duration ?? 3).toFixed(1)}
              onChange={e => setExtraSources(prev => ({ ...prev, [selImage.key]: { ...prev[selImage.key], duration: Math.max(0.5, +e.target.value) } }))}
              style={{ ...S.input, width: 80 }} />
          </div>
          {/* Transition entrante */}
          <div>
            <div style={S.label}>Transition entrante</div>
            <select value={selImage.transition?.type ?? 'none'}
              onChange={e => setExtraSources(prev => ({ ...prev, [selImage.key]: { ...prev[selImage.key],
                transition: e.target.value === 'none' ? undefined : { type: e.target.value as TransitionType, duration: selImage.transition?.duration ?? 1 } } }))}
              style={{ ...S.input, marginBottom: 4 }}>
              <option value="none">Aucune</option>
              <option value="fade">Fondu (fade)</option>
              <option value="dissolve">Dissolution</option>
              <option value="slide_left">Glissement gauche</option>
              <option value="slide_right">Glissement droite</option>
              <option value="wipe_left">Balayage gauche</option>
              <option value="circleopen">Ouverture circulaire</option>
            </select>
            {selImage.transition && (
              <div style={S.srow}>
                <span style={{ fontSize: 10, color: '#64748b', minWidth: 50 }}>Durée</span>
                <input type="range" min={0.2} max={2} step={0.1} value={selImage.transition.duration}
                  onChange={e => setExtraSources(prev => ({ ...prev, [selImage.key]: { ...prev[selImage.key],
                    transition: { type: selImage.transition!.type, duration: +e.target.value } } }))}
                  style={S.range} />
                <span style={S.val}>{selImage.transition.duration.toFixed(1)}s</span>
              </div>
            )}
          </div>
          <button onClick={() => { setAiSelectedImage(selImage.fileItem); }}
            style={{ ...S.btn, background: aiSelectedImage?.url === selImage.fileItem.url ? '#92400e' : '#1e293b', color: '#fef3c7' }}>
            {aiSelectedImage?.url === selImage.fileItem.url ? '✓ Sélectionnée pour IA' : 'Sélectionner pour IA'}
          </button>
          <button onClick={() => deleteImage(selImage.key)} style={S.danger}>🗑 Supprimer l'image</button>
        </div>
      );
    }
    if (selText) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={S.title}>Texte / Sous-titre</div>
          {/* Live preview of the text with current style */}
          <div style={{
            background: '#000', borderRadius: 4, padding: '10px 8px', textAlign: 'center',
            minHeight: 48, display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid #334155', overflow: 'hidden',
          }}>
            <span style={{
              fontSize: Math.max(10, selText.fontSize * 0.22),
              color: selText.color,
              fontWeight: selText.bold ? 700 : 400,
              fontStyle: selText.italic ? 'italic' : 'normal',
              fontFamily: FONTS[selText.font]?.web ?? 'sans-serif',
              textShadow: selText.shadow ? '1px 1px 3px rgba(0,0,0,0.9)' : 'none',
              wordBreak: 'break-word',
            }}>{selText.content || 'Aperçu'}</span>
          </div>
          <textarea value={selText.content} rows={3} onChange={e => updateText(selText.id, { content: e.target.value })} style={S.input} />
          <div>
            <div style={S.label}>Position X</div>
            <div style={S.srow}>
              <input type="range" min={0} max={100} value={selText.x} onChange={e => updateText(selText.id, { x: +e.target.value })} style={S.range} />
              <span style={S.val}>{selText.x}%</span>
            </div>
          </div>
          <div>
            <div style={S.label}>Position Y</div>
            <div style={S.srow}>
              <input type="range" min={0} max={100} value={selText.y} onChange={e => updateText(selText.id, { y: +e.target.value })} style={S.range} />
              <span style={S.val}>{selText.y}%</span>
            </div>
          </div>
          <div>
            <div style={S.label}>Taille</div>
            <div style={S.srow}>
              <input type="range" min={16} max={300} value={selText.fontSize} onChange={e => updateText(selText.id, { fontSize: +e.target.value })} style={S.range} />
              <span style={S.val}>{selText.fontSize}px</span>
            </div>
          </div>
          {/* Police */}
          <div>
            <div style={S.label}>Police</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
              {FONT_CATEGORIES.map(cat => {
                const catFonts = Object.entries(FONTS).filter(([, f]) => f.category === cat);
                if (!catFonts.length) return null;
                return (
                  <div key={cat}>
                    <div style={{ fontSize: 9, color: '#475569', marginBottom: 2, textTransform: 'uppercase' as const }}>{cat}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 3 }}>
                      {catFonts.map(([key, f]) => (
                        <button key={key} onClick={() => updateText(selText.id, { font: key })}
                          style={{
                            fontSize: 11, padding: '3px 7px', borderRadius: 4, border: 'none', cursor: 'pointer',
                            background: selText.font === key ? '#6366f1' : '#334155',
                            color: selText.font === key ? '#fff' : '#94a3b8',
                            fontFamily: f.web,
                            fontWeight: selText.font === key ? 700 : 400,
                            whiteSpace: 'nowrap' as const,
                          }}>
                          {f.label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={S.label}>Couleur</div>
            <input type="color" value={selText.color} onChange={e => updateText(selText.id, { color: e.target.value })} style={{ width: 36, height: 24, border: 'none', borderRadius: 4, cursor: 'pointer' }} />
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <PBtn active={selText.bold} onClick={() => updateText(selText.id, { bold: !selText.bold })}>Gras</PBtn>
            <PBtn active={selText.shadow} onClick={() => updateText(selText.id, { shadow: !selText.shadow })}>Ombre</PBtn>
          </div>
          <div>
            <div style={S.label}>Fond</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <input type="color" value={selText.bgColor ?? '#000000'}
                onChange={e => updateText(selText.id, { bgColor: e.target.value, bgOpacity: selText.bgOpacity || 60 })}
                style={{ width: 36, height: 24, border: 'none', borderRadius: 4, cursor: 'pointer' }} />
              <span style={{ fontSize: 11, color: '#94a3b8' }}>Couleur</span>
            </div>
            <div style={S.srow}>
              <span style={{ fontSize: 10, color: '#64748b', width: 60 }}>Opacité</span>
              <input type="range" min={0} max={100} value={selText.bgOpacity ?? 0}
                onChange={e => updateText(selText.id, { bgOpacity: +e.target.value })} style={S.range} />
              <span style={S.val}>{selText.bgOpacity ?? 0}%</span>
            </div>
          </div>
          <div style={{ borderTop: '1px solid #334155', paddingTop: 8 }}>
            <div style={S.label}>Animation entrée</div>
            <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 4, marginTop: 4 }}>
              {(['none', 'fade', 'slideUp', 'zoomIn'] as const).map(a => (
                <PBtn key={a} active={(selText.animIn ?? 'none') === a} onClick={() => updateText(selText.id, { animIn: a })}>
                  {a === 'none' ? 'Aucune' : a === 'fade' ? 'Fondu' : a === 'slideUp' ? 'Glissement ↑' : 'Zoom'}
                </PBtn>
              ))}
            </div>
          </div>
          <div>
            <div style={S.label}>Animation sortie</div>
            <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 4, marginTop: 4 }}>
              {(['none', 'fade', 'slideDown', 'zoomOut'] as const).map(a => (
                <PBtn key={a} active={(selText.animOut ?? 'none') === a} onClick={() => updateText(selText.id, { animOut: a })}>
                  {a === 'none' ? 'Aucune' : a === 'fade' ? 'Fondu' : a === 'slideDown' ? 'Glissement ↓' : 'Zoom'}
                </PBtn>
              ))}
            </div>
          </div>
          <div style={{ borderTop: '1px solid #334155', paddingTop: 8 }}>
            <button onClick={() => deleteText(selText.id)} style={S.danger}>🗑 Supprimer</button>
          </div>
        </div>
      );
    }

    if (selAudioLayer) {
      const src = extraSources[selAudioLayer.sourceKey];
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={S.title}>{selAudioLayer.track === 'main' ? '🔊 Piste AUDIO' : selAudioLayer.track === 'audio' ? '🎤 Piste VOIX' : '🎵 Piste MUSIC'}</div>
          <div style={{ fontSize: 11, color: '#94a3b8', wordBreak: 'break-all' as const }}>{src?.fileItem.name}</div>
          {/* Edit in AudioEditor */}
          {src && (
            <button
              disabled={!!audioExtractMsg}
              onClick={async () => {
                const srcType = extraSources[selAudioLayer.sourceKey]?.type;
                if (srcType === 'video') {
                  setAudioExtractMsg('Extraction audio en cours…');
                  try {
                    const wavBlob = await extractAudioFromVideo(src.fileItem.url);
                    const wavUrl  = URL.createObjectURL(wavBlob);
                    const baseName = src.fileItem.name.replace(/\.[^.]+$/, '');
                    setAudioEditFile({ name: baseName + '_audio.wav', url: wavUrl });
                  } catch (e: any) {
                    setAudioExtractMsg(null);
                    alert('Extraction audio échouée : ' + e.message);
                    return;
                  }
                  setAudioExtractMsg(null);
                } else {
                  setAudioEditFile({ ...src.fileItem });
                }
                setAudioEditLayerId(selAudioLayer.id);
              }}
              style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
              {audioExtractMsg ?? '🎛 Éditer (Effets)'}
            </button>
          )}
          <div>
            <div style={S.label}>Piste</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <PBtn active={selAudioLayer.track === 'main'}  onClick={() => updateAudioLayer(selAudioLayer.id, { track: 'main'  })}>🔊 AUDIO</PBtn>
              <PBtn active={selAudioLayer.track === 'audio'} onClick={() => updateAudioLayer(selAudioLayer.id, { track: 'audio' })}>🎤 VOIX</PBtn>
              <PBtn active={selAudioLayer.track === 'music'} onClick={() => updateAudioLayer(selAudioLayer.id, { track: 'music' })}>🎵 MUSIC</PBtn>
            </div>
          </div>
          <div>
            <div style={S.label}>Volume</div>
            <div style={S.srow}>
              <input type="range" min={0} max={200} value={selAudioLayer.volume}
                onChange={e => updateAudioLayer(selAudioLayer.id, { volume: +e.target.value })} style={S.range} />
              <span style={S.val}>{selAudioLayer.volume}%</span>
            </div>
          </div>
          <div>
            <div style={S.label}>Durée (s)</div>
            <div style={S.srow}>
              <input type="range" min={1} max={300} value={selAudioLayer.duration}
                onChange={e => updateAudioLayer(selAudioLayer.id, { duration: +e.target.value })} style={S.range} />
              <span style={S.val}>{selAudioLayer.duration}s</span>
            </div>
          </div>
          <div>
            <div style={S.label}>Fondu entrant (s)</div>
            <div style={S.srow}>
              <input type="range" min={0} max={10} step={0.1} value={selAudioLayer.fadeIn ?? 0}
                onChange={e => setAudioLayers(ls => ls.map(l => l.id === selAudioLayer.id ? { ...l, fadeIn: +e.target.value } : l))}
                style={S.range} />
              <span style={S.val}>{(selAudioLayer.fadeIn ?? 0).toFixed(1)}s</span>
            </div>
          </div>
          <div>
            <div style={S.label}>Fondu sortant (s)</div>
            <div style={S.srow}>
              <input type="range" min={0} max={10} step={0.1} value={selAudioLayer.fadeOut ?? 0}
                onChange={e => setAudioLayers(ls => ls.map(l => l.id === selAudioLayer.id ? { ...l, fadeOut: +e.target.value } : l))}
                style={S.range} />
              <span style={S.val}>{(selAudioLayer.fadeOut ?? 0).toFixed(1)}s</span>
            </div>
          </div>
          <div style={{ borderTop: '1px solid #334155', paddingTop: 8 }}>
            <button onClick={() => deleteAudioLayer(selAudioLayer.id)} style={S.danger}>🗑 Supprimer</button>
          </div>
        </div>
      );
    }

    if (selClip) {
      const clipIdx = clips.indexOf(selClip);
      const hasNext = clipIdx < clips.length - 1;
      const hasPrev = clipIdx > 0;
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={S.title}>Clip — {fmt(selClip.srcStart)}→{fmt(selClip.srcEnd)}</div>
          {selClip.transition && (
            <div style={{ background: '#1a2a1a', border: '1px solid #facc15', borderRadius: 4, padding: '6px 8px' }}>
              <div style={{ ...S.label, color: '#facc15' }}>⟷ Transition entrante</div>
              <div style={{ fontSize: 11, color: '#fef08a', marginTop: 2 }}>
                {TRANSITION_LABELS[selClip.transition.type]} — {selClip.transition.duration.toFixed(1)}s
              </div>
              {hasPrev && (
                <button onClick={() => updateClip(selClip.id, { transition: undefined })}
                  style={{ marginTop: 4, fontSize: 10, padding: '2px 8px', background: '#7f1d1d', color: '#fca5a5', border: 'none', borderRadius: 3, cursor: 'pointer' }}>
                  Supprimer
                </button>
              )}
            </div>
          )}

          <div style={S.label}>Filtre</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {(Object.keys(FILTER_LABELS) as ClipFilter[]).map(f => (
              <PBtn key={f} active={selClip.filter === f} onClick={() => updateClip(selClip.id, { filter: f })}>{FILTER_LABELS[f]}</PBtn>
            ))}
          </div>

          <div>
            <div style={S.label}>Luminosité</div>
            <div style={S.srow}>
              <input type="range" min={0} max={200} value={selClip.brightness} onChange={e => updateClip(selClip.id, { brightness: +e.target.value })} style={S.range} />
              <span style={S.val}>{selClip.brightness}%</span>
            </div>
          </div>
          <div>
            <div style={S.label}>Contraste</div>
            <div style={S.srow}>
              <input type="range" min={0} max={200} value={selClip.contrast} onChange={e => updateClip(selClip.id, { contrast: +e.target.value })} style={S.range} />
              <span style={S.val}>{selClip.contrast}%</span>
            </div>
          </div>
          <div>
            <div style={S.label}>Saturation</div>
            <div style={S.srow}>
              <input type="range" min={0} max={300} value={selClip.saturation} onChange={e => updateClip(selClip.id, { saturation: +e.target.value })} style={S.range} />
              <span style={S.val}>{selClip.saturation}%</span>
            </div>
          </div>

          <div style={S.label}>Vitesse</div>
          <div style={{ display: 'flex', gap: 4 }}>
            {([0.25, 0.5, 1, 1.5, 2] as SpeedVal[]).map(s => (
              <PBtn key={s} active={selClip.speed === s} onClick={() => updateClip(selClip.id, { speed: s })}>{SPEED_LABELS[String(s)]}</PBtn>
            ))}
          </div>

          <div>
            <div style={S.label}>Hauteur tonale (demi-tons)</div>
            <div style={S.srow}>
              <input type="range" min={-12} max={12} step={1} value={selClip.pitchShift ?? 0}
                onChange={e => updateClip(selClip.id, { pitchShift: +e.target.value })} style={S.range} />
              <span style={S.val}>{(selClip.pitchShift ?? 0) > 0 ? '+' : ''}{selClip.pitchShift ?? 0}</span>
            </div>
            {(selClip.pitchShift ?? 0) !== 0 && <button onClick={() => updateClip(selClip.id, { pitchShift: 0 })} style={{ fontSize: 10, padding: '2px 8px', background: '#334155', color: '#94a3b8', border: 'none', borderRadius: 3, cursor: 'pointer', marginTop: 2 }}>Réinitialiser</button>}
          </div>

          <div style={{ display: 'flex', gap: 6 }}>
            <PBtn active={selClip.reverse} onClick={() => updateClip(selClip.id, { reverse: !selClip.reverse })}>◀ Inverser</PBtn>
            <PBtn active={!!selClip.muteAudio} onClick={() => updateClip(selClip.id, { muteAudio: !selClip.muteAudio })}>🔇 Couper son</PBtn>
          </div>

          {/* Transition de départ — fondu depuis noir */}
          <div style={{ borderTop: '1px solid #334155', paddingTop: 8 }}>
            <div style={S.label}>▶ Transition de départ</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
              <PBtn active={!selClip.transitionIn} onClick={() => updateClip(selClip.id, { transitionIn: undefined })}>Aucune</PBtn>
              {(Object.keys(TRANSITION_LABELS) as TransitionType[]).map(tt => (
                <PBtn key={tt} active={selClip.transitionIn?.type === tt}
                  onClick={() => updateClip(selClip.id, { transitionIn: { type: tt, duration: selClip.transitionIn?.duration ?? 1 } })}>
                  {TRANSITION_LABELS[tt]}
                </PBtn>
              ))}
            </div>
            {selClip.transitionIn && (
              <div style={S.srow}>
                <span style={{ fontSize: 10, color: '#64748b', minWidth: 50 }}>Durée</span>
                <input type="range" min={0.2} max={3} step={0.1} value={selClip.transitionIn.duration}
                  onChange={e => updateClip(selClip.id, { transitionIn: { ...selClip.transitionIn!, duration: +e.target.value } })}
                  style={S.range} />
                <span style={S.val}>{selClip.transitionIn.duration.toFixed(1)}s</span>
              </div>
            )}
          </div>

          {/* Transition de fin — fondu vers noir */}
          <div style={{ borderTop: '1px solid #334155', paddingTop: 8 }}>
            <div style={S.label}>⏹ Transition de fin</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
              <PBtn active={!selClip.transitionOut} onClick={() => updateClip(selClip.id, { transitionOut: undefined })}>Aucune</PBtn>
              {(Object.keys(TRANSITION_LABELS) as TransitionType[]).map(tt => (
                <PBtn key={tt} active={selClip.transitionOut?.type === tt}
                  onClick={() => updateClip(selClip.id, { transitionOut: { type: tt, duration: selClip.transitionOut?.duration ?? 1 } })}>
                  {TRANSITION_LABELS[tt]}
                </PBtn>
              ))}
            </div>
            {selClip.transitionOut && (
              <div style={S.srow}>
                <span style={{ fontSize: 10, color: '#64748b', minWidth: 50 }}>Durée</span>
                <input type="range" min={0.2} max={3} step={0.1} value={selClip.transitionOut.duration}
                  onChange={e => updateClip(selClip.id, { transitionOut: { ...selClip.transitionOut!, duration: +e.target.value } })}
                  style={S.range} />
                <span style={S.val}>{selClip.transitionOut.duration.toFixed(1)}s</span>
              </div>
            )}
          </div>

          {/* Transition crossfade vers clip suivant */}
          {hasNext && (
            <div style={{ borderTop: '1px solid #334155', paddingTop: 8 }}>
              <div style={S.label}>⇄ Crossfade vers clip suivant</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                <PBtn active={!clips[clips.indexOf(selClip) + 1]?.transition} onClick={() => {
                  const nextIdx = clips.indexOf(selClip) + 1;
                  if (nextIdx < clips.length) updateClip(clips[nextIdx].id, { transition: undefined });
                }}>Aucune</PBtn>
                {(Object.keys(TRANSITION_LABELS) as TransitionType[]).map(tt => {
                  const nextIdx = clips.indexOf(selClip) + 1;
                  const nextClip = clips[nextIdx];
                  return (
                    <PBtn key={tt} active={nextClip?.transition?.type === tt} onClick={() => {
                      if (nextClip) updateClip(nextClip.id, { transition: { type: tt, duration: nextClip.transition?.duration ?? 1 } });
                    }}>{TRANSITION_LABELS[tt]}</PBtn>
                  );
                })}
              </div>
              {(() => {
                const nextClip = clips[clips.indexOf(selClip) + 1];
                if (!nextClip?.transition) return null;
                return (
                  <div style={{ marginTop: 6 }}>
                    <div style={S.srow}>
                      <span style={{ fontSize: 10, color: '#64748b', minWidth: 50 }}>Durée</span>
                      <input type="range" min={0.2} max={3} step={0.1} value={nextClip.transition.duration}
                        onChange={e => updateClip(nextClip.id, { transition: { ...nextClip.transition!, duration: +e.target.value } })}
                        style={S.range} />
                      <span style={S.val}>{nextClip.transition.duration.toFixed(1)}s</span>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* ── Color grading ── */}
          <div style={{ borderTop: '1px solid #334155', paddingTop: 8 }}>
            <div style={S.title}>🎨 Color grading</div>
            {([
              { key: 'hue',   label: 'Teinte',        min: -180, max: 180, unit: '°' },
              { key: 'temp',  label: 'Température',   min: -100, max: 100, unit: '' },
              { key: 'lift',  label: 'Ombres (lift)',  min: -100, max: 100, unit: '' },
              { key: 'gamma', label: 'Milieux (gamma)',min: -100, max: 100, unit: '' },
              { key: 'gain',  label: 'Hautes lum.',   min: -100, max: 100, unit: '' },
            ] as const).map(({ key, label, min, max, unit }) => {
              const cg = selClip.colorGrading ?? DEFAULT_COLOR_GRADING;
              return (
                <div key={key} style={{ marginBottom: 6 }}>
                  <div style={S.label}>{label}</div>
                  <div style={S.srow}>
                    <input type="range" min={min} max={max} step={1} value={cg[key]}
                      onChange={e => updateClip(selClip.id, { colorGrading: { ...cg, [key]: +e.target.value } })}
                      style={S.range} />
                    <span style={S.val}>{cg[key]}{unit}</span>
                  </div>
                </div>
              );
            })}
            <button onClick={() => updateClip(selClip.id, { colorGrading: DEFAULT_COLOR_GRADING })}
              style={{ ...S.btn, fontSize: 10, padding: '3px 8px', width: 'auto' }}>↺ Reset</button>
          </div>

          <div style={{ borderTop: '1px solid #334155', paddingTop: 8 }}>
            <button onClick={() => deleteClip(selClip.id)} style={S.danger}>🗑 Supprimer le clip</button>
          </div>
        </div>
      );
    }

    return (
      <div style={{ color: '#475569', fontSize: 12, textAlign: 'center', paddingTop: 20 }}>
        Cliquez sur un clip<br />ou un texte<br />pour modifier ses propriétés
      </div>
    );
  };

  // ─── Interactive Panel ─────────────────────────────────────────────────────

  const renderInteractivePanel = () => {
    const ICONS: Record<InteractiveElementType, string> = {
      quiz: '❓', trueFalse: '✓✗', info: '💬', statement: '👁',
      link: '🔗', imagePop: '🖼', hotspot: '⊙', navigation: '⤵', panorama360: '🌐',
    };
    const LABELS: Record<InteractiveElementType, string> = {
      quiz: 'Quiz QCM', trueFalse: 'Vrai / Faux', info: 'Info-bulle',
      statement: 'Affirmation', link: 'Lien externe', imagePop: 'Image pop-up',
      hotspot: 'Zone cliquable', navigation: 'Navigation', panorama360: 'Panorama 360°',
    };
    const TYPES: InteractiveElementType[] = ['quiz', 'trueFalse', 'info', 'statement', 'link', 'imagePop', 'hotspot', 'navigation', 'panorama360'];

    const selEl = interactiveElements.find(e => e.id === selectedInteractiveId) ?? null;

    const addElement = (type: InteractiveElementType) => {
      const base: InteractiveElement = {
        id: uid(), type, time: currentTimeRef.current,
        pauseVideo: type !== 'info' && type !== 'hotspot' && type !== 'navigation',
      };
      const extras: Partial<InteractiveElement> =
        type === 'quiz' ? { question: '', choices: [{ id: uid(), text: '', correct: true }, { id: uid(), text: '', correct: false }], feedbackCorrect: 'Bravo !', feedbackWrong: 'Essayez encore.' } :
        type === 'trueFalse' ? { question: '', choices: [{ id: uid(), text: 'Vrai', correct: true }, { id: uid(), text: 'Faux', correct: false }], feedbackCorrect: 'Exact !', feedbackWrong: 'Pas tout à fait.' } :
        type === 'info' ? { text: '', autoClose: 5 } :
        type === 'statement' ? { text: '', pauseVideo: true } :
        type === 'link' ? { linkUrl: '', linkLabel: 'En savoir plus' } :
        type === 'imagePop' ? { imageUrl: '', imageName: '' } :
        type === 'hotspot' ? { hotspotX: 50, hotspotY: 50, hotspotW: 15, hotspotH: 8, hotspotDuration: 5, hotspotShape: 'rect', hotspotAction: 'text', hotspotValue: '' } :
        type === 'navigation'   ? { label: 'Continuer', jumpToTime: 0 } :
        type === 'panorama360'  ? { panoramaUrl: '', panoramaName: '', startYaw: 0, startPitch: 0, hotspots360: [], pauseVideo: true } : {};
      const el = { ...base, ...extras };
      setInteractiveElements(prev => [...prev, el].sort((a, b) => a.time - b.time));
      setSelectedInteractiveId(el.id);
      setShowAddInteractiveType(false);
    };

    const updateEl = (id: string, patch: Partial<InteractiveElement>) =>
      setInteractiveElements(prev => prev.map(e => e.id === id ? { ...e, ...patch } : e));

    const deleteEl = (id: string) => {
      setInteractiveElements(prev => prev.filter(e => e.id !== id));
      if (selectedInteractiveId === id) setSelectedInteractiveId(null);
    };

    const exportJson = () => {
      const data = JSON.stringify({ elements: interactiveElements, markers }, null, 2);
      const blob = new Blob([data], { type: 'application/json' });
      const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: `${file.name.replace(/\.[^.]+$/, '')}_interactive.json` });
      a.click(); URL.revokeObjectURL(a.href);
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, height: '100%', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={S.title}>🎯 Interactivité</span>
          <button
            onClick={() => setInteractivePreviewMode(p => !p)}
            style={{ fontSize: 10, padding: '3px 8px', borderRadius: 4, border: 'none', cursor: 'pointer',
              background: interactivePreviewMode ? '#0891b2' : '#334155',
              color: interactivePreviewMode ? '#fff' : '#94a3b8', fontWeight: interactivePreviewMode ? 700 : 400 }}>
            {interactivePreviewMode ? '⏹ Quitter aperçu' : '▶ Aperçu'}
          </button>
        </div>

        {interactivePreviewMode && (
          <div style={{ fontSize: 10, color: '#22d3ee', background: '#0c1f2b', border: '1px solid #0891b2', borderRadius: 4, padding: '4px 8px' }}>
            Mode aperçu actif — lancez la lecture
            {interactiveScore.total > 0 && (
              <span style={{ marginLeft: 6, color: '#86efac' }}>{interactiveScore.correct}/{interactiveScore.total} ✓</span>
            )}
          </div>
        )}

        {/* Add button */}
        <div style={{ position: 'relative' }}>
          <button onClick={() => setShowAddInteractiveType(p => !p)}
            style={{ ...S.btn, background: '#0c4a6e', color: '#7dd3fc', border: '1px solid #0369a1', width: '100%' }}>
            ➕ Ajouter à {fmt(currentTime)}
          </button>
          {showAddInteractiveType && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#1e293b', border: '1px solid #334155', borderRadius: 6, zIndex: 200, padding: 5, display: 'flex', flexDirection: 'column', gap: 2, boxShadow: '0 8px 24px rgba(0,0,0,0.6)' }}>
              {TYPES.map(t => (
                <button key={t} onClick={() => addElement(t)}
                  style={{ background: '#0f172a', border: 'none', color: '#e2e8f0', padding: '5px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 11, textAlign: 'left', display: 'flex', alignItems: 'center', gap: 6 }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#1e3a5f')}
                  onMouseLeave={e => (e.currentTarget.style.background = '#0f172a')}>
                  <span>{ICONS[t]}</span><span>{LABELS[t]}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 3, minHeight: 0 }}>
          {interactiveElements.length === 0 && (
            <div style={{ color: '#475569', fontSize: 11, textAlign: 'center', paddingTop: 12 }}>
              Aucun élément.<br />Cliquez "Ajouter" pour commencer.
            </div>
          )}
          {interactiveElements.map(el => {
            const isSel = el.id === selectedInteractiveId;
            return (
              <div key={el.id} onClick={() => { setSelectedInteractiveId(isSel ? null : el.id); setCurrentTime(el.time); }}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 7px', borderRadius: 5, cursor: 'pointer',
                  background: isSel ? '#0c2d3d' : '#0f172a', border: `1px solid ${isSel ? '#0891b2' : '#1e293b'}` }}>
                <span style={{ fontSize: 14, flexShrink: 0 }}>{ICONS[el.type]}</span>
                <div style={{ flex: 1, overflow: 'hidden' }}>
                  <div style={{ fontSize: 10, color: '#94a3b8', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{LABELS[el.type]}</div>
                  <div style={{ fontSize: 10, color: '#475569' }}>{fmt(el.time)}</div>
                </div>
                <button onClick={e => { e.stopPropagation(); deleteEl(el.id); }}
                  style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: '0 2px', flexShrink: 0 }}>✕</button>
              </div>
            );
          })}
        </div>

        {/* Edit form */}
        {selEl && (
          <div style={{ borderTop: '1px solid #1e293b', paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 7, overflowY: 'auto', maxHeight: '55%' }}>
            <div style={S.title}>{ICONS[selEl.type]} {LABELS[selEl.type]}</div>

            {/* Common: timestamp */}
            <div>
              <div style={S.label}>Timestamp (s)</div>
              <input type="number" min={0} step={0.1} value={selEl.time.toFixed(1)}
                onChange={e => updateEl(selEl.id, { time: Math.max(0, +e.target.value) })}
                style={{ ...S.input, width: 80 }} />
            </div>
            {selEl.type !== 'hotspot' && selEl.type !== 'navigation' && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#94a3b8', cursor: 'pointer' }}>
                <input type="checkbox" checked={selEl.pauseVideo} onChange={e => updateEl(selEl.id, { pauseVideo: e.target.checked })} />
                Pause auto
              </label>
            )}

            {/* Quiz / TrueFalse */}
            {(selEl.type === 'quiz' || selEl.type === 'trueFalse') && (<>
              <div>
                <div style={S.label}>Question</div>
                <textarea value={selEl.question ?? ''} onChange={e => updateEl(selEl.id, { question: e.target.value })}
                  rows={2} style={S.input} placeholder="Entrez la question…" />
              </div>
              {selEl.type === 'quiz' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <div style={S.label}>Choix (● = correct)</div>
                    <button onClick={() => updateEl(selEl.id, { choices: [...(selEl.choices ?? []), { id: uid(), text: '', correct: false }] })}
                      style={{ ...S.btn, fontSize: 10, padding: '1px 6px', width: 'auto' }}>+</button>
                  </div>
                  {(selEl.choices ?? []).map((c, ci) => (
                    <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
                      <input type="radio" name={`correct-${selEl.id}`} checked={c.correct}
                        onChange={() => updateEl(selEl.id, { choices: selEl.choices!.map((ch, i) => ({ ...ch, correct: i === ci })) })} />
                      <input value={c.text}
                        onChange={e => updateEl(selEl.id, { choices: selEl.choices!.map((ch, i) => i === ci ? { ...ch, text: e.target.value } : ch) })}
                        style={{ ...S.input, flex: 1, width: 'auto' }} placeholder={`Choix ${ci + 1}`} />
                      {(selEl.choices?.length ?? 0) > 2 && (
                        <button onClick={() => updateEl(selEl.id, { choices: selEl.choices!.filter((_, i) => i !== ci) })}
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 12 }}>✕</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {selEl.type === 'trueFalse' && (
                <div>
                  <div style={S.label}>Réponse correcte</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {(selEl.choices ?? []).map((c, ci) => (
                      <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#94a3b8', cursor: 'pointer' }}>
                        <input type="radio" name={`tf-${selEl.id}`} checked={c.correct}
                          onChange={() => updateEl(selEl.id, { choices: selEl.choices!.map((ch, i) => ({ ...ch, correct: i === ci })) })} />
                        {c.text}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <div style={S.label}>Feedback si correct</div>
                <input value={selEl.feedbackCorrect ?? ''} onChange={e => updateEl(selEl.id, { feedbackCorrect: e.target.value })}
                  style={S.input} placeholder="Bravo !" />
              </div>
              <div>
                <div style={S.label}>Feedback si incorrect</div>
                <input value={selEl.feedbackWrong ?? ''} onChange={e => updateEl(selEl.id, { feedbackWrong: e.target.value })}
                  style={S.input} placeholder="Essayez encore." />
              </div>
              <div>
                <div style={S.label}>Si incorrect → aller à (s, vide = continuer)</div>
                <input type="number" min={0} step={0.1} value={selEl.jumpToOnWrong ?? ''}
                  onChange={e => updateEl(selEl.id, { jumpToOnWrong: e.target.value ? +e.target.value : undefined })}
                  style={{ ...S.input, width: 80 }} placeholder="—" />
              </div>
            </>)}

            {/* Info / Statement */}
            {(selEl.type === 'info' || selEl.type === 'statement') && (<>
              <div>
                <div style={S.label}>Texte</div>
                <textarea value={selEl.text ?? ''} onChange={e => updateEl(selEl.id, { text: e.target.value })}
                  rows={3} style={S.input} placeholder="Entrez le contenu…" />
              </div>
              {selEl.type === 'info' && (
                <div>
                  <div style={S.label}>Fermeture auto (s, 0 = manuelle)</div>
                  <input type="number" min={0} max={30} value={selEl.autoClose ?? 0}
                    onChange={e => updateEl(selEl.id, { autoClose: +e.target.value })}
                    style={{ ...S.input, width: 70 }} />
                </div>
              )}
            </>)}

            {/* Link */}
            {selEl.type === 'link' && (<>
              <div>
                <div style={S.label}>URL</div>
                <input value={selEl.linkUrl ?? ''} onChange={e => updateEl(selEl.id, { linkUrl: e.target.value })}
                  style={S.input} placeholder="https://…" />
              </div>
              <div>
                <div style={S.label}>Label du bouton</div>
                <input value={selEl.linkLabel ?? ''} onChange={e => updateEl(selEl.id, { linkLabel: e.target.value })}
                  style={S.input} placeholder="En savoir plus" />
              </div>
            </>)}

            {/* ImagePop */}
            {selEl.type === 'imagePop' && (
              <div>
                <div style={S.label}>URL de l'image</div>
                <input value={selEl.imageUrl ?? ''} onChange={e => updateEl(selEl.id, { imageUrl: e.target.value })}
                  style={S.input} placeholder="/api/files/… ou https://…" />
              </div>
            )}

            {/* Hotspot */}
            {selEl.type === 'hotspot' && (<>
              <div>
                <div style={S.label}>Position X (%)</div>
                <div style={S.srow}>
                  <input type="range" min={0} max={100} value={selEl.hotspotX ?? 50} onChange={e => updateEl(selEl.id, { hotspotX: +e.target.value })} style={S.range} />
                  <span style={S.val}>{selEl.hotspotX ?? 50}%</span>
                </div>
              </div>
              <div>
                <div style={S.label}>Position Y (%)</div>
                <div style={S.srow}>
                  <input type="range" min={0} max={100} value={selEl.hotspotY ?? 50} onChange={e => updateEl(selEl.id, { hotspotY: +e.target.value })} style={S.range} />
                  <span style={S.val}>{selEl.hotspotY ?? 50}%</span>
                </div>
              </div>
              <div style={S.srow}>
                <div style={{ flex: 1 }}>
                  <div style={S.label}>Largeur (%)</div>
                  <div style={S.srow}>
                    <input type="range" min={5} max={60} value={selEl.hotspotW ?? 15} onChange={e => updateEl(selEl.id, { hotspotW: +e.target.value })} style={S.range} />
                    <span style={S.val}>{selEl.hotspotW ?? 15}%</span>
                  </div>
                </div>
              </div>
              <div style={S.srow}>
                <div style={{ flex: 1 }}>
                  <div style={S.label}>Hauteur (%)</div>
                  <div style={S.srow}>
                    <input type="range" min={3} max={40} value={selEl.hotspotH ?? 8} onChange={e => updateEl(selEl.id, { hotspotH: +e.target.value })} style={S.range} />
                    <span style={S.val}>{selEl.hotspotH ?? 8}%</span>
                  </div>
                </div>
              </div>
              <div>
                <div style={S.label}>Durée visible (s)</div>
                <input type="number" min={1} max={60} value={selEl.hotspotDuration ?? 5}
                  onChange={e => updateEl(selEl.id, { hotspotDuration: +e.target.value })}
                  style={{ ...S.input, width: 70 }} />
              </div>
              <div>
                <div style={S.label}>Forme</div>
                <div style={S.srow}>
                  <PBtn active={selEl.hotspotShape !== 'circle'} onClick={() => updateEl(selEl.id, { hotspotShape: 'rect' })}>Rect</PBtn>
                  <PBtn active={selEl.hotspotShape === 'circle'} onClick={() => updateEl(selEl.id, { hotspotShape: 'circle' })}>Cercle</PBtn>
                </div>
              </div>
              <div>
                <div style={S.label}>Action au clic</div>
                <div style={S.srow}>
                  <PBtn active={selEl.hotspotAction === 'text'} onClick={() => updateEl(selEl.id, { hotspotAction: 'text' })}>Texte</PBtn>
                  <PBtn active={selEl.hotspotAction === 'link'} onClick={() => updateEl(selEl.id, { hotspotAction: 'link' })}>Lien</PBtn>
                  <PBtn active={selEl.hotspotAction === 'jump'} onClick={() => updateEl(selEl.id, { hotspotAction: 'jump' })}>Saut</PBtn>
                </div>
              </div>
              <div>
                <div style={S.label}>{selEl.hotspotAction === 'link' ? 'URL' : selEl.hotspotAction === 'jump' ? 'Aller à (s)' : 'Texte info-bulle'}</div>
                <input value={selEl.hotspotValue ?? ''} onChange={e => updateEl(selEl.id, { hotspotValue: e.target.value })}
                  style={S.input} type={selEl.hotspotAction === 'jump' ? 'number' : 'text'} />
              </div>
            </>)}

            {/* Navigation */}
            {selEl.type === 'navigation' && (<>
              <div>
                <div style={S.label}>Label du bouton</div>
                <input value={selEl.label ?? ''} onChange={e => updateEl(selEl.id, { label: e.target.value })}
                  style={S.input} placeholder="Continuer" />
              </div>
              <div>
                <div style={S.label}>Aller à (s)</div>
                <input type="number" min={0} step={0.1} value={selEl.jumpToTime ?? 0}
                  onChange={e => updateEl(selEl.id, { jumpToTime: +e.target.value })}
                  style={{ ...S.input, width: 80 }} />
              </div>
            </>)}

            {/* Panorama 360° */}
            {selEl.type === 'panorama360' && (<>
              <div>
                <div style={S.label}>URL de l'image équirectangulaire</div>
                <input value={selEl.panoramaUrl ?? ''} onChange={e => updateEl(selEl.id, { panoramaUrl: e.target.value })}
                  style={S.input} placeholder="/api/files/… ou https://…" />
                <div style={{ fontSize: 10, color: '#475569', marginTop: 3 }}>JPEG/PNG équirectangulaire 2:1 (ex. 4096×2048)</div>
              </div>
              <div>
                <div style={S.label}>Angle de départ — Yaw (°)</div>
                <div style={S.srow}>
                  <input type="range" min={-180} max={180} value={selEl.startYaw ?? 0}
                    onChange={e => updateEl(selEl.id, { startYaw: +e.target.value })} style={S.range} />
                  <span style={S.val}>{selEl.startYaw ?? 0}°</span>
                </div>
              </div>
              <div>
                <div style={S.label}>Angle de départ — Pitch (°)</div>
                <div style={S.srow}>
                  <input type="range" min={-80} max={80} value={selEl.startPitch ?? 0}
                    onChange={e => updateEl(selEl.id, { startPitch: +e.target.value })} style={S.range} />
                  <span style={S.val}>{selEl.startPitch ?? 0}°</span>
                </div>
              </div>

              {/* Hotspots 360° */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <div style={S.label}>Points d'intérêt</div>
                  <button
                    onClick={() => updateEl(selEl.id, { hotspots360: [...(selEl.hotspots360 ?? []), { id: uid(), yaw: 0, pitch: 0, label: '', action: 'text', value: '' }] })}
                    style={{ ...S.btn, fontSize: 10, padding: '1px 6px', width: 'auto' }}>+ Ajouter</button>
                </div>
                {(selEl.hotspots360 ?? []).length === 0 && (
                  <div style={{ fontSize: 10, color: '#475569', textAlign: 'center', padding: '4px 0' }}>Aucun point. Cliquez "+ Ajouter".</div>
                )}
                {(selEl.hotspots360 ?? []).map((hs, hi) => (
                  <div key={hs.id} style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 6, padding: '7px 8px', marginBottom: 5, display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontSize: 10, color: '#475569', minWidth: 16 }}>#{hi + 1}</span>
                      <input value={hs.label}
                        onChange={e => updateEl(selEl.id, { hotspots360: selEl.hotspots360!.map((h, i) => i === hi ? { ...h, label: e.target.value } : h) })}
                        style={{ ...S.input, flex: 1 }} placeholder="Label" />
                      <button onClick={() => updateEl(selEl.id, { hotspots360: selEl.hotspots360!.filter((_, i) => i !== hi) })}
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 12 }}>✕</button>
                    </div>
                    <div style={S.srow}>
                      <span style={{ fontSize: 10, color: '#64748b', minWidth: 30 }}>Yaw</span>
                      <input type="range" min={-180} max={180} value={hs.yaw}
                        onChange={e => updateEl(selEl.id, { hotspots360: selEl.hotspots360!.map((h, i) => i === hi ? { ...h, yaw: +e.target.value } : h) })}
                        style={S.range} />
                      <span style={S.val}>{hs.yaw}°</span>
                    </div>
                    <div style={S.srow}>
                      <span style={{ fontSize: 10, color: '#64748b', minWidth: 30 }}>Pitch</span>
                      <input type="range" min={-80} max={80} value={hs.pitch}
                        onChange={e => updateEl(selEl.id, { hotspots360: selEl.hotspots360!.map((h, i) => i === hi ? { ...h, pitch: +e.target.value } : h) })}
                        style={S.range} />
                      <span style={S.val}>{hs.pitch}°</span>
                    </div>
                    <div style={S.srow}>
                      <span style={{ fontSize: 10, color: '#64748b', minWidth: 30 }}>Action</span>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {(['text', 'link', 'jump'] as const).map(a => (
                          <PBtn key={a} active={hs.action === a}
                            onClick={() => updateEl(selEl.id, { hotspots360: selEl.hotspots360!.map((h, i) => i === hi ? { ...h, action: a } : h) })}>
                            {a === 'text' ? 'Texte' : a === 'link' ? 'Lien' : 'Saut'}
                          </PBtn>
                        ))}
                      </div>
                    </div>
                    <input value={hs.value}
                      onChange={e => updateEl(selEl.id, { hotspots360: selEl.hotspots360!.map((h, i) => i === hi ? { ...h, value: e.target.value } : h) })}
                      style={S.input}
                      placeholder={hs.action === 'link' ? 'https://…' : hs.action === 'jump' ? 'Secondes (ex. 42)' : 'Texte à afficher'} />
                  </div>
                ))}
              </div>
            </>)}

            <button onClick={() => deleteEl(selEl.id)} style={S.danger}>🗑 Supprimer</button>
          </div>
        )}

        {/* Export */}
        <div style={{ borderTop: '1px solid #1e293b', paddingTop: 8 }}>
          <button onClick={exportJson}
            style={{ ...S.btn, background: '#14532d', color: '#86efac', border: '1px solid #166534' }}>
            ⬇ Exporter JSON
          </button>
        </div>
      </div>
    );
  };

  const imgMaxEnd = Object.values(extraSources).filter(s => s.type === 'image')
    .reduce((acc, s) => Math.max(acc, (s.trackStart ?? 0) + (s.duration ?? 3)), 0);
  const tot = Math.max(totalDuration(clips), duration, imgMaxEnd, 10);
  totalDurationRef.current = tot;

  // ─── Zone de transition courante (progress 0→1) ────────────────────────────
  const transInfo = (() => {
    for (let i = 1; i < clips.length; i++) {
      const prev = clips[i - 1];
      const curr = clips[i];
      if (!curr.transition) continue;
      const zoneStart = clipTrackEnd(prev) - curr.transition.duration;
      if (currentTime >= zoneStart && currentTime < clipTrackEnd(prev)) {
        const p = Math.max(0, Math.min(1, (currentTime - zoneStart) / curr.transition.duration));
        return { type: curr.transition.type, progress: p };
      }
    }
    return null;
  })();

  // Fondu depuis noir (transitionIn) — progress 0→1
  const fadeInProgress = (() => {
    for (const c of clips) {
      if (!c.transitionIn) continue;
      if (currentTime >= c.trackStart && currentTime < c.trackStart + c.transitionIn.duration)
        return (currentTime - c.trackStart) / c.transitionIn.duration;
    }
    return null;
  })();

  // Fondu vers noir (transitionOut) — progress 1→0
  const fadeOutProgress = (() => {
    for (const c of clips) {
      if (!c.transitionOut) continue;
      const fadeStart = clipTrackEnd(c) - c.transitionOut.duration;
      if (currentTime >= fadeStart && currentTime < clipTrackEnd(c))
        return (clipTrackEnd(c) - currentTime) / c.transitionOut.duration;
    }
    return null;
  })();

  // Styles CSS pour la transition (primary = clip sortant, secondary = clip entrant)
  const transPrimaryStyle: React.CSSProperties = (() => {
    let style: React.CSSProperties = {};
    if (transInfo) {
      const p = transInfo.progress;
      switch (transInfo.type) {
        case 'fade':      case 'dissolve':   style = { opacity: 1 - p }; break;
        case 'slide_left':                   style = { transform: `translateX(${-p * 100}%)` }; break;
        case 'slide_right':                  style = { transform: `translateX(${p * 100}%)` }; break;
        case 'wipe_left':                    style = { clipPath: `inset(0 ${p * 100}% 0 0)` }; break;
        case 'circleopen':                   style = { opacity: 1 - p }; break;
        default:                             style = { opacity: 1 - p }; break;
      }
    }
    // Appliquer fondu depuis/vers noir (opacité multiplicative avec le crossfade)
    let opacity = style.opacity !== undefined ? (style.opacity as number) : 1;
    if (fadeInProgress  !== null) opacity = Math.min(opacity, fadeInProgress);
    if (fadeOutProgress !== null) opacity = Math.min(opacity, fadeOutProgress);
    if (opacity < 1) style = { ...style, opacity };
    return style;
  })();

  const transSecondaryVisible = !!transInfo;
  const transSecondaryStyle: React.CSSProperties = (() => {
    const base: React.CSSProperties = {
      position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
      objectFit: 'contain' as const, background: '#000', zIndex: 1,
    };
    if (!transInfo) {
      // Garder le secondary visible pendant le chargement de la nouvelle source primaire
      // si la secondary a bien la source du clip qui arrive (préchargée pendant le crossfade).
      // Évite la frame noire entre la fin du crossfade et le début du clip suivant.
      if (primaryLoading && activeSrcUrl && transNextSrcRef.current === activeSrcUrl)
        return { ...base, opacity: 1 };
      return { ...base, visibility: 'hidden' as const };
    }
    const p = transInfo.progress;
    switch (transInfo.type) {
      case 'fade':      case 'dissolve':   return { ...base, opacity: p };
      case 'slide_left':                   return { ...base, transform: `translateX(${(1 - p) * 100}%)` };
      case 'slide_right':                  return { ...base, transform: `translateX(${-(1 - p) * 100}%)` };
      case 'wipe_left':                    return { ...base, clipPath: `inset(0 0 0 ${(1 - p) * 100}%)` };
      case 'circleopen':                   return { ...base, clipPath: `circle(${p * 75}% at center)` };
      default:                             return { ...base, opacity: p };
    }
  })();

  // ─── JSX ───────────────────────────────────────────────────────────────────

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 99999, background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="card shadow-lg" style={{ width: '96%', height: '96%', maxWidth: '1600px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Header */}
        <div className="card-header bg-white d-flex justify-content-between align-items-center py-2 px-3">
          <h5 className="mb-0 text-muted"><i className="fas fa-film me-2"></i>Éditeur vidéo — {displayName}</h5>
          <button onClick={onCancel} disabled={processing}
            style={{ background: 'none', border: 'none', fontSize: 22, cursor: processing ? 'not-allowed' : 'pointer', color: '#6c757d', lineHeight: 1, padding: '2px 6px', opacity: processing ? 0.4 : 1 }}>
            ✕
          </button>
        </div>

        {/* Main area: preview + properties */}
        <div style={{ display: 'flex', flex: '0 0 45%', overflow: 'hidden', background: '#0f172a' }}>

          {/* Preview */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '8px 8px 16px', position: 'relative' }}>
            {/* Wrapper pour empiler primary + secondary video + overlay image (transitions) */}
            <div style={{ position: 'relative', display: 'inline-block', maxWidth: '100%', maxHeight: 'calc(100% - 48px)', overflow: 'hidden', borderRadius: 4 }}>
              <video ref={videoRef}
                style={{ display: 'block', maxWidth: '100%', maxHeight: '100%', background: '#000', filter: videoFilter || undefined, ...transPrimaryStyle }}
                onClick={togglePlay}
              />
              {/* Second video — clip entrant pendant la transition */}
              <video ref={videoNextRef} muted style={transSecondaryStyle} onClick={togglePlay} />
              {/* Overlay image en preview — DOIT être dans ce wrapper pour ne pas masquer les contrôles */}
              {(() => {
                const activeImg = Object.values(extraSources)
                  .filter(s => s.type === 'image')
                  .find(s => currentTime >= (s.trackStart ?? 0) && currentTime < (s.trackStart ?? 0) + (s.duration ?? 3));
                if (!activeImg) return null;
                const imgStart = activeImg.trackStart ?? 0;
                const imgEnd   = imgStart + (activeImg.duration ?? 3);
                const trans    = activeImg.transition;
                let opacity    = 1;
                if (trans && trans.duration > 0) {
                  const t = currentTime;
                  if (t < imgStart + trans.duration) opacity = Math.min(1, (t - imgStart) / trans.duration);
                  else if (t > imgEnd - trans.duration) opacity = Math.max(0, (imgEnd - t) / trans.duration);
                }
                return (
                  <img src={activeImg.fileItem.url} alt="" onClick={togglePlay}
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%',
                      objectFit: 'contain', background: '#000', opacity, zIndex: 2,
                      transition: 'opacity 0.15s linear', cursor: 'pointer' }} />
                );
              })()}
            {/* Overlay texte en preview */}
            {texts.filter(t => {
              const inRange = currentTime >= t.trackStart && currentTime <= t.trackStart + t.duration;
              // Toujours afficher le texte sélectionné quand la vidéo est en pause (pour l'édition des propriétés)
              return inRange || (!playing && selectedId === t.id);
            }).map(t => {
              const inRange = currentTime >= t.trackStart && currentTime <= t.trackStart + t.duration;
              const animStyle = inRange ? getTextAnimStyle(t, currentTime) : { opacity: 0.85 };
              const anchorX = typeof t.anchorX === 'number' ? t.anchorX : 0.5;
              const anchorY = typeof t.anchorY === 'number' ? t.anchorY : 0.5;
              return (
                <div key={t.id} style={{
                  position: 'absolute',
                  left: `${t.x}%`, top: `${t.y}%`,
                  transform: `translate(${-anchorX * 100}%, ${-anchorY * 100}%)${animStyle.transform ? ' ' + animStyle.transform : ''}`,
                  fontSize: t.fontSize * 0.4, fontWeight: t.bold ? 700 : 400,
                  fontStyle: t.italic ? 'italic' : 'normal',
                  textDecoration: t.underline ? 'underline' : 'none',
                  fontFamily: FONTS[t.font]?.web ?? 'sans-serif',
                  color: t.color,
                  textShadow: t.shadow ? '2px 2px 4px rgba(0,0,0,0.8)' : 'none',
                  textAlign: t.textAlign ?? 'center',
                  background: t.bgOpacity ? `${t.bgColor ?? '#000000'}${Math.round((t.bgOpacity / 100) * 255).toString(16).padStart(2, '0')}` : 'transparent',
                  padding: t.bgOpacity ? '2px 6px' : undefined,
                  borderRadius: t.bgOpacity ? 3 : undefined,
                  pointerEvents: 'none', whiteSpace: 'pre-wrap',
                  opacity: animStyle.opacity,
                }}>{t.content}</div>
              );
            })}
            {/* Incrustations (PiP) en preview */}
            {videoOverlays.filter(ov => currentTime >= ov.trackStart && currentTime < ov.trackStart + ov.duration).map(ov => (
              <div
                key={ov.id}
                onClick={() => setSelectedId(ov.id)}
                style={{
                  position: 'absolute',
                  left: `${ov.x}%`,
                  top: `${ov.y}%`,
                  transform: 'translate(-50%, -50%)',
                  width: `${ov.width}%`,
                  opacity: ov.opacity / 100,
                  cursor: 'pointer',
                  border: ov.borderWidth > 0
                    ? `${ov.borderWidth}px solid ${ov.borderColor}`
                    : '1px solid rgba(255,255,255,0.18)',
                  outline: selectedId === ov.id ? '2px solid #facc15' : 'none',
                  outlineOffset: '1px',
                  boxSizing: 'border-box' as const,
                  overflow: 'hidden' as const,
                  zIndex: 3,
                }}
              >
                {ov.isVideo ? (
                  <video
                    src={ov.fileItem.url}
                    muted playsInline
                    ref={el => {
                      if (el) overlayVideoElsRef.current.set(ov.id, el);
                      else overlayVideoElsRef.current.delete(ov.id);
                    }}
                    style={{ width: '100%', height: 'auto', display: 'block', pointerEvents: 'none' }}
                  />
                ) : (
                  <img src={ov.fileItem.url} alt=""
                    style={{ width: '100%', height: 'auto', display: 'block', objectFit: 'contain', pointerEvents: 'none' }} />
                )}
              </div>
            ))}

            {/* ── Hotspots (zones cliquables) ──────────────────────────────── */}
            {interactivePreviewMode && interactiveElements
              .filter(el => el.type === 'hotspot' && currentTime >= el.time && currentTime < el.time + (el.hotspotDuration ?? 5))
              .map(el => {
                const isCircle = el.hotspotShape === 'circle';
                return (
                  <div key={el.id}
                    title={el.hotspotAction === 'text' ? (el.hotspotValue ?? '') : undefined}
                    onClick={() => {
                      if (el.hotspotAction === 'link' && el.hotspotValue) {
                        window.open(el.hotspotValue, '_blank', 'noopener');
                      } else if (el.hotspotAction === 'jump' && el.hotspotValue) {
                        const t = parseFloat(el.hotspotValue);
                        if (!isNaN(t)) { setCurrentTime(t); if (videoRef.current) videoRef.current.currentTime = t; }
                      } else if (el.hotspotAction === 'text' && el.hotspotValue) {
                        setHotspotPopup(p => p?.el.id === el.id ? null : { el, content: el.hotspotValue! });
                      }
                    }}
                    style={{
                      position: 'absolute',
                      left: `${(el.hotspotX ?? 50) - (el.hotspotW ?? 15) / 2}%`,
                      top: `${(el.hotspotY ?? 50) - (el.hotspotH ?? 8) / 2}%`,
                      width: `${el.hotspotW ?? 15}%`,
                      height: `${el.hotspotH ?? 8}%`,
                      borderRadius: isCircle ? '50%' : 6,
                      border: '2px solid rgba(34,211,238,0.8)',
                      background: 'rgba(8,145,178,0.18)',
                      cursor: 'pointer',
                      zIndex: 15,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      animation: 'hs-pulse 2s ease-in-out infinite',
                    }}>
                    <span style={{ fontSize: '0.55em', color: '#22d3ee', fontWeight: 700, pointerEvents: 'none', textShadow: '0 0 4px #000' }}>⊙</span>
                  </div>
                );
              })}

            {/* Hotspot text popup */}
            {hotspotPopup && (
              <div style={{ position: 'absolute', left: `${hotspotPopup.el.hotspotX ?? 50}%`, top: `${Math.min(85, (hotspotPopup.el.hotspotY ?? 50) + (hotspotPopup.el.hotspotH ?? 8))}%`,
                transform: 'translateX(-50%)', background: '#1e293b', border: '1px solid #334155', borderRadius: 6,
                padding: '6px 10px', fontSize: 11, color: '#e2e8f0', zIndex: 20, maxWidth: '70%',
                boxShadow: '0 4px 16px rgba(0,0,0,0.6)', pointerEvents: 'auto' }}>
                {hotspotPopup.content}
                <button onClick={() => setHotspotPopup(null)}
                  style={{ display: 'block', marginTop: 4, background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 11 }}>
                  Fermer
                </button>
              </div>
            )}

            {/* ── Interactive element overlay (quiz, info, etc.) ────────────── */}
            {interactivePreviewMode && activeInteractive && (() => {
              const el = activeInteractive;
              const dismiss = (resumePlay = true) => {
                dismissedInteractiveRef.current.add(el.id);
                setActiveInteractive(null);
                setHotspotPopup(null);
                if (resumePlay && el.pauseVideo) {
                  setPlaying(true);
                  videoRef.current?.play().catch(() => {});
                }
              };
              const handleAnswer = (choiceId: string) => {
                if (interactiveAnswerState.showFeedback) return;
                const isCorrect = el.choices?.find(c => c.id === choiceId)?.correct ?? false;
                setInteractiveAnswerState({ selectedChoiceId: choiceId, showFeedback: true, isCorrect });
                setInteractiveScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), total: s.total + 1 }));
              };
              const handleContinueAfterAnswer = () => {
                if (!interactiveAnswerState.isCorrect && el.jumpToOnWrong != null) {
                  const t = el.jumpToOnWrong;
                  setCurrentTime(t); if (videoRef.current) videoRef.current.currentTime = t;
                  dismissedInteractiveRef.current.delete(el.id); // allow re-trigger
                }
                dismiss(true);
              };

              const cardStyle: React.CSSProperties = {
                position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 25, background: 'rgba(0,0,0,0.6)', pointerEvents: 'auto',
              };
              const boxStyle: React.CSSProperties = {
                background: '#1e293b', borderRadius: 10, padding: 18, maxWidth: '88%', width: 270,
                boxShadow: '0 10px 40px rgba(0,0,0,0.7)', border: '1px solid #334155',
                display: 'flex', flexDirection: 'column', gap: 10,
              };
              const btnPrimary: React.CSSProperties = {
                background: '#0891b2', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 14px',
                cursor: 'pointer', fontSize: 12, fontWeight: 700, width: '100%',
              };

              if (el.type === 'quiz' || el.type === 'trueFalse') {
                return (
                  <div style={cardStyle}>
                    <div style={boxStyle}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#38bdf8' }}>{el.type === 'trueFalse' ? '✓✗ Vrai ou Faux ?' : '❓ Question'}</div>
                      <div style={{ fontSize: 12, color: '#e2e8f0', lineHeight: 1.5 }}>{el.question}</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {(el.choices ?? []).map(c => {
                          const isSel = interactiveAnswerState.selectedChoiceId === c.id;
                          const showResult = interactiveAnswerState.showFeedback;
                          const bg = showResult
                            ? c.correct ? '#14532d' : isSel ? '#7f1d1d' : '#0f172a'
                            : isSel ? '#1e3a5f' : '#0f172a';
                          const border = showResult
                            ? c.correct ? '1px solid #22c55e' : isSel ? '1px solid #ef4444' : '1px solid #1e293b'
                            : isSel ? '1px solid #38bdf8' : '1px solid #334155';
                          return (
                            <button key={c.id} onClick={() => handleAnswer(c.id)}
                              style={{ background: bg, border, borderRadius: 6, padding: '7px 10px', cursor: showResult ? 'default' : 'pointer', fontSize: 11, color: '#e2e8f0', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 6 }}>
                              {showResult && c.correct && <span style={{ color: '#22c55e' }}>✓</span>}
                              {showResult && isSel && !c.correct && <span style={{ color: '#ef4444' }}>✗</span>}
                              {c.text}
                            </button>
                          );
                        })}
                      </div>
                      {interactiveAnswerState.showFeedback && (
                        <div style={{ fontSize: 11, padding: '6px 10px', borderRadius: 5, textAlign: 'center', fontWeight: 700,
                          background: interactiveAnswerState.isCorrect ? '#14532d' : '#7f1d1d',
                          color: interactiveAnswerState.isCorrect ? '#86efac' : '#fca5a5' }}>
                          {interactiveAnswerState.isCorrect ? (el.feedbackCorrect || 'Bravo !') : (el.feedbackWrong || 'Essayez encore.')}
                        </div>
                      )}
                      {interactiveAnswerState.showFeedback && (
                        <button onClick={handleContinueAfterAnswer} style={btnPrimary}>Continuer →</button>
                      )}
                    </div>
                  </div>
                );
              }

              if (el.type === 'info') {
                return (
                  <div style={{ position: 'absolute', bottom: '14%', left: '50%', transform: 'translateX(-50%)', zIndex: 25, pointerEvents: 'auto', width: '80%', maxWidth: 260 }}>
                    <div style={{ background: '#0c2d3d', border: '1px solid #0891b2', borderRadius: 8, padding: '10px 14px', display: 'flex', gap: 8, alignItems: 'flex-start', boxShadow: '0 4px 16px rgba(0,0,0,0.5)' }}>
                      <span style={{ fontSize: 16 }}>💬</span>
                      <div style={{ flex: 1, fontSize: 11, color: '#bae6fd', lineHeight: 1.5 }}>{el.text}</div>
                      <button onClick={() => dismiss(false)} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 14, lineHeight: 1, flexShrink: 0 }}>✕</button>
                    </div>
                  </div>
                );
              }

              if (el.type === 'statement') {
                return (
                  <div style={cardStyle}>
                    <div style={boxStyle}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#a78bfa' }}>👁 Affirmation</div>
                      <div style={{ fontSize: 12, color: '#e2e8f0', lineHeight: 1.5 }}>{el.text}</div>
                      <button onClick={() => dismiss(true)} style={{ ...btnPrimary, background: '#6d28d9' }}>J'ai compris ✓</button>
                    </div>
                  </div>
                );
              }

              if (el.type === 'link') {
                return (
                  <div style={cardStyle}>
                    <div style={boxStyle}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#fbbf24' }}>🔗 Lien externe</div>
                      <a href={el.linkUrl} target="_blank" rel="noopener noreferrer"
                        style={{ ...btnPrimary, background: '#92400e', textAlign: 'center', textDecoration: 'none', display: 'block' }}>
                        {el.linkLabel || 'En savoir plus'} ↗
                      </a>
                      <button onClick={() => dismiss(true)} style={{ ...btnPrimary, background: '#334155' }}>Continuer →</button>
                    </div>
                  </div>
                );
              }

              if (el.type === 'imagePop') {
                return (
                  <div style={cardStyle} onClick={() => dismiss(true)}>
                    <div style={{ position: 'relative', maxWidth: '90%', maxHeight: '90%' }} onClick={e => e.stopPropagation()}>
                      <img src={el.imageUrl} alt={el.imageName ?? ''} style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 8, boxShadow: '0 10px 40px rgba(0,0,0,0.8)' }} />
                      <button onClick={() => dismiss(true)}
                        style={{ position: 'absolute', top: -10, right: -10, background: '#1e293b', border: '1px solid #334155', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer', color: '#e2e8f0', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                    </div>
                  </div>
                );
              }

              if (el.type === 'navigation') {
                return (
                  <div style={{ position: 'absolute', bottom: '10%', right: '6%', zIndex: 25, pointerEvents: 'auto' }}>
                    <button onClick={() => {
                      if (el.jumpToTime != null) { setCurrentTime(el.jumpToTime); if (videoRef.current) videoRef.current.currentTime = el.jumpToTime; }
                      dismiss(true);
                    }} style={{ background: '#0891b2', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 700, boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}>
                      {el.label || 'Continuer'} ⤵
                    </button>
                  </div>
                );
              }

              if (el.type === 'panorama360') {
                return (
                  <Panorama360Viewer
                    element={el}
                    onDismiss={() => dismiss(true)}
                    onJump={t => { setCurrentTime(t); if (videoRef.current) videoRef.current.currentTime = t; }}
                  />
                );
              }

              return null;
            })()}

            </div>
            {/* ── Sélecteur de format en mode création (aucun clip encore) ── */}
            {isCreationMode && clips.length === 0 && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#080d17', borderRadius: 4, gap: 16, padding: 24 }}>
                <div style={{ fontSize: 13, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Format de la vidéo</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center' }}>
                  {([
                    { v: '1080p',     label: '16:9 HD',   sub: '1920 × 1080', w: 48, h: 27 },
                    { v: '720p',      label: '16:9 720p',  sub: '1280 × 720',  w: 48, h: 27 },
                    { v: '4k',        label: '4K UHD',     sub: '3840 × 2160', w: 48, h: 27 },
                    { v: '1080x1080', label: '1:1 Carré',  sub: '1080 × 1080', w: 36, h: 36 },
                    { v: '1080x1920', label: '9:16 Vertical', sub: '1080 × 1920', w: 27, h: 48 },
                    { v: 'custom',    label: 'Personnalisé', sub: `${saveFormat.customWidth} × ${saveFormat.customHeight}`, w: 40, h: 30 },
                  ] as const).map(opt => {
                    const sel = saveFormat.resolution === opt.v;
                    return (
                      <button key={opt.v} onClick={() => setSaveFormat(f => ({ ...f, resolution: opt.v }))}
                        style={{ background: sel ? '#1e3a5f' : '#0f172a', border: `2px solid ${sel ? '#3b82f6' : '#1e293b'}`, borderRadius: 8, padding: '10px 12px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, minWidth: 90, transition: 'border-color 0.15s' }}>
                        {/* Aperçu proportionnel */}
                        <div style={{ width: opt.w, height: opt.h, background: sel ? '#3b82f6' : '#1e293b', borderRadius: 3, border: `1px solid ${sel ? '#60a5fa' : '#334155'}` }} />
                        <div style={{ fontSize: 11, color: sel ? '#e2e8f0' : '#94a3b8', fontWeight: sel ? 700 : 400 }}>{opt.label}</div>
                        <div style={{ fontSize: 10, color: '#475569' }}>{opt.sub}</div>
                      </button>
                    );
                  })}
                </div>
                {saveFormat.resolution === 'custom' && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="number" min={1} max={7680} value={saveFormat.customWidth}
                      onChange={e => setSaveFormat(f => ({ ...f, customWidth: Math.max(1, +e.target.value) }))}
                      style={{ width: 80, background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 4, padding: '4px 6px', fontSize: 12, textAlign: 'center' }} />
                    <span style={{ color: '#475569', fontSize: 13 }}>×</span>
                    <input type="number" min={1} max={4320} value={saveFormat.customHeight}
                      onChange={e => setSaveFormat(f => ({ ...f, customHeight: Math.max(1, +e.target.value) }))}
                      style={{ width: 80, background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 4, padding: '4px 6px', fontSize: 12, textAlign: 'center' }} />
                    <span style={{ fontSize: 11, color: '#64748b' }}>px</span>
                  </div>
                )}
                <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>Ajoutez un clip ou lancez une génération IA pour commencer</div>
              </div>
            )}

            {/* Contrôles */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
              <button onClick={() => { setCurrentTime(0); if (videoRef.current) videoRef.current.currentTime = 0; }} style={ctrlBtn}>⏮</button>
              <button onClick={() => setCurrentTime(t => Math.max(0, t - 5))} style={ctrlBtn}>⏪</button>
              <button onClick={togglePlay} style={{ ...ctrlBtn, fontSize: 18, padding: '4px 14px' }}>{playing ? '⏸' : '▶'}</button>
              <button onClick={() => setCurrentTime(t => Math.min(tot, t + 5))} style={ctrlBtn}>⏩</button>
              <button onClick={() => setCurrentTime(tot)} style={ctrlBtn}>⏭</button>
              <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 8 }}>{fmt(currentTime)} / {fmt(tot)}</span>
            </div>
          </div>

          {/* Properties panel / Interactive panel */}
          <div style={{ width: 240, background: '#1e293b', overflowY: showInteractivePanel ? 'hidden' : 'auto', padding: 12, borderLeft: `1px solid ${showInteractivePanel ? '#0891b2' : '#0f172a'}`, fontSize: 12, color: '#e2e8f0', display: 'flex', flexDirection: 'column' }}>
            {showInteractivePanel ? renderInteractivePanel() : renderProperties()}
          </div>
        </div>

        {/* Séparateur preview / timeline */}
        <div style={{ height: 10, background: '#020617', borderTop: '1px solid #1e293b', borderBottom: '1px solid #334155', flexShrink: 0 }} />

        {/* Timeline toolbar */}
        <div style={{ background: '#1e293b', padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <button onClick={splitAtPlayhead} disabled={!!selImage} style={{ ...tlBtn, opacity: selImage ? 0.4 : 1 }} title="Diviser au curseur (✂)">✂ Diviser</button>
          <button onClick={() => { const v = videoRef.current; const base = file.name.replace(/\.[^.]+$/, ''); setExtractFileName(`${base}_${Math.round((v?.currentTime ?? currentTime) * 100) / 100}s`); setShowExtractModal(true); }} style={tlBtn} title="Capturer la frame courante en JPG">📷 Extraire image</button>
          <button onClick={() => setShowVideoEffects(true)} style={{ ...tlBtn, background: mainVfxConfig ? '#14532d' : '#1e3a5f', color: mainVfxConfig ? '#86efac' : '#7dd3fc', outline: mainVfxConfig ? '1px solid #4ade80' : 'none' }} title="Effets vidéo et segmentation IA par zones">🎬 Effets{mainVfxConfig ? ' ✓' : ''}</button>
          <button
            onClick={() => { setShowInteractivePanel(p => !p); setShowAddInteractiveType(false); }}
            style={{ ...tlBtn, background: showInteractivePanel ? '#0891b2' : interactiveElements.length > 0 ? '#0c4a6e' : '#334155', color: showInteractivePanel ? '#fff' : '#7dd3fc', outline: showInteractivePanel ? '1px solid #22d3ee' : 'none' }}
            title="Panneau d'interactivité">
            📍 Interactivité{interactiveElements.length > 0 ? ` (${interactiveElements.length})` : ''}
          </button>
          <button onClick={addText} style={tlBtn} title="Ajouter un texte">T Texte</button>
          <button
            onClick={() => setShowSubEditor(true)}
            style={{ ...tlBtn, background: showSubEditor ? '#4f46e5' : '#334155', color: showSubEditor ? '#fff' : '#e2e8f0' }}
            title="Éditeur de sous-titres ASS/SSA"
          >💬 ASS</button>
          {selImage && (
            <button onClick={() => deleteImage(selImage.key)} style={{ ...tlBtn, color: '#fca5a5' }}>🗑 Supprimer image</button>
          )}
          {selOverlay && (
            <button onClick={() => deleteVideoOverlay(selOverlay.id)} style={{ ...tlBtn, color: '#fca5a5' }}>🗑 Supprimer incrustation</button>
          )}
          {!selImage && selectedId && clips.find(c => c.id === selectedId) && (
            <button onClick={() => deleteClip(selectedId)} style={{ ...tlBtn, color: '#fca5a5' }}>🗑 Supprimer clip</button>
          )}
          {!selImage && selectedId && texts.find(t => t.id === selectedId) && (
            <button onClick={() => deleteText(selectedId)} style={{ ...tlBtn, color: '#fca5a5' }}>🗑 Supprimer texte</button>
          )}
          {!selImage && selectedId && audioLayers.find(l => l.id === selectedId) && (() => {
            const lyr = audioLayers.find(l => l.id === selectedId)!;
            const src = extraSources[lyr.sourceKey];
            return (
              <>
                {src && (
                  <button
                    disabled={!!audioExtractMsg}
                    onClick={async () => {
                      if (extraSources[lyr.sourceKey]?.type === 'video') {
                        setAudioExtractMsg('Extraction…');
                        try {
                          const wavBlob = await extractAudioFromVideo(src.fileItem.url);
                          const wavUrl  = URL.createObjectURL(wavBlob);
                          setAudioEditFile({ name: src.fileItem.name.replace(/\.[^.]+$/, '') + '_audio.wav', url: wavUrl });
                        } catch (e: any) { setAudioExtractMsg(null); alert('Extraction audio échouée : ' + e.message); return; }
                        setAudioExtractMsg(null);
                      } else {
                        setAudioEditFile({ ...src.fileItem });
                      }
                      setAudioEditLayerId(selectedId);
                    }}
                    style={{ ...tlBtn, background: '#6366f1', color: '#fff' }}>
                    {audioExtractMsg ?? '🎛 Éditer'}
                  </button>
                )}
                <button onClick={() => deleteAudioLayer(selectedId)} style={{ ...tlBtn, color: '#fca5a5' }}>🗑 Supprimer audio</button>
              </>
            );
          })()}
          <div style={{ width: 1, height: 20, background: '#334155', margin: '0 4px' }} />
          <button onClick={() => {
            if (historyIdx > 0) {
              historyIdxRef.current = historyIdx - 1;
              const s = historyRef.current[historyIdxRef.current];
              setClips(s.clips); setTexts(s.texts); setAudioLayers(s.audioLayers);
              setHistoryIdx(historyIdxRef.current);
            }
          }} style={{ ...tlBtn, opacity: historyIdx > 0 ? 1 : 0.4 }} title="Annuler (Ctrl+Z)">↩ Undo</button>
          <button onClick={() => {
            if (historyIdx < historyRef.current.length - 1) {
              historyIdxRef.current = historyIdx + 1;
              const s = historyRef.current[historyIdxRef.current];
              setClips(s.clips); setTexts(s.texts); setAudioLayers(s.audioLayers);
              setHistoryIdx(historyIdxRef.current);
            }
          }} style={{ ...tlBtn, opacity: historyIdx < historyRef.current.length - 1 ? 1 : 0.4 }} title="Rétablir (Ctrl+Y)">↪ Redo</button>
          <button onClick={() => setSnapEnabled(v => !v)} style={{ ...tlBtn, background: snapEnabled ? '#1e3a5f' : '#334155', color: snapEnabled ? '#7dd3fc' : '#94a3b8', outline: snapEnabled ? '1px solid #3b82f6' : 'none' }} title="Magnétisme (snap entre clips)">⚡ Snap</button>
          <label title="Importer sous-titres SRT ou VTT" style={{ ...tlBtn, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', marginLeft: 0 }}>
            📝 SRT
            <input type="file" accept=".srt,.vtt" style={{ display: 'none' }} onChange={async e => {
              const f = e.target.files?.[0]; if (!f) return;
              let text = await f.text();
              if (f.name.toLowerCase().endsWith('.vtt')) {
                text = text.replace(/^WEBVTT[^\n]*\n+/, '').replace(/(\d{2}:\d{2}:\d{2})\.(\d{3})/g, '$1,$2').replace(/<[^>]+>/g, '');
              }
              const overlays = parseSrt(text, subFontSize, subColor, subBold, subFont);
              if (overlays.length > 0) { setTexts(ts => [...ts, ...overlays]); }
              e.target.value = '';
            }} />
          </label>
          <div style={{ width: 1, height: 20, background: '#334155', margin: '0 4px' }} />
          <span style={{ fontSize: 11, color: '#64748b' }}>Zoom</span>
          <button onClick={() => setZoom(z => Math.max(0.25, z - 0.25))} style={tlBtn}>−</button>
          <span style={{ fontSize: 11, color: '#94a3b8', width: 36, textAlign: 'center' }}>{zoom}×</span>
          <button onClick={() => setZoom(z => Math.min(8, z + 0.25))} style={tlBtn}>+</button>
          <span style={{ fontSize: 10, color: '#475569' }}>(Ctrl+Roue)</span>
          <div style={{ width: 1, height: 20, background: '#334155', margin: '0 4px' }} />
          <span style={{ fontSize: 11, color: '#64748b' }}>Vol.</span>
          <input type="range" min={0} max={200} value={audioVolume} onChange={e => setAudioVolume(+e.target.value)}
            style={{ width: 70, accentColor: '#10b981' }} />
          <span style={{ fontSize: 11, color: '#94a3b8', width: 32 }}>{audioVolume}%</span>
          <div style={{ width: 1, height: 20, background: '#334155', margin: '0 4px' }} />
          {availableFiles && (
            <>
              <button onClick={() => setShowAddFile('video')} style={tlBtn} title="Ajouter une vidéo">📹 +Vidéo</button>
              <button onClick={() => setShowAddFile('audio')} style={tlBtn} title="Ajouter audio">🎵 +Audio</button>
              <button onClick={() => setShowAddFile('text')}  style={tlBtn} title="Ajouter fichier texte">📄 +Texte</button>
              <button onClick={() => setShowAddFile('image')} style={tlBtn} title="Ajouter une image">🖼 +Image</button>
              <button onClick={() => setShowAddFile('overlay')} style={tlBtn} title="Ajouter une incrustation PiP (image ou vidéo)">🎞 +PiP</button>
            </>
          )}
        </div>

        {/* Timeline */}
        <div ref={timelineRef} style={{ flex: 1, overflowX: 'auto', overflowY: 'hidden', background: '#0f172a', minHeight: 0 }}
          onWheel={e => {
            if (e.ctrlKey || e.metaKey) {
              e.preventDefault();
              setZoom(z => Math.max(0.25, Math.min(8, z * (e.deltaY > 0 ? 0.85 : 1.18))));
            }
          }}>
          <div style={{ minWidth: tot * PX + 120, display: 'flex', flexDirection: 'column' }}>
            {/* Ruler */}
            <div style={{ display: 'flex' }}>
              <div style={{ width: 60, minWidth: 60, background: '#0f172a' }} />
              <div style={{ flex: 1, position: 'relative' }}>
                {renderRuler()}
                {/* Playhead handle */}
                <div
                  onMouseDown={onPlayheadMouseDown}
                  style={{ position: 'absolute', top: 0, left: currentTime * PX - 6, width: 12, height: 24, cursor: 'ew-resize', zIndex: 30, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '10px solid #facc15' }} />
                </div>
              </div>
            </div>

            {/* Video track */}
            {renderTrack('VIDEO', 48, clips.map((c, i) => renderClipBlock(c, i)), '#1e293b', 'video')}

            {/* AUDIO track — independent (piste audio principale) */}
            {(() => {
              const layers = audioLayers.filter(l => l.track === 'main');
              return (
                <div style={{ display: 'flex', minHeight: 40, borderBottom: '1px solid #0f172a' }}>
                  <div style={{ width: 60, minWidth: 60, background: '#0f172a', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, padding: '2px 0' }}>
                    <span style={{ fontSize: 8, color: '#34d399', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: 0.5 }}>AUDIO</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <input type="range" min={0} max={200} value={audioMainVolume}
                        onChange={e => setAudioMainVolume(+e.target.value)}
                        style={{ width: 36, accentColor: '#34d399' }}
                        title={`Volume audio: ${audioMainVolume}%`} />
                      <span style={{ fontSize: 7, color: '#64748b', minWidth: 22 }}>{audioMainVolume}%</span>
                    </div>
                  </div>
                  <div style={{ flex: 1, position: 'relative', background: '#071c14', overflow: 'visible' }} onMouseDown={onTimelineMouseDown}>
                    {layers.map((l, i) => renderAudioLayerBlock(l, i))}
                    <div style={{ position: 'absolute', top: 0, bottom: 0, left: currentTime * PX, width: 2, background: '#facc15', zIndex: 20, pointerEvents: 'none' }} />
                  </div>
                </div>
              );
            })()}

            {/* VOIX track (narration / voice-over) */}
            {(() => {
              const layers = audioLayers.filter(l => l.track === 'audio');
              return (
                <div style={{ display: 'flex', minHeight: 40, borderBottom: '1px solid #0f172a' }}>
                  <div style={{ width: 60, minWidth: 60, background: '#0f172a', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, padding: '2px 0' }}>
                    <span style={{ fontSize: 8, color: '#22d3ee', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: 0.5 }}>VOIX</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <input type="range" min={0} max={200} value={audioTrackVolume}
                        onChange={e => setAudioTrackVolume(+e.target.value)}
                        style={{ width: 36, accentColor: '#22d3ee' }}
                        title={`Volume voix: ${audioTrackVolume}%`} />
                      <span style={{ fontSize: 7, color: '#64748b', minWidth: 22 }}>{audioTrackVolume}%</span>
                    </div>
                  </div>
                  <div style={{ flex: 1, position: 'relative', background: '#071828', overflow: 'visible' }} onMouseDown={onTimelineMouseDown}>
                    {layers.map((l, i) => renderAudioLayerBlock(l, i))}
                    <div style={{ position: 'absolute', top: 0, bottom: 0, left: currentTime * PX, width: 2, background: '#facc15', zIndex: 20, pointerEvents: 'none' }} />
                  </div>
                </div>
              );
            })()}

            {/* MUSIC track (fond musical) */}
            {(() => {
              const layers = audioLayers.filter(l => l.track === 'music');
              return (
                <div style={{ display: 'flex', minHeight: 40, borderBottom: '1px solid #0f172a' }}>
                  <div style={{ width: 60, minWidth: 60, background: '#0f172a', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, padding: '2px 0' }}>
                    <span style={{ fontSize: 8, color: '#fbbf24', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: 0.5 }}>MUSIC</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <input type="range" min={0} max={200} value={musicTrackVolume}
                        onChange={e => setMusicTrackVolume(+e.target.value)}
                        style={{ width: 36, accentColor: '#fbbf24' }}
                        title={`Volume musique: ${musicTrackVolume}%`} />
                      <span style={{ fontSize: 7, color: '#64748b', minWidth: 22 }}>{musicTrackVolume}%</span>
                    </div>
                  </div>
                  <div style={{ flex: 1, position: 'relative', background: '#0a1628', overflow: 'visible' }} onMouseDown={onTimelineMouseDown}>
                    {layers.map((l, i) => renderAudioLayerBlock(l, i))}
                    <div style={{ position: 'absolute', top: 0, bottom: 0, left: currentTime * PX, width: 2, background: '#facc15', zIndex: 20, pointerEvents: 'none' }} />
                  </div>
                </div>
              );
            })()}

            {/* Image track — shown only when images have been added */}
            {Object.values(extraSources).some(s => s.type === 'image') && renderTrack('IMAGE', 36,
              Object.values(extraSources).filter(s => s.type === 'image').map(src => {
                const imgLeft = (src.trackStart ?? 0) * PX;
                const imgW    = Math.max(20, (src.duration ?? 3) * PX);
                const isSelImg = selectedId === src.key;
                return (
                  <React.Fragment key={src.key}>
                    {/* Indicateur de transition */}
                    {src.transition && imgLeft > 0 && (
                      <div style={{ position: 'absolute', left: imgLeft - 8, top: 6, width: 16, height: 22,
                        background: '#f59e0b', borderRadius: 3, zIndex: 5, cursor: 'pointer', opacity: 0.9,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#000' }}
                        title={`Transition: ${src.transition.type} (${src.transition.duration}s)`}>⟷</div>
                    )}
                    <div
                      style={{
                        position: 'absolute', left: imgLeft, top: 4, width: imgW, height: 28,
                        background: isSelImg ? '#b45309' : aiSelectedImage?.url === src.fileItem.url ? '#92400e' : '#78350f',
                        borderRadius: 4, display: 'flex', alignItems: 'center', padding: '0 6px',
                        cursor: 'grab', userSelect: 'none' as const,
                        border: `2px solid ${isSelImg ? '#f59e0b' : aiSelectedImage?.url === src.fileItem.url ? '#f59e0b88' : 'transparent'}`,
                        overflow: 'hidden', boxSizing: 'border-box' as const,
                        boxShadow: isSelImg ? '0 0 0 1px #f59e0b' : 'none',
                      }}
                      onMouseDown={e => { setSelectedId(src.key); setAiSelectedImage(src.fileItem); startClipDrag(e, 'image-block', src.key, src.trackStart ?? 0); }}>
                      <span style={{ fontSize: 9, color: '#fef3c7', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const, flex: 1, pointerEvents: 'none' }}>
                        🖼 {src.fileItem.name}
                      </span>
                      {/* Trim-right handle */}
                      <div
                        style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 7, cursor: 'ew-resize', background: 'rgba(255,255,255,0.25)', zIndex: 2 }}
                        onMouseDown={e => { e.stopPropagation(); startClipDrag(e, 'image-trim-r', src.key, src.duration ?? 3); }}
                      />
                    </div>
                  </React.Fragment>
                );
              }), '#0a1200'
            )}

            {/* Overlay track — piste d'incrustation PiP */}
            {videoOverlays.length > 0 && renderTrack('PIP', 36, videoOverlays.map(ov => {
              const isSelected = selectedId === ov.id;
              const ovLeft = ov.trackStart * PX;
              const ovW = Math.max(20, ov.duration * PX);
              return (
                <div key={ov.id} style={{
                  position: 'absolute', left: ovLeft, top: 4, width: ovW, height: 28,
                  background: isSelected ? '#be185d' : '#9d174d',
                  borderRadius: 4, cursor: 'grab', userSelect: 'none' as const,
                  border: `2px solid ${isSelected ? '#facc15' : 'transparent'}`,
                  display: 'flex', alignItems: 'center', overflow: 'hidden',
                  boxShadow: isSelected ? '0 0 0 1px #facc15' : 'none', zIndex: isSelected ? 8 : 4,
                }}
                  onMouseDown={e => {
                    if (timelineRef.current) {
                      const rect = timelineRef.current.getBoundingClientRect();
                      const x = e.clientX - rect.left + timelineRef.current.scrollLeft - 60;
                      setCurrentTime(Math.max(0, x / PXRef.current));
                    }
                    setSelectedId(ov.id);
                    startClipDrag(e, 'overlay-block', ov.id, ov.trackStart);
                  }}>
                  <span style={{ fontSize: 9, color: '#fce7f3', paddingLeft: 6, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const, pointerEvents: 'none' }}>
                    {ov.isVideo ? '🎞' : '🖼'} {ov.fileItem.name}
                  </span>
                  <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 7, cursor: 'ew-resize', background: 'rgba(255,255,255,0.25)', zIndex: 2 }}
                    onMouseDown={e => { e.stopPropagation(); startClipDrag(e, 'overlay-trim-r', ov.id, ov.duration); }} />
                </div>
              );
            }), '#150010')}

            {/* Text track */}
            {renderTrack('TEXT', 36, texts.map(t => renderTextBlock(t)), '#0f1728')}

            {/* Interactive track */}
            {interactiveElements.length > 0 && (() => {
              const IICONS: Record<InteractiveElementType, string> = {
                quiz: '❓', trueFalse: '✓✗', info: '💬', statement: '👁',
                link: '🔗', imagePop: '🖼', hotspot: '⊙', navigation: '⤵', panorama360: '🌐',
              };
              return renderTrack('INTERACT', 28,
                interactiveElements.map(el => {
                  const isSel = selectedInteractiveId === el.id;
                  return (
                    <div key={el.id}
                      onClick={() => {
                        setSelectedInteractiveId(isSel ? null : el.id);
                        setCurrentTime(el.time);
                        setShowInteractivePanel(true);
                      }}
                      style={{
                        position: 'absolute', left: el.time * PX - 12, top: 3, width: 24, height: 22,
                        background: isSel ? '#0891b2' : '#164e63',
                        borderRadius: 4, cursor: 'pointer', userSelect: 'none',
                        border: `2px solid ${isSel ? '#22d3ee' : '#0e7490'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, zIndex: isSel ? 8 : 4,
                        boxShadow: isSel ? '0 0 0 1px #22d3ee' : 'none',
                      }}
                      title={`${el.type} @ ${fmt(el.time)}`}>
                      {IICONS[el.type]}
                    </div>
                  );
                }),
                '#001520'
              );
            })()}
          </div>
        </div>

        {/* ── AI Studio Panel ─────────────────────────────────────────────── */}
        <div style={{ background: '#080d17', borderTop: '2px solid #1e293b', flexShrink: 0 }}>
          {/* Task card grid */}
          <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px 4px', gap: 8 }}>
            <span style={{ fontSize: 10, color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, flexShrink: 0 }}>✨ AI Studio</span>
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', flex: 1 }}>
              {([
                { id: 'continue-video',   icon: '▶',  label: 'Continuer',        sub: 'Étendre la vidéo', color: '#f97316' },
                { id: 'video-from-image', icon: '🎞', label: 'Texte→Vidéo',      sub: 'Génération IA',    color: '#ec4899' },
                { id: 'video-img-comfy',  icon: '✨',  label: 'Img→Vidéo',       sub: 'GPU ComfyUI',      color: '#6366f1' },
                { id: 'tts-video',        icon: '🔊', label: 'Voix off',          sub: 'TTS OpenAI',       color: '#f59e0b' },
                { id: 'subtitles',        icon: '💬', label: 'Sous-titres',       sub: 'Auto-transcription',color: '#22d3ee' },
                { id: 'lip-sync',         icon: '👄', label: 'Lip Sync',          sub: 'Sync lèvres',      color: '#06b6d4' },
                { id: 'face-swap',        icon: '🎭', label: 'Face Swap',         sub: 'InsightFace',      color: '#a855f7' },
              ] as const).map(task => {
                const isActive = aiPanelTask === task.id;
                return (
                  <button key={task.id}
                    onClick={() => setAiPanelTask(p => p === task.id ? null : task.id)}
                    title={task.sub}
                    style={{
                      background: isActive ? task.color : '#1e293b',
                      color: isActive ? '#fff' : '#94a3b8',
                      border: `1px solid ${isActive ? task.color : '#334155'}`,
                      borderRadius: 6, padding: '4px 10px', cursor: 'pointer',
                      fontSize: 11, fontWeight: isActive ? 700 : 400,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
                      minWidth: 68, transition: 'background 0.15s, border-color 0.15s',
                      boxShadow: isActive ? `0 0 8px ${task.color}55` : 'none',
                    }}>
                    <span style={{ fontSize: 14 }}>{task.icon}</span>
                    <span style={{ fontSize: 10, whiteSpace: 'nowrap' }}>{task.label}</span>
                  </button>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
              <button onClick={() => setShowVoiceRecordModal(true)} title="Enregistrer une voix"
                style={{ background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 700, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, minWidth: 54 }}>
                <span style={{ fontSize: 14 }}>🎙</span><span style={{ fontSize: 10 }}>Voix</span>
              </button>
              <button onClick={() => setShowRecordModal(true)} title="Enregistrer en direct"
                style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 700, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, minWidth: 54 }}>
                <span style={{ fontSize: 14 }}>●</span><span style={{ fontSize: 10 }}>Caméra</span>
              </button>
              <button onClick={() => setShowStoryboard(v => !v)} title="Storyboard / filmstrip"
                style={{ background: showStoryboard ? '#0f4c75' : '#1e293b', color: showStoryboard ? '#7dd3fc' : '#94a3b8', border: `1px solid ${showStoryboard ? '#3b82f6' : '#334155'}`, borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 700, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, minWidth: 54 }}>
                <span style={{ fontSize: 14 }}>🎬</span><span style={{ fontSize: 10 }}>Board</span>
              </button>
              <button onClick={() => setShowRenderQueue(v => !v)} title="File de rendu"
                style={{ background: showRenderQueue ? '#14532d' : '#1e293b', color: showRenderQueue ? '#86efac' : '#94a3b8', border: `1px solid ${showRenderQueue ? '#22c55e' : '#334155'}`, borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 700, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, minWidth: 54, position: 'relative' }}>
                <span style={{ fontSize: 14 }}>📋</span><span style={{ fontSize: 10 }}>Rendus</span>
                {renderQueue.length > 0 && <span style={{ position: 'absolute', top: 2, right: 4, background: renderQueue.some(r => r.status === 'rendering') ? '#f59e0b' : '#22c55e', color: '#000', borderRadius: '50%', width: 14, height: 14, fontSize: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>{renderQueue.length}</span>}
              </button>
            </div>
          </div>

          {/* Task-specific panel */}
          {aiPanelTask && (
            <div style={{ padding: '12px 14px', borderTop: '1px solid #1e293b', maxHeight: 260, overflowY: 'auto' }}>
              <style>{`@keyframes ai-progress-slide{0%{transform:translateX(-100%)}100%{transform:translateX(400%)}}`}</style>

              {/* ── Continuer une vidéo ─────────────────────────────────────── */}
              {aiPanelTask === 'continue-video' && (() => {
                // Only models that support video continuation (extend endpoint)
                const continueModels = aiVideoModels.filter(m => m.provider === 'LTX');
                const selectedClip = clips.find(c => c.id === selectedId);
                const activeExtraSource = selectedClip?.sourceKey ? extraSources[selectedClip.sourceKey] : null;
                const continueSourceName = activeExtraSource ? activeExtraSource.fileItem.name : file.name;
                return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>Source : <strong style={{ color: activeExtraSource ? '#a78bfa' : '#e2e8f0' }}>{continueSourceName}</strong></div>
                  {isLoadingModels ? (
                    <div style={{ fontSize: 11, color: '#64748b' }}>Chargement des modèles...</div>
                  ) : continueModels.length === 0 ? (
                    <div style={{ fontSize: 11, color: '#f59e0b' }}>Aucun modèle compatible (LTX requis — configurez votre clé API LTX).</div>
                  ) : (
                    <select value={continueModels.find(m => m.id === selectedAiModel) ? selectedAiModel : continueModels[0].id}
                      onChange={e => setSelectedAiModel(e.target.value)}
                      style={{ background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 4, padding: '4px 8px', fontSize: 12 }}>
                      {continueModels.map(m => <option key={m.id} value={m.id}>{m.label} ({m.provider})</option>)}
                    </select>
                  )}
                  <textarea value={aiPrompt} onChange={e => setAiPrompt(e.target.value)}
                    placeholder="Décrivez la suite de la vidéo..." rows={2}
                    style={{ background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 4, padding: '6px 8px', fontSize: 12, resize: 'vertical' as const }} />
                  {isGeneratingAI && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>
                        <span>Génération...</span><span>{aiProgress !== null ? `${aiProgress}%` : '…'}</span>
                      </div>
                      <div style={{ height: 4, background: '#1e293b', borderRadius: 2, overflow: 'hidden' }}>
                        {aiProgress !== null
                          ? <div style={{ height: 4, background: '#f97316', borderRadius: 2, width: `${aiProgress}%`, transition: 'width 0.5s' }} />
                          : <div style={{ height: 4, width: '30%', background: '#f97316', borderRadius: 2, animation: 'ai-progress-slide 1.4s ease-in-out infinite' }} />}
                      </div>
                    </div>
                  )}
                  <button onClick={handleAIVideoGenerate} disabled={isGeneratingAI || !aiPrompt.trim() || continueModels.length === 0}
                    style={{ background: '#f97316', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                    {isGeneratingAI ? '⏳ Génération...' : '▶ Continuer la vidéo'}
                  </button>
                </div>
                );
              })()}

              {/* ── Vidéo depuis image ──────────────────────────────────────── */}
              {aiPanelTask === 'video-from-image' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {isLoadingModels ? (
                    <div style={{ fontSize: 11, color: '#64748b' }}>Chargement des modèles...</div>
                  ) : aiVideoModels.length > 0 && (
                    <select value={selectedAiModel} onChange={e => setSelectedAiModel(e.target.value)}
                      style={{ background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 4, padding: '4px 8px', fontSize: 12 }}>
                      {aiVideoModels.map(m => <option key={m.id} value={m.id}>{m.label} ({m.provider})</option>)}
                    </select>
                  )}
                  <textarea value={aiPrompt} onChange={e => setAiPrompt(e.target.value)}
                    placeholder="Décrivez la scène, le mouvement, le style..." rows={2}
                    style={{ background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 4, padding: '6px 8px', fontSize: 12, resize: 'vertical' as const }} />
                  {isGeneratingAI && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#94a3b8', marginBottom: 4 }}><span>Génération...</span><span>{aiProgress !== null ? `${aiProgress}%` : '…'}</span></div>
                      <div style={{ height: 4, background: '#1e293b', borderRadius: 2, overflow: 'hidden' }}>
                        {aiProgress !== null
                          ? <div style={{ height: 4, background: '#ec4899', borderRadius: 2, width: `${aiProgress}%`, transition: 'width 0.5s' }} />
                          : <div style={{ height: 4, width: '30%', background: '#ec4899', borderRadius: 2, animation: 'ai-progress-slide 1.4s ease-in-out infinite' }} />}
                      </div>
                    </div>
                  )}
                  <button onClick={handleAIVideoGenerate} disabled={isGeneratingAI || !aiPrompt.trim()}
                    style={{ background: '#ec4899', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                    {isGeneratingAI ? '⏳ Génération...' : '🎞 Générer la vidéo'}
                  </button>
                </div>
              )}

              {/* ── Image + texte → vidéo GPU ───────────────────────────────── */}
              {aiPanelTask === 'video-img-comfy' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>
                    Image source <span style={{ color: '#475569' }}>(ajoutez via "+ Image" dans la timeline)</span> :
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 80, overflowY: 'auto' }}>
                    {Object.values(extraSources).filter(s => s.type === 'image').map(src => (
                      <button key={src.key} onClick={() => setAiSelectedImage(prev => prev?.url === src.fileItem.url ? null : src.fileItem)}
                        style={{ background: aiSelectedImage?.url === src.fileItem.url ? '#6366f1' : '#1e293b', color: '#fff', border: `1px solid ${aiSelectedImage?.url === src.fileItem.url ? '#6366f1' : '#334155'}`, borderRadius: 4, padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}>
                        🖼 {src.fileItem.name}
                      </button>
                    ))}
                    {Object.values(extraSources).filter(s => s.type === 'image').length === 0 && (
                      <span style={{ fontSize: 11, color: '#64748b' }}>Aucune image dans la timeline. Cliquez "+ Image" pour en ajouter.</span>
                    )}
                  </div>
                  {isLoadingModels ? (
                    <div style={{ fontSize: 11, color: '#64748b' }}>Chargement des modèles...</div>
                  ) : aiVideoModels.length === 0 ? (
                    <div style={{ fontSize: 11, color: '#f59e0b' }}>Aucun modèle IA disponible (configurez vos clés API).</div>
                  ) : (
                    <select value={selectedAiModel} onChange={e => setSelectedAiModel(e.target.value)}
                      style={{ background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 4, padding: '4px 8px', fontSize: 12 }}>
                      {aiVideoModels.map(m => <option key={m.id} value={m.id}>{m.label} ({m.provider})</option>)}
                    </select>
                  )}
                  <textarea value={aiPrompt} onChange={e => setAiPrompt(e.target.value)}
                    placeholder="Décrivez le mouvement, l'animation (Wan2.1 GPU)..." rows={2}
                    style={{ background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 4, padding: '6px 8px', fontSize: 12, resize: 'vertical' as const }} />
                  {isGeneratingAI && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#94a3b8', marginBottom: 4 }}><span>Génération GPU...</span><span>{aiProgress !== null ? `${aiProgress}%` : '…'}</span></div>
                      <div style={{ height: 4, background: '#1e293b', borderRadius: 2, overflow: 'hidden' }}>
                        {aiProgress !== null
                          ? <div style={{ height: 4, background: '#6366f1', borderRadius: 2, width: `${aiProgress}%`, transition: 'width 0.5s' }} />
                          : <div style={{ height: 4, width: '30%', background: '#6366f1', borderRadius: 2, animation: 'ai-progress-slide 1.4s ease-in-out infinite' }} />}
                      </div>
                    </div>
                  )}
                  <button onClick={handleAIVideoGenerate} disabled={isGeneratingAI || !aiPrompt.trim() || !aiSelectedImage || aiVideoModels.length === 0}
                    style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                    {isGeneratingAI ? '⏳ Génération GPU...' : '✨ Générer (GPU)'}
                  </button>
                </div>
              )}

              {/* ── Voix sur vidéo ──────────────────────────────────────────── */}
              {aiPanelTask === 'tts-video' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>Vidéo : <strong style={{ color: '#e2e8f0' }}>{file.name}</strong></div>
                  <textarea value={ttsText} onChange={e => setTtsText(e.target.value)}
                    placeholder="Texte de la voix off..." rows={3}
                    style={{ background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 4, padding: '6px 8px', fontSize: 12, resize: 'vertical' as const }} />
                  <div style={{ display: 'flex', gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 3 }}>Voix</label>
                      <select value={ttsVoice} onChange={e => setTtsVoice(e.target.value)}
                        style={{ width: '100%', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 4, padding: '4px 8px', fontSize: 12 }}>
                        <option value="alloy">Alloy</option>
                        <option value="echo">Echo</option>
                        <option value="fable">Fable</option>
                        <option value="onyx">Onyx</option>
                        <option value="nova">Nova</option>
                        <option value="shimmer">Shimmer</option>
                      </select>
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 3 }}>Modèle</label>
                      <select value={ttsModelQ} onChange={e => setTtsModelQ(e.target.value)}
                        style={{ width: '100%', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 4, padding: '4px 8px', fontSize: 12 }}>
                        <option value="tts-1">Standard</option>
                        <option value="tts-1-hd">HD</option>
                      </select>
                    </div>
                  </div>
                  {isRunningTts && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#94a3b8', marginBottom: 4 }}><span>Génération voix...</span><span>…</span></div>
                      <div style={{ height: 4, background: '#1e293b', borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{ height: 4, width: '30%', background: '#f59e0b', borderRadius: 2, animation: 'ai-progress-slide 1.4s ease-in-out infinite' }} />
                      </div>
                    </div>
                  )}
                  <button onClick={handleTtsVideoAI} disabled={isRunningTts || !ttsText.trim()}
                    style={{ background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                    {isRunningTts ? '⏳ Génération...' : '🔊 Ajouter la voix'}
                  </button>
                </div>
              )}

              {/* ── Sous-titrage ─────────────────────────────────────────────── */}
              {aiPanelTask === 'subtitles' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>Vidéo : <strong style={{ color: '#e2e8f0' }}>{file.name}</strong></div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    <div>
                      <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 3 }}>Langue</label>
                      <select value={subLanguage} onChange={e => setSubLanguage(e.target.value)}
                        style={{ background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 4, padding: '4px 8px', fontSize: 12 }}>
                        <option value="auto">Auto</option>
                        <option value="fr">Français</option>
                        <option value="en">English</option>
                        <option value="es">Español</option>
                        <option value="de">Deutsch</option>
                        <option value="it">Italiano</option>
                        <option value="pt">Português</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 3 }}>Taille</label>
                      <input type="number" min={20} max={200} value={subFontSize} onChange={e => setSubFontSize(+e.target.value)}
                        style={{ width: 60, background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 4, padding: '4px 6px', fontSize: 12 }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 3 }}>Couleur</label>
                      <input type="color" value={subColor} onChange={e => setSubColor(e.target.value)}
                        style={{ width: 36, height: 28, border: 'none', borderRadius: 4, cursor: 'pointer', background: 'none' }} />
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#94a3b8', alignSelf: 'flex-end', paddingBottom: 4 }}>
                      <input type="checkbox" checked={subBold} onChange={e => setSubBold(e.target.checked)} /> Gras
                    </label>
                  </div>
                  {isRunningSubtitles && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#94a3b8', marginBottom: 4 }}><span>Transcription en cours...</span><span>…</span></div>
                      <div style={{ height: 4, background: '#1e293b', borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{ height: 4, width: '30%', background: '#6366f1', borderRadius: 2, animation: 'ai-progress-slide 1.4s ease-in-out infinite' }} />
                      </div>
                    </div>
                  )}
                  <button onClick={handleSubtitlesAI} disabled={isRunningSubtitles}
                    style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                    {isRunningSubtitles ? '⏳ Transcription...' : '💬 Sous-titrer la vidéo'}
                  </button>
                  {subtitleSrt && (
                    <div>
                      <div style={{ fontSize: 11, color: '#22c55e', marginBottom: 4 }}>SRT généré :</div>
                      <textarea readOnly value={subtitleSrt} rows={4}
                        style={{ width: '100%', background: '#0f172a', color: '#94a3b8', border: '1px solid #334155', borderRadius: 4, padding: '6px 8px', fontSize: 11, resize: 'vertical' as const }} />
                    </div>
                  )}
                </div>
              )}

              {/* ── Face Swap insightface ───────────────────────────────────── */}
              {aiPanelTask === 'face-swap' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {/* Sélecteur vidéo cible */}
                  <div>
                    <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 }}>
                      Vidéo cible (clip à modifier) :
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {file.url && (
                        <button onClick={() => setFaceSwapVideoKey(k => k === '' ? '' : '')}
                          style={{ background: faceSwapVideoKey === '' ? '#7c3aed' : '#1e293b', color: '#fff', border: `1px solid ${faceSwapVideoKey === '' ? '#7c3aed' : '#334155'}`, borderRadius: 4, padding: '3px 10px', fontSize: 11, cursor: 'pointer' }}
                          title="Fichier principal">
                          🎬 {file.name || 'Fichier principal'}
                        </button>
                      )}
                      {Object.values(extraSources).filter(s => s.type === 'video' && s.key !== '__main__').map(s => (
                        <button key={s.key} onClick={() => setFaceSwapVideoKey(k => k === s.key ? '' : s.key)}
                          style={{ background: faceSwapVideoKey === s.key ? '#7c3aed' : '#1e293b', color: '#fff', border: `1px solid ${faceSwapVideoKey === s.key ? '#7c3aed' : '#334155'}`, borderRadius: 4, padding: '3px 10px', fontSize: 11, cursor: 'pointer' }}>
                          🎬 {s.fileItem.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Sélecteur image source (visage) */}
                  <div>
                    <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 }}>
                      Image source — visage à insérer (ajoutez via "+ Image") :
                    </label>
                    {Object.values(extraSources).filter(s => s.type === 'image').length === 0 ? (
                      <div style={{ fontSize: 11, color: '#f59e0b' }}>
                        Aucune image dans la timeline. Ajoutez une photo via "+ Image".
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {Object.values(extraSources).filter(s => s.type === 'image').map(s => (
                          <button key={s.key} onClick={() => setFaceSwapSourceKey(k => k === s.key ? '' : s.key)}
                            style={{ background: faceSwapSourceKey === s.key ? '#a855f7' : '#1e293b', color: '#fff', border: `1px solid ${faceSwapSourceKey === s.key ? '#a855f7' : '#334155'}`, borderRadius: 4, padding: '3px 10px', fontSize: 11, cursor: 'pointer' }}>
                            🖼 {s.fileItem.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {isGeneratingAI && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>
                        <span>Face swap en cours...</span><span>{aiProgress !== null ? `${aiProgress}%` : '…'}</span>
                      </div>
                      <div style={{ height: 4, background: '#1e293b', borderRadius: 2, overflow: 'hidden' }}>
                        {aiProgress !== null
                          ? <div style={{ height: 4, background: '#a855f7', borderRadius: 2, width: `${aiProgress}%`, transition: 'width 0.5s' }} />
                          : <div style={{ height: 4, width: '30%', background: '#a855f7', borderRadius: 2, animation: 'ai-progress-slide 1.4s ease-in-out infinite' }} />}
                      </div>
                    </div>
                  )}
                  <button onClick={handleFaceSwap} disabled={isGeneratingAI || !faceSwapSourceKey || (!faceSwapVideoKey && !file.url)}
                    style={{ background: '#a855f7', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                    {isGeneratingAI ? '⏳ Traitement...' : '🎭 Swapper le visage'}
                  </button>
                </div>
              )}

              {/* ── Lip Sync Wav2Lip ─────────────────────────────────────────── */}
              {aiPanelTask === 'lip-sync' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>
                    Vidéo (visage) : <strong style={{ color: '#e2e8f0' }}>{file.name}</strong>
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 }}>
                      Piste audio source (voix/musique dans la timeline) :
                    </label>
                    {Object.values(extraSources).filter(s => s.type === 'audio').length === 0 ? (
                      <div style={{ fontSize: 11, color: '#f59e0b' }}>
                        Aucune piste audio dans la timeline. Ajoutez une piste audio via "+ Audio".
                      </div>
                    ) : (
                      <select value={lipSyncAudioKey} onChange={e => setLipSyncAudioKey(e.target.value)}
                        style={{ width: '100%', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 4, padding: '4px 8px', fontSize: 12 }}>
                        <option value="">— Choisir une piste audio —</option>
                        {Object.values(extraSources).filter(s => s.type === 'audio').map(s => (
                          <option key={s.key} value={s.key}>🔊 {s.fileItem.name}</option>
                        ))}
                      </select>
                    )}
                  </div>
                  {isGeneratingAI && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>
                        <span>Synchronisation labiale...</span><span>{aiProgress !== null ? `${aiProgress}%` : '…'}</span>
                      </div>
                      <div style={{ height: 4, background: '#1e293b', borderRadius: 2, overflow: 'hidden' }}>
                        {aiProgress !== null
                          ? <div style={{ height: 4, background: '#06b6d4', borderRadius: 2, width: `${aiProgress}%`, transition: 'width 0.5s' }} />
                          : <div style={{ height: 4, width: '30%', background: '#06b6d4', borderRadius: 2, animation: 'ai-progress-slide 1.4s ease-in-out infinite' }} />}
                      </div>
                    </div>
                  )}
                  <button onClick={handleLipSync} disabled={isGeneratingAI || !lipSyncAudioKey}
                    style={{ background: '#06b6d4', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                    {isGeneratingAI ? '⏳ Traitement...' : '👄 Synchroniser les lèvres'}
                  </button>
                </div>
              )}

            </div>
          )}
        </div>

        {/* Footer */}
        <div className="card-footer bg-light py-2 px-3 d-flex align-items-center justify-content-between" style={{ flexShrink: 0 }}>
          <div style={{ fontSize: 12, color: '#64748b', flex: 1 }}>
            {processing
              ? <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <span><span className="spinner-border spinner-border-sm me-2" role="status" />{processMsg}</span>
                  {renderProgress && (
                    <div style={{ height: 4, background: '#1e293b', borderRadius: 2, overflow: 'hidden', width: 240 }}>
                      {renderProgress.percent >= 0
                        ? <div style={{ height: 4, background: '#22c55e', borderRadius: 2, width: `${renderProgress.percent}%`, transition: 'width 0.4s' }} />
                        : <div style={{ height: 4, width: '35%', background: '#22c55e', borderRadius: 2, animation: 'ai-progress-slide 1.4s ease-in-out infinite' }} />
                      }
                    </div>
                  )}
                </div>
              : error
                ? <span style={{ color: '#ef4444' }}>⚠ {error}</span>
                : <span>{clips.length} clip(s) · {texts.length} texte(s) · {audioLayers.length} piste(s) audio · Durée : {fmt(totalDuration(clips))}</span>
            }
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-sm btn-light border" onClick={onCancel} disabled={processing}>Annuler</button>
            <button className="btn btn-sm btn-success px-4 fw-bold"
              onClick={() => {
                const ext = saveFormat.format === 'webm' ? '.webm' : '.mp4';
                setSaveFileName(file.name.replace(/\.[^.]+$/, '') + '_edited' + ext);
                setSaveModal(true);
              }}
              disabled={processing || clips.length === 0}>
              {processing
                ? <><span className="spinner-border spinner-border-sm me-2" />Rendu FFmpeg...</>
                : <><i className="fas fa-check me-2" />Enregistrer</>}
            </button>
          </div>
        </div>

      </div>

      {/* Extract frame modal */}
      {showExtractModal && createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 999999, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setShowExtractModal(false); }}>
          <div style={{ background: '#1e293b', borderRadius: 8, padding: 24, width: 380, boxShadow: '0 8px 32px rgba(0,0,0,0.6)' }}>
            <h6 style={{ color: '#e2e8f0', marginBottom: 16, fontWeight: 700 }}>📷 Extraire l'image courante</h6>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>
              Frame à <strong style={{ color: '#f97316' }}>{Math.round((videoRef.current?.currentTime ?? currentTime) * 100) / 100}s</strong> — répertoire : <strong style={{ color: '#7dd3fc' }}>{currentPath || '/'}</strong>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <input
                autoFocus
                value={extractFileName}
                onChange={e => setExtractFileName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleExtractFrame(); if (e.key === 'Escape') setShowExtractModal(false); }}
                placeholder="nom du fichier"
                style={{ flex: 1, background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 4, padding: '7px 10px', fontSize: 13 }}
              />
              <span style={{ color: '#475569', fontSize: 12, whiteSpace: 'nowrap' }}>.jpg</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setShowExtractModal(false)}
                style={{ background: '#334155', color: '#94a3b8', border: 'none', borderRadius: 4, padding: '6px 14px', cursor: 'pointer', fontSize: 12 }}>
                Annuler
              </button>
              <button onClick={handleExtractFrame} disabled={!extractFileName.trim()}
                style={{ background: '#f97316', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 16px', cursor: 'pointer', fontSize: 12, fontWeight: 700, opacity: extractFileName.trim() ? 1 : 0.5 }}>
                💾 Enregistrer
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Save modal */}
      {saveModal && createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 999999, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#1e293b', borderRadius: 8, padding: 24, width: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
            <h6 style={{ color: '#e2e8f0', marginBottom: 16 }}>Enregistrer la vidéo</h6>

            {/* Nom */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, color: '#94a3b8', display: 'block', marginBottom: 4 }}>Nom du fichier</label>
              <input
                type="text"
                value={saveFileName}
                onChange={e => setSaveFileName(e.target.value)}
                style={{ width: '100%', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 4, padding: '6px 10px', fontSize: 13, boxSizing: 'border-box' as const }}
                onKeyDown={e => { if (e.key === 'Enter' && saveFileName.trim()) handleExport(saveFileName.trim()); }}
                autoFocus
              />
            </div>

            {/* Résolution */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, color: '#94a3b8', display: 'block', marginBottom: 4 }}>Résolution</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {([
                  { v: 'original', label: 'Originale' },
                  { v: '4k',       label: '4K (2160p)' },
                  { v: '1080p',    label: '1080p' },
                  { v: '720p',     label: '720p' },
                  { v: '480p',     label: '480p' },
                  { v: '1080x1080',label: '1:1 (1080)' },
                  { v: '1080x1920',label: '9:16 (vertical)' },
                  { v: 'custom',   label: 'Personnalisée' },
                ] as const).map(opt => (
                  <button key={opt.v}
                    onClick={() => setSaveFormat(f => ({ ...f, resolution: opt.v }))}
                    style={{
                      background: saveFormat.resolution === opt.v ? '#3b82f6' : '#0f172a',
                      color: saveFormat.resolution === opt.v ? '#fff' : '#94a3b8',
                      border: `1px solid ${saveFormat.resolution === opt.v ? '#3b82f6' : '#334155'}`,
                      borderRadius: 4, padding: '3px 10px', cursor: 'pointer', fontSize: 11,
                    }}>{opt.label}</button>
                ))}
              </div>
              {saveFormat.resolution === 'custom' && (
                <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
                  <input type="number" min={1} max={7680} value={saveFormat.customWidth}
                    onChange={e => setSaveFormat(f => ({ ...f, customWidth: Math.max(1, +e.target.value) }))}
                    style={{ width: 80, background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 4, padding: '4px 6px', fontSize: 12 }} />
                  <span style={{ color: '#64748b', fontSize: 12 }}>×</span>
                  <input type="number" min={1} max={4320} value={saveFormat.customHeight}
                    onChange={e => setSaveFormat(f => ({ ...f, customHeight: Math.max(1, +e.target.value) }))}
                    style={{ width: 80, background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 4, padding: '4px 6px', fontSize: 12 }} />
                  <span style={{ color: '#64748b', fontSize: 11 }}>px</span>
                </div>
              )}
            </div>

            {/* Qualité + FPS */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 18 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, color: '#94a3b8', display: 'block', marginBottom: 4 }}>Qualité</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  {([
                    { v: 'high',   label: 'Haute' },
                    { v: 'normal', label: 'Normale' },
                    { v: 'low',    label: 'Basse' },
                  ] as const).map(opt => (
                    <button key={opt.v}
                      onClick={() => setSaveFormat(f => ({ ...f, quality: opt.v }))}
                      style={{
                        background: saveFormat.quality === opt.v ? '#8b5cf6' : '#0f172a',
                        color: saveFormat.quality === opt.v ? '#fff' : '#94a3b8',
                        border: `1px solid ${saveFormat.quality === opt.v ? '#8b5cf6' : '#334155'}`,
                        borderRadius: 4, padding: '3px 10px', cursor: 'pointer', fontSize: 11,
                      }}>{opt.label}</button>
                  ))}
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, color: '#94a3b8', display: 'block', marginBottom: 4 }}>FPS</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  {([
                    { v: 0,  label: 'Auto' },
                    { v: 24, label: '24' },
                    { v: 30, label: '30' },
                    { v: 60, label: '60' },
                  ] as const).map(opt => (
                    <button key={opt.v}
                      onClick={() => setSaveFormat(f => ({ ...f, fps: opt.v }))}
                      style={{
                        background: saveFormat.fps === opt.v ? '#f59e0b' : '#0f172a',
                        color: saveFormat.fps === opt.v ? '#fff' : '#94a3b8',
                        border: `1px solid ${saveFormat.fps === opt.v ? '#f59e0b' : '#334155'}`,
                        borderRadius: 4, padding: '3px 10px', cursor: 'pointer', fontSize: 11,
                      }}>{opt.label}</button>
                  ))}
                </div>
              </div>
            </div>

            {/* Format */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, color: '#94a3b8', display: 'block', marginBottom: 4 }}>Format de sortie</label>
              <div style={{ display: 'flex', gap: 6 }}>
                {(['mp4', 'webm'] as const).map(fmt => (
                  <button key={fmt} onClick={() => setSaveFormat(f => ({ ...f, format: fmt }))}
                    style={{ background: (saveFormat.format ?? 'mp4') === fmt ? '#ec4899' : '#0f172a', color: (saveFormat.format ?? 'mp4') === fmt ? '#fff' : '#94a3b8', border: `1px solid ${(saveFormat.format ?? 'mp4') === fmt ? '#ec4899' : '#334155'}`, borderRadius: 4, padding: '3px 12px', cursor: 'pointer', fontSize: 11 }}>
                    {fmt === 'mp4' ? 'MP4 (H.264)' : 'WebM (VP9)'}
                  </button>
                ))}
              </div>
              {(saveFormat.format ?? 'mp4') === 'webm' && <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>VP9 — meilleure compression, support alpha, idéal Web</div>}
            </div>

            {/* Waveform overlay */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={saveFormat.waveformOverlay ?? false}
                  onChange={e => setSaveFormat(f => ({ ...f, waveformOverlay: e.target.checked }))} />
                <span style={{ fontSize: 12, color: '#94a3b8' }}>Superposer la waveform audio</span>
              </label>
              {saveFormat.waveformOverlay && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                  <span style={{ fontSize: 11, color: '#64748b' }}>Couleur</span>
                  <input type="color" value={saveFormat.waveformColor ?? '#ffffff'}
                    onChange={e => setSaveFormat(f => ({ ...f, waveformColor: e.target.value }))}
                    style={{ width: 32, height: 22 }} />
                  <span style={{ fontSize: 11, color: '#64748b' }}>Hauteur</span>
                  <input type="range" min={20} max={200} value={saveFormat.waveformHeight ?? 60}
                    onChange={e => setSaveFormat(f => ({ ...f, waveformHeight: +e.target.value }))}
                    style={{ width: 80 }} />
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>{saveFormat.waveformHeight ?? 60}px</span>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setSaveModal(false)} style={{ background: '#334155', color: '#e2e8f0', border: 'none', borderRadius: 4, padding: '6px 14px', cursor: 'pointer', fontSize: 12 }}>Annuler</button>
              <button
                onClick={() => { if (saveFileName.trim()) handleExport(saveFileName.trim()); }}
                disabled={!saveFileName.trim()}
                style={{ background: '#22c55e', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 16px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                Enregistrer
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* File picker modal */}
      {/* Recording modal */}
      {showRecordModal && createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 999997, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => { if (!isRecording) { recordStreamRef.current?.getTracks().forEach(t => t.stop()); setShowRecordModal(false); } }}>
          <div style={{ background: '#1e293b', borderRadius: 10, padding: 20, width: 560, boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h6 style={{ color: '#e2e8f0', margin: 0 }}>● Enregistrement en direct</h6>
              {!isRecording && (
                <button onClick={() => { recordStreamRef.current?.getTracks().forEach(t => t.stop()); setShowRecordModal(false); }}
                  style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 18, cursor: 'pointer' }}>✕</button>
              )}
            </div>
            <video ref={recordVideoRef} muted autoPlay
              style={{ width: '100%', borderRadius: 6, background: '#000', maxHeight: 300, marginBottom: 14 }} />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              {!isRecording ? (
                <button onClick={startRecording}
                  style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 24px', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                  ● Démarrer l'enregistrement
                </button>
              ) : (
                <button onClick={stopRecording}
                  style={{ background: '#1e293b', color: '#ef4444', border: '2px solid #ef4444', borderRadius: 6, padding: '8px 24px', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                  ■ Arrêter et ajouter à la timeline
                </button>
              )}
            </div>
            {isRecording && (
              <div style={{ textAlign: 'center', marginTop: 10, fontSize: 12, color: '#ef4444' }}>
                ● Enregistrement en cours...
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Track picker modal */}
      {trackPicker && createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 999999, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setTrackPicker(null)}>
          <div style={{ background: '#1e293b', borderRadius: 10, padding: 24, width: 360, boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0', marginBottom: 16, textAlign: 'center' }}>Choisir la piste de destination</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => { trackPicker.cb('main'); setTrackPicker(null); }}
                style={{ flex: 1, padding: '14px 0', borderRadius: 8, border: '2px solid #34d399', background: '#065f46', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 22 }}>🔊</span>
                Piste AUDIO
              </button>
              <button onClick={() => { trackPicker.cb('audio'); setTrackPicker(null); }}
                style={{ flex: 1, padding: '14px 0', borderRadius: 8, border: '2px solid #22d3ee', background: '#0e7490', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 22 }}>🎤</span>
                Piste VOIX
              </button>
              <button onClick={() => { trackPicker.cb('music'); setTrackPicker(null); }}
                style={{ flex: 1, padding: '14px 0', borderRadius: 8, border: '2px solid #fbbf24', background: '#92400e', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 22 }}>🎵</span>
                Piste MUSIC
              </button>
            </div>
            <button onClick={() => setTrackPicker(null)} style={{ marginTop: 12, width: '100%', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 12 }}>Annuler</button>
          </div>
        </div>,
        document.body
      )}

      {/* Voice recording modal */}
      {showVoiceRecordModal && createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 999996, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => { if (!isVoiceRecording) { voiceStreamRef.current?.getTracks().forEach(t => t.stop()); setShowVoiceRecordModal(false); } }}>
          <div style={{ background: '#1e293b', borderRadius: 10, padding: 24, width: 380, boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h6 style={{ color: '#e2e8f0', margin: 0 }}>🎙 Enregistrement vocal</h6>
              {!isVoiceRecording && (
                <button onClick={() => { voiceStreamRef.current?.getTracks().forEach(t => t.stop()); setShowVoiceRecordModal(false); }}
                  style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 18, cursor: 'pointer' }}>✕</button>
              )}
            </div>
            <div style={{ textAlign: 'center', padding: '20px 0', fontSize: 48, marginBottom: 8 }}>
              {isVoiceRecording ? '🔴' : '🎙'}
            </div>
            {isVoiceRecording && (
              <div style={{ textAlign: 'center', fontSize: 12, color: '#ef4444', marginBottom: 16 }}>
                ● Enregistrement en cours... (parlez dans votre microphone)
              </div>
            )}
            {!isVoiceRecording && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: '#64748b', textAlign: 'center', marginBottom: 8 }}>Choisir la piste de destination :</div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                  <button onClick={() => setVoiceTrack('main')} style={{ flex: 1, padding: '7px 0', borderRadius: 6, border: `2px solid ${voiceTrack === 'main' ? '#34d399' : '#334155'}`, background: voiceTrack === 'main' ? '#065f46' : '#1e293b', color: voiceTrack === 'main' ? '#fff' : '#94a3b8', cursor: 'pointer', fontSize: 12, fontWeight: voiceTrack === 'main' ? 700 : 400 }}>
                    🔊 AUDIO
                  </button>
                  <button onClick={() => setVoiceTrack('audio')} style={{ flex: 1, padding: '7px 0', borderRadius: 6, border: `2px solid ${voiceTrack === 'audio' ? '#22d3ee' : '#334155'}`, background: voiceTrack === 'audio' ? '#0e7490' : '#1e293b', color: voiceTrack === 'audio' ? '#fff' : '#94a3b8', cursor: 'pointer', fontSize: 12, fontWeight: voiceTrack === 'audio' ? 700 : 400 }}>
                    🎤 VOIX
                  </button>
                  <button onClick={() => setVoiceTrack('music')} style={{ flex: 1, padding: '7px 0', borderRadius: 6, border: `2px solid ${voiceTrack === 'music' ? '#fbbf24' : '#334155'}`, background: voiceTrack === 'music' ? '#92400e' : '#1e293b', color: voiceTrack === 'music' ? '#fff' : '#94a3b8', cursor: 'pointer', fontSize: 12, fontWeight: voiceTrack === 'music' ? 700 : 400 }}>
                    🎵 MUSIC
                  </button>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              {!isVoiceRecording ? (
                <button onClick={startVoiceRecording}
                  style={{ background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 24px', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                  🎙 Démarrer l'enregistrement
                </button>
              ) : (
                <button onClick={stopVoiceRecording}
                  style={{ background: '#1e293b', color: '#a78bfa', border: '2px solid #7c3aed', borderRadius: 6, padding: '8px 24px', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                  ■ Arrêter et ajouter à la timeline
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {showAddFile && createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000001, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => { setShowAddFile(null); setPickerCallback(null); }}>
          <div style={{ background: '#1e293b', borderRadius: 8, padding: 20, width: 520, maxHeight: '75vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 10 }} onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h6 style={{ color: '#e2e8f0', margin: 0, fontSize: 14 }}>
                {pickerCallback
                  ? (showAddFile === 'image' ? '🖼 Choisir une image de fond' : '🎬 Choisir une vidéo de fond')
                  : showAddFile === 'video' ? '📹 Ajouter une vidéo' : showAddFile === 'audio' ? '🎵 Ajouter une piste audio' : showAddFile === 'image' ? '🖼 Ajouter une image' : showAddFile === 'overlay' ? '🎞 Ajouter une incrustation PiP' : '📄 Ajouter un fichier texte'}
              </h6>
              <button onClick={() => { setShowAddFile(null); setPickerCallback(null); }} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 18, cursor: 'pointer' }}>✕</button>
            </div>

            {/* Breadcrumb navigation */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', background: '#0f172a', borderRadius: 6, padding: '5px 8px', fontSize: 12 }}>
              <button onClick={() => setPickerPath('')}
                style={{ background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer', padding: '0 2px', fontSize: 12 }}>
                🏠 Racine
              </button>
              {pickerPath.split('/').filter(Boolean).map((seg, i, arr) => {
                const path = arr.slice(0, i + 1).join('/');
                return (
                  <React.Fragment key={path}>
                    <span style={{ color: '#475569' }}>/</span>
                    <button onClick={() => setPickerPath(path)}
                      style={{ background: 'none', border: 'none', color: i === arr.length - 1 ? '#e2e8f0' : '#60a5fa', cursor: 'pointer', padding: '0 2px', fontSize: 12 }}>
                      {seg}
                    </button>
                  </React.Fragment>
                );
              })}
            </div>

            {/* Search */}
            <input
              type="text"
              placeholder="Rechercher…"
              value={pickerSearch}
              onChange={e => setPickerSearch(e.target.value)}
              style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', padding: '6px 10px', fontSize: 12, outline: 'none' }}
            />

            {/* File list */}
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {pickerLoading ? (
                <p style={{ color: '#64748b', fontSize: 13, textAlign: 'center', padding: 20 }}>⏳ Chargement…</p>
              ) : (() => {
                const search = pickerSearch.toLowerCase();

                // Directories first (always shown, filtered by search)
                const dirs = pickerFiles.filter(f => f.isDir && (!search || f.name.toLowerCase().includes(search)));
                // Files matching type + search
                const matchFn = showAddFile === 'video' ? isVideoFile : showAddFile === 'audio' ? isAudioFile : showAddFile === 'image' ? isImageFile : showAddFile === 'overlay' ? ((n: string) => isImageFile(n) || isVideoFile(n)) : isTextFile;
                const files = pickerFiles.filter(f => !f.isDir && matchFn(f.name) && (!search || f.name.toLowerCase().includes(search)));

                if (!dirs.length && !files.length) return (
                  <p style={{ color: '#64748b', fontSize: 13, textAlign: 'center', padding: 20 }}>Aucun fichier trouvé</p>
                );

                const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderRadius: 6, cursor: 'pointer', marginBottom: 3, border: '1px solid #334155' };

                return (
                  <>
                    {/* Parent directory ".." */}
                    {pickerPath && (
                      <div style={{ ...rowStyle, background: '#0f172a' }}
                        onClick={() => setPickerPath(pickerPath.includes('/') ? pickerPath.split('/').slice(0, -1).join('/') : '')}
                        onMouseEnter={e => (e.currentTarget.style.background = '#1e3a5f')}
                        onMouseLeave={e => (e.currentTarget.style.background = '#0f172a')}>
                        <span style={{ fontSize: 16 }}>📁</span>
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>..</span>
                      </div>
                    )}
                    {/* Subdirectories */}
                    {dirs.map(d => (
                      <div key={d.url} style={{ ...rowStyle, background: '#0f172a' }}
                        onClick={() => setPickerPath(pickerPath ? `${pickerPath}/${d.name}` : d.name)}
                        onMouseEnter={e => (e.currentTarget.style.background = '#1e3a5f')}
                        onMouseLeave={e => (e.currentTarget.style.background = '#0f172a')}>
                        <span style={{ fontSize: 16 }}>📁</span>
                        <span style={{ fontSize: 12, color: '#93c5fd', fontWeight: 600 }}>{d.name}/</span>
                      </div>
                    ))}
                    {/* Files */}
                    {files.map(f => (
                      <div key={f.url} style={{ ...rowStyle, background: '#0f172a' }}
                        draggable
                        onDragStart={e => {
                          e.dataTransfer.setData('application/x-flexihub-file', JSON.stringify(f));
                          e.dataTransfer.effectAllowed = 'copy';
                          // Close picker when starting drag
                          setShowAddFile(null);
                          setPickerCallback(null);
                        }}
                        onClick={() => {
                          if (pickerCallback) {
                            pickerCallback(f);
                            setPickerCallback(null);
                            setShowAddFile(null);
                            return;
                          }
                          if (showAddFile === 'video') addVideoSource(f);
                          else if (showAddFile === 'audio') addAudioSource(f);
                          else if (showAddFile === 'overlay') { addVideoOverlay(f); }
                          else if (showAddFile === 'image') {
                            const key = `img_${Date.now()}`;
                            const sortedClips = [...clipsRef.current].sort((a, b) => a.trackStart - b.trackStart);
                            let insertAt = totalDuration(clipsRef.current);
                            let insertDur = 3;
                            for (let i = 0; i < sortedClips.length - 1; i++) {
                              const gapStart = clipTrackEnd(sortedClips[i]);
                              const gapEnd   = sortedClips[i + 1].trackStart;
                              if (gapEnd - gapStart >= 0.5) { insertAt = gapStart; insertDur = gapEnd - gapStart; break; }
                            }
                            setExtraSources(prev => ({ ...prev, [key]: { key, fileItem: f, type: 'image', trackStart: insertAt, duration: insertDur } }));
                            setAiSelectedImage(f);
                            setSelectedId(key);
                          } else addTextFile(f);
                          setShowAddFile(null);
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#1e3a5f')}
                        onMouseLeave={e => (e.currentTarget.style.background = '#0f172a')}>
                        <span style={{ fontSize: 16 }}>{showAddFile === 'audio' ? '🎵' : showAddFile === 'text' ? '📄' : showAddFile === 'image' ? '🖼' : showAddFile === 'overlay' ? '🎞' : '🎬'}</span>
                        <span style={{ fontSize: 12, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{f.name}</span>
                        {f.size !== undefined && (
                          <span style={{ fontSize: 10, color: '#475569', flexShrink: 0 }}>
                            {f.size > 1048576 ? (f.size / 1048576).toFixed(1) + ' Mo' : f.size > 1024 ? (f.size / 1024).toFixed(0) + ' Ko' : f.size + ' o'}
                          </span>
                        )}
                      </div>
                    ))}
                  </>
                );
              })()}
            </div>
          </div>
        </div>,
        document.body
      )}
      {/* ── Storyboard panel ────────────────────────────────────────────── */}
      {showStoryboard && createPortal(
        <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 999998, background: '#0f172a', borderTop: '2px solid #334155', padding: '10px 14px', maxHeight: 160, overflowX: 'auto', overflowY: 'hidden', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 10, color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, flexShrink: 0, writingMode: 'vertical-rl', transform: 'rotate(180deg)', height: 60 }}>BOARD</span>
            {clips.map((c, i) => {
              const thumbArr = clipThumbnails[c.id];
              const thumb = thumbArr ? thumbArr[Math.floor(thumbArr.length / 2)] : undefined;
              const isActive = currentTime >= c.trackStart && currentTime < clipTrackEnd(c);
              return (
                <div key={c.id} onClick={() => { setCurrentTime(c.trackStart); setSelectedId(c.id); }}
                  style={{
                    flexShrink: 0, width: 96, cursor: 'pointer', borderRadius: 6,
                    border: `2px solid ${isActive ? '#facc15' : selectedId === c.id ? '#6366f1' : '#334155'}`,
                    overflow: 'hidden', background: '#1e293b', transition: 'border-color 0.15s',
                  }}>
                  {thumb
                    ? <img src={thumb} alt="" style={{ width: '100%', height: 54, objectFit: 'cover', display: 'block' }} />
                    : <div style={{ width: '100%', height: 54, background: clipColor(i), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, color: '#fff' }}>🎬</div>}
                  <div style={{ padding: '3px 5px', fontSize: 9, color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {fmt(c.srcStart)}–{fmt(c.srcEnd)} {c.speed !== 1 ? `${c.speed}×` : ''}
                  </div>
                </div>
              );
            })}
            {texts.map(t => (
              <div key={t.id} onClick={() => { setCurrentTime(t.trackStart); setSelectedId(t.id); }}
                style={{
                  flexShrink: 0, width: 80, cursor: 'pointer', borderRadius: 6,
                  border: `2px solid ${selectedId === t.id ? '#6366f1' : '#334155'}`,
                  background: '#7c3aed', padding: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', height: 70,
                }}>
                <span style={{ fontSize: 10, color: '#fff', textAlign: 'center', wordBreak: 'break-word' }}>{t.content.slice(0, 30)}</span>
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}

      {/* ── End screen portal ───────────────────────────────────────────── */}
      {showEndScreen && interactivePreviewMode && createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 999999, background: 'rgba(0,0,0,0.82)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#1e293b', borderRadius: 14, padding: 32, width: 360, boxShadow: '0 20px 60px rgba(0,0,0,0.7)', border: '1px solid #334155', display: 'flex', flexDirection: 'column', gap: 18, alignItems: 'center', textAlign: 'center' }}>
            <div style={{ fontSize: 48 }}>🏆</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#e2e8f0' }}>Vidéo terminée !</div>
            {interactiveScore.total > 0 && (
              <>
                <div style={{ fontSize: 13, color: '#94a3b8' }}>Score du quiz</div>
                <div style={{ fontSize: 36, fontWeight: 900, color: interactiveScore.correct === interactiveScore.total ? '#22c55e' : interactiveScore.correct >= interactiveScore.total / 2 ? '#f59e0b' : '#ef4444' }}>
                  {interactiveScore.correct} / {interactiveScore.total}
                </div>
                <div style={{ width: '100%', height: 8, background: '#0f172a', borderRadius: 4 }}>
                  <div style={{ height: 8, borderRadius: 4, background: interactiveScore.correct === interactiveScore.total ? '#22c55e' : '#3b82f6', width: `${(interactiveScore.correct / interactiveScore.total) * 100}%`, transition: 'width 0.6s' }} />
                </div>
                <div style={{ fontSize: 12, color: '#64748b' }}>
                  {interactiveScore.correct === interactiveScore.total
                    ? 'Parfait ! Toutes les réponses sont correctes.'
                    : interactiveScore.correct >= interactiveScore.total / 2
                    ? 'Bon travail ! Vous pouvez revoir la vidéo pour améliorer votre score.'
                    : 'Revoyez la vidéo pour mieux comprendre le contenu.'}
                </div>
              </>
            )}
            <div style={{ display: 'flex', gap: 10, width: '100%' }}>
              <button onClick={() => {
                setShowEndScreen(false);
                setInteractivePreviewMode(false);
                setCurrentTime(0);
                if (videoRef.current) videoRef.current.currentTime = 0;
              }} style={{ flex: 1, background: '#334155', color: '#e2e8f0', border: 'none', borderRadius: 6, padding: '10px 0', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                ✕ Fermer
              </button>
              <button onClick={() => {
                setShowEndScreen(false);
                dismissedInteractiveRef.current = new Set();
                setInteractiveScore({ correct: 0, total: 0 });
                setActiveInteractive(null);
                setCurrentTime(0);
                if (videoRef.current) videoRef.current.currentTime = 0;
                setPlaying(true);
                videoRef.current?.play().catch(() => {});
              }} style={{ flex: 1, background: '#0891b2', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 0', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                ↺ Rejouer
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Render queue panel ──────────────────────────────────────────── */}
      {showRenderQueue && createPortal(
        <div style={{ position: 'fixed', bottom: 60, right: 16, zIndex: 999998, background: '#1e293b', border: '1px solid #334155', borderRadius: 10, width: 360, maxHeight: 400, overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.6)' }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, color: '#e2e8f0', fontSize: 13 }}>📋 File de rendu</span>
            <button onClick={() => setShowRenderQueue(false)} style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>✕</button>
          </div>
          {renderQueue.length === 0 ? (
            <div style={{ padding: 20, color: '#475569', fontSize: 12, textAlign: 'center' }}>
              Aucun rendu en file. Cliquez "Enregistrer" puis "Ajouter à la file".
            </div>
          ) : renderQueue.map(item => (
            <div key={item.id} style={{ padding: '10px 14px', borderBottom: '1px solid #0f172a', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{item.filename}</span>
                <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, marginLeft: 8, flexShrink: 0,
                  background: item.status === 'done' ? '#14532d' : item.status === 'error' ? '#7f1d1d' : item.status === 'rendering' ? '#1e3a5f' : '#1e293b',
                  color: item.status === 'done' ? '#86efac' : item.status === 'error' ? '#fca5a5' : item.status === 'rendering' ? '#93c5fd' : '#64748b',
                }}>
                  {item.status === 'done' ? '✓ Terminé' : item.status === 'error' ? '✗ Erreur' : item.status === 'rendering' ? '⏳ Rendu…' : '⏸ Attente'}
                </span>
              </div>
              {item.status === 'rendering' && (
                <div style={{ height: 3, background: '#0f172a', borderRadius: 2 }}>
                  <div style={{ height: 3, background: '#3b82f6', borderRadius: 2, width: `${item.progress}%`, transition: 'width 0.4s' }} />
                </div>
              )}
              {item.status === 'done' && item.blobUrl && (
                <a href={item.blobUrl} download={item.filename}
                  style={{ fontSize: 11, color: '#86efac', textDecoration: 'none', background: '#14532d', padding: '3px 8px', borderRadius: 4, display: 'inline-block', width: 'fit-content' }}>
                  ⬇ Télécharger
                </a>
              )}
              {item.error && <span style={{ fontSize: 10, color: '#fca5a5' }}>{item.error}</span>}
            </div>
          ))}
        </div>,
        document.body
      )}

      {/* ── VideoEffectsEditor overlay ──────────────────────────────────── */}
      {showVideoEffects && (
        <VideoEffectsEditor
          file={file}
          duration={duration}
          initialGlobalEffects={mainVfxConfig?.globalEffects}
          initialZones={mainVfxConfig?.zones}
          initialGlobalSeg={mainVfxConfig?.globalSeg as GlobalSegConfig | undefined}
          onPickFile={(accept, cb) => { setPickerCallback(() => cb); setShowAddFile(accept); }}
          onSave={(blob, filename, config) => {
            const url     = URL.createObjectURL(blob);
            const newFile: FileItem = { name: filename, url, size: blob.size };
            // Remplacer (ou créer) la source VFX — révoquer l'ancien blob si besoin
            setExtraSources(prev => {
              if (prev['__vfx__']) URL.revokeObjectURL(prev['__vfx__'].fileItem.url);
              return { ...prev, '__vfx__': { key: '__vfx__', fileItem: newFile, type: 'video' as const } };
            });
            // Mettre à jour les clips principaux (sans sourceKey) pour utiliser la version avec effets
            // muteAudio: true car la piste audio vient du layer '__main__'
            setClips(prev => prev.map(c =>
              c.sourceKey === undefined
                ? { ...c, sourceKey: '__vfx__', muteAudio: true }
                : c.sourceKey === '__vfx__'
                  ? c  // déjà mis à jour — reste sur '__vfx__' (nouveau blob)
                  : c
            ));
            setMainVfxConfig(config);
            setShowVideoEffects(false);
          }}
          onCancel={() => setShowVideoEffects(false)}
        />
      )}

{/* ── AudioEditor overlay (édition d'une piste audio) ───────────────── */}
      {audioEditLayerId && audioEditFile && (() => {
        const handleAudioSave = (blob: Blob, filename: string) => {
          const newUrl  = URL.createObjectURL(blob);
          const newKey  = `edited_${Date.now()}`;
          const newFile: FileItem = { name: filename, url: newUrl, size: blob.size };
          setExtraSources(prev => ({
            ...prev,
            [newKey]: { key: newKey, fileItem: newFile, type: 'audio' },
          }));
          setAudioLayers(prev => prev.map(l =>
            l.id === audioEditLayerId ? { ...l, sourceKey: newKey } : l,
          ));
          setAudioEditLayerId(null);
          setAudioEditFile(null);
        };

        return createPortal(
          <div style={{ position: 'fixed', inset: 0, zIndex: 1000000, background: 'rgba(0,0,0,0.95)' }}>
            <AudioEditor
              file={audioEditFile}
              defaultTab="effects"
              onSave={handleAudioSave}
              onCancel={() => { setAudioEditLayerId(null); setAudioEditFile(null); }}
            />
          </div>,
          document.body,
        );
      })()}

      <AssSubtitleEditor
        show={showSubEditor}
        onClose={() => setShowSubEditor(false)}
        subtitleSrt={subtitleSrt}
        timelineTexts={texts}
        videoRef={videoRef}
        fileName={file.name}
        onApply={(newTexts: AssTextOverlay[], newIds: string[], replacedIds: string[]) => {
          setTexts(prev => [...prev.filter(t => !assTextIds.includes(t.id) && !replacedIds.includes(t.id)), ...newTexts]);
          setAssTextIds(newIds);
          if (newTexts.length > 0) {
            const first = newTexts.reduce((a, b) => a.trackStart <= b.trackStart ? a : b);
            setSelectedId(first.id);
            // Pause la vidéo et déplace l'élément <video> : sans ça le RAF loop
            // écrase setCurrentTime avec la position courante du flux vidéo
            setPlaying(false);
            setCurrentTime(first.trackStart);
            if (videoRef.current) {
              videoRef.current.pause();
              videoRef.current.currentTime = first.trackStart;
            }
          }
        }}
      />

    </div>
  );
};

const ctrlBtn: React.CSSProperties = { background: '#334155', color: '#e2e8f0', border: 'none', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: 14 };
const tlBtn: React.CSSProperties = { background: '#334155', color: '#e2e8f0', border: 'none', borderRadius: 4, padding: '3px 9px', cursor: 'pointer', fontSize: 11 };

export default VideoEditor;
